// ============================================================================
// design-profile.js — the Design Profile: the core data object (V2 element model)
// ============================================================================
// REWRITTEN 2026-07-11 (Ryan's real-world spec). The legacy/first-V2 profile
// was a flat { logo, schoolName, primaryColor, secondaryColor } paired with a
// 6-template gallery. Real plates are designed as a small set of PLACED ELEMENTS
// on the plate's printable ring (geometry "ring B"), anchored at four slots
// (top/bottom/left/right mid), each element richly styled with a fill + a stack
// of 1–4 sharp mitered outlines drawn from a 3-color palette.
//
// This module stays PURE (no DOM, no THREE, no globals) so the whole model +
// render path is node-testable. It flows through every system (2D preview, 3D
// texture, quote handoff) — 2D and 3D render from the SAME profile so brand
// colors match exactly (V2 plan §5 — color fidelity is a trust issue).
// ============================================================================

import fontManifest from './fonts/manifest.js';
import fontInkOffsets from './fonts/ink-offsets.js';
import fontInkCaps from './fonts/ink-caps.js';
import fontCharW from './fonts/char-widths.js';
import { srgbToLab, deltaE } from './palette-extract.js';

// ── Logo sub-object (unchanged shape — reused by the 'logo' element) ─────────

/**
 * @typedef {Object} LogoProfile
 * @property {string|null} dataUrl
 * @property {string|null} fileName
 * @property {string|null} mimeType
 * @property {number|null} width
 * @property {number|null} height
 * @property {number|null} [aspectRatio]
 * @property {boolean} [isVector]
 * @property {'high'|'medium'|'low'|null} quality
 * @property {boolean} transparentBg
 */

/** Square fallback aspect for logos whose real intrinsic size is unknown. */
export const DEFAULT_LOGO_ASPECT = 1;

/** Builds a fresh, fully-defaulted logo sub-object. */
export function createDefaultLogo() {
  return {
    dataUrl: null,
    fileName: null,
    mimeType: null,
    width: null,
    height: null,
    aspectRatio: null,
    isVector: false,
    quality: null,
    transparentBg: true,
    // How the client-side background keyer treated this logo, so a RESUMED session can restore the
    // "let weight show through enclosed areas" toggle instead of hiding it (see logo-step.js's
    // hydrate). `keyColor` is the removed background ([r,g,b], or null for the near-white path) and
    // `keyGlobal` is whether enclosed pockets were cut. A few bytes — deliberately NOT the pre-key
    // image, which would double the autosave AND every share link (main.js:636 notes they carry the
    // same string) for something that can be reconstructed instead.
    keyColor: null,
    // The near-white brightness cutoff that key ran at, now that it is derived from the MEASURED
    // background field rather than a constant (see nearWhiteThresh in ui/logo-step.js). Null on the
    // colour path and on anything saved before it existed; both fall back to the legacy 238.
    keyWhite: null,
    // …and the background field that cutoff was derived FROM. The keyer now works in RGB distance
    // around this colour rather than by per-channel brightness, so a resume that re-keys without it
    // re-keys against pure white and produces a visibly different cut on any dim or warm field —
    // the same class of bug as losing `keyWhite`, one layer down.
    keyField: null,
    keyGlobal: null,
    // …and the two fields the PLATE-AWARE show-through rule needs across a resume (Ryan 2026-07-29):
    // `keyPockets` is the measured pocket geometry ({share, pocketHex, ringHex} — three values, not an
    // image) so the question can be re-asked on a plate change without re-keying to measure, and
    // `keyGlobalUserSet` records whether the answer was the app's or the buyer's. Lose the flag and a
    // resumed design starts overruling a choice the buyer made deliberately, which is worse than the
    // bug the rule was added to fix. Same lesson as `sizeUserSet` / `textUserSet`.
    keyPockets: null,
    keyGlobalUserSet: false,
    // WD 485: the art carries the buyer's "Edit see-through parts" changes. Saved so a reopened design still knows
    // (Reset logo has something to go back to; a chooser tap keeps the edits) - it was dropped by the whitelist below.
    tapFixed: false,
    // Where this artwork has the least ink — see logo-structure.findClearSpot. Null until measured.
    clearSpot: null,
  };
}

// ── Fonts ────────────────────────────────────────────────────────────────────
// Pluggable font registry. Phase 1 ships one athletic-block face + Montserrat.
// The athletic face's actual @font-face is embedded in fonts/fonts.js; here we
// only need the id → CSS font-family stack (with safe fallbacks) for the SVG.
// (Font asset selection is pending Ryan's preferred file — 'varsity' currently
// falls back to the bundled Montserrat stack until the athletic woff2 lands.)

// The font registry is generated from the manifest (metadata source of truth;
// bytes + @font-face live in fonts/). `category` groups the picker.
export const FONTS = Object.freeze(
  Object.fromEntries(
    fontManifest.map((f) => [
      f.id,
      {
        id: f.id, label: f.label, family: f.family, weight: f.weight, category: f.category, wr: f.wr,
        // Per-font vertical ink offset (fraction of em) for arc-text re-centering;
        // measured by tools/measure-ink-offsets.mjs. 0 for well-behaved faces.
        ink: Number.isFinite(fontInkOffsets[f.id]) ? fontInkOffsets[f.id] : 0,
        // Per-font cap-height (fraction of em) — keeps the weight number↔unit gap + the
        // ruled rules a constant distance across fonts (measured by the same tool).
        cap: Number.isFinite(fontInkCaps[f.id]) && fontInkCaps[f.id] > 0 ? fontInkCaps[f.id] : 0.72,
        // Per-font advance-width ratios [digit, uppercase, lowercase] (fraction of em) — the
        // weight unit line is sized off these so its width matches the number exactly.
        charW: Array.isArray(fontCharW[f.id]) && fontCharW[f.id].length === 3 ? fontCharW[f.id] : [0.55, 0.62, 0.5],
      },
    ]),
  ),
);

// Montserrat (Ryan 2026-07-25, was Anton). This is BOTH the font every new design opens on AND the
// fallback every metric accessor resolves to, so a design referencing a since-removed font renders
// in this rather than breaking. The guard stays: if the id ever leaves the manifest, fall back to
// whatever is first instead of pointing at nothing.
export const DEFAULT_FONT_ID = FONTS.montserrat2 ? 'montserrat2' : Object.keys(FONTS)[0];

/** Ordered category groups for the font picker. */
export const FONT_CATEGORIES = ['collegiate', 'block', 'slab', 'condensed', 'modern', 'serif', 'script', 'display'];
export const FONT_CATEGORY_LABELS = {
  collegiate: 'Collegiate',
  block: 'Block / Impact',
  slab: 'Slab',
  condensed: 'Condensed',
  modern: 'Modern / Thin',
  serif: 'Serif / Elegant',
  script: 'Script',
  display: 'Display',
};

/** Resolves a fontId to its CSS font-family stack (falls back to the default). */
export function fontFamilyFor(fontId) {
  return (FONTS[fontId] || FONTS[DEFAULT_FONT_ID]).family;
}

/** Resolves a fontId to the CSS font-weight to request (each face is single-weight). */
export function fontWeightFor(fontId) {
  return (FONTS[fontId] || FONTS[DEFAULT_FONT_ID]).weight;
}

/** Average glyph advance ÷ font-size for a face (measured; 0.6 fallback). Drives
 *  the DOM-free text auto-fit so wide faces don't overflow the arc. */
export function fontWidthRatio(fontId) {
  const wr = (FONTS[fontId] || FONTS[DEFAULT_FONT_ID]).wr;
  return Number.isFinite(wr) && wr > 0 ? wr : 0.6;
}

/** Per-font vertical ink offset (fraction of em; + = ink above the `central`
 *  baseline). render-engine shifts each arc's path radius by ink×fontSize to
 *  re-center the letters in the ring band. 0 for well-behaved faces. */
export function fontInkOffset(fontId) {
  const f = FONTS[fontId] || FONTS[DEFAULT_FONT_ID];
  return f && Number.isFinite(f.ink) ? f.ink : 0;
}

/** Per-font cap-height (fraction of em) — the measured ink height of uppercase glyphs.
 *  Lets the weight lockup position its number/unit + the ruled rules off the ACTUAL glyph
 *  height, so the gaps stay constant across fonts instead of jumping. ~0.72 fallback. */
export function fontCapFrac(fontId) {
  const f = FONTS[fontId] || FONTS[DEFAULT_FONT_ID];
  return f && Number.isFinite(f.cap) && f.cap > 0 ? f.cap : 0.72;
}

/** Advance width (fraction of em) of ONE character, from the measured [digit, uppercase, lowercase]
 *  ratios (dashes/other ≈ half a digit). Needed on its own by the grip plate's UPRIGHT arc text
 *  (2026-07-30), which places every glyph itself and so has to know where each one starts.
 *    The class-average is an approximation — a real I is narrower than a real W — and it is the SAME
 *  approximation `fitArcFont` has always made when deciding whether a word fits its arc. Keeping one
 *  reader of `charW` is what stops the placement and the fit disagreeing about the same word. */
export function charAdvanceFrac(ch, fontId) {
  const f = FONTS[fontId] || FONTS[DEFAULT_FONT_ID];
  const cw = f && Array.isArray(f.charW) ? f.charW : [0.55, 0.62, 0.5];
  if (ch >= '0' && ch <= '9') return cw[0];
  if (ch >= 'A' && ch <= 'Z') return cw[1];
  if (ch >= 'a' && ch <= 'z') return cw[2];
  return cw[0] * 0.5; // dash / punctuation ≈ half a digit
}

/** Advance width (fraction of em) of a string in a font — summed per glyph from the measured
 *  [digit, uppercase, lowercase] ratios (dashes/other ≈ half a digit). Lets the weight lockup
 *  size its unit line so its width matches the number EXACTLY across fonts (Ryan 2026-07-22). */
export function strAdvanceFrac(str, fontId) {
  let a = 0;
  for (const ch of String(str || '')) a += charAdvanceFrac(ch, fontId);
  return a;
}

// ── Plate body color (B6 §4) ─────────────────────────────────────────────────
// The rubber body's color (rings A/B-ground/C). Bumpers can be Pantone-dyed;
// black is the default + ships fastest (§4.1). Stored as a hex; the near-black
// default matches the satin-rubber material in reveal-3d.js (bodyMaterial).
export const DEFAULT_BODY_COLOR = '#0B0C0F';

// ── Colors / palette ─────────────────────────────────────────────────────────

/**
 * @typedef {Object} Palette
 * @property {string} primary   - '#RRGGBB' fill color for most text.
 * @property {string} secondary - '#RRGGBB' typical outline color.
 * @property {string|null} tertiary - optional 3rd color (extra outline), null if unused.
 */

/**
 * Normalizes a hex color to `#RRGGBB` (uppercase). Accepts 3- or 6-digit hex
 * with/without a leading `#`. Returns `null` for invalid input.
 */
export function normalizeHex(input) {
  if (typeof input !== 'string') return null;
  let hex = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return '#' + hex.toUpperCase();
}

/**
 * Resolves a color reference against a palette. A ref is one of
 * 'primary' | 'secondary' | 'tertiary' | a literal '#RRGGBB'. Unknown/'' → primary.
 * @param {string} ref
 * @param {Palette} palette
 * @returns {string} '#RRGGBB'
 */
export function resolvePaletteRef(ref, palette) {
  const p = palette || {};
  if (ref === 'primary') return p.primary || '#000000';
  if (ref === 'secondary') return p.secondary || '#FFFFFF';
  if (ref === 'tertiary') return p.tertiary || p.secondary || '#FFFFFF';
  const lit = normalizeHex(ref);
  return lit || p.primary || '#000000';
}

// ── plate-dye contrast (§4.2, Ryan 2026-07-18) ───────────────────────────────
// When the plate is dyed a non-black color, a text/number's OUTERMOST visible
// layer can merge with that base. These pure helpers compute the EFFECTIVE stack
// as a reversible VIEW over the element's real (intended) stack — the design is
// never mutated, so successive plate-color picks can't erode it (dye red → the
// red ring hides; dye white again → it returns). Used by BOTH the render engine
// and the quote so what's shown is what's made.

// ΔE below which two ADJACENT colors read as one muddy / invisible edge (a
// conflict). palette-extract treats ΔE<12 as "the same color" and >10 as "clearly
// distinct"; 14 gives a small margin so a merely-muddy ring peels too. Tune here.
export const CONFLICT_DELTA_E = 14;

// ΔE below which two extracted colors are the SAME tone for the "Original" swatch (two slightly
// different yellows read as one yellow). Higher than CONFLICT_DELTA_E because this merges same-
// family SHADES, not just muddy edges — but well under the ~40+ that separates real brand hues.
export const TONE_MERGE_DELTA_E = 24;

/* ΔE below which two of a MARK's tones count as ONE COLOUR for the recolour gate — and only on a
 * plate dealt in one of those two colours (Ryan 2026-09-20, on the swimmer): *"if the colour is
 * actually two colours and they're very close together in shade then it can be treated as one
 * colour... when placed against a background colour on the plate of the same blue or of one of the
 * same blues."*
 *   ⛔ AND IT IS NOT `TONE_MERGE_DELTA_E`, WHICH IS ONE POINT TOO LOOSE. Reusing 24 was the first
 * thing tried, because the house rule is to reuse a constant rather than invent one and that one
 * already means "two shades read as one". Measured, 24 also merges `secondary-png`'s 23.9 pair and
 * reverses two plates Ryan wants kept.
 *   ⭐ THE NUMBER IS A MEASURED GAP, NOT A CHOICE. The swimmer's two cyans sit at 18.1 and the next
 * pair in the corpus that changes any outcome is that 23.9 — every bar between them gives an
 * identical answer, and 21 is the middle. ⚠ Still justified by ONE mark: 29 marks carry a near-twin
 * pair in this band on a plate of their own colour and only the swimmer moves, because the rest are
 * not refused a recolour, touch a real colour as well, or head for the wrong neutral. */
export const TWIN_ON_PLATE_DELTA_E = 21;

/** Two of a mark's tones the eye reads as one shade — see `TWIN_ON_PLATE_DELTA_E`. */
export function colorsSameShade(hexA, hexB) {
  return deltaE(labOfHex(hexA), labOfHex(hexB)) < TWIN_ON_PLATE_DELTA_E;
}

/** CIELAB of a '#RRGGBB' hex (via palette-extract's pure color math). */
function labOfHex(hex) {
  const h = normalizeHex(hex) || '#000000';
  const rgb = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  return srgbToLab(rgb);
}

/** CIELAB chroma (√(a²+b²)) of a hex — how far from the neutral axis it sits. 0 for grays/invalid.
 *  Same measure `analyzeStructure` uses to pick a chromatic fill (`minChroma`). */
export function hexChroma(hex) {
  const lab = labOfHex(hex);
  return Math.hypot(lab[1], lab[2]);
}

/* ── CAN YOU READ ONE OF THESE ON THE OTHER? ────────────────────────────────────────────────────
 * ⛔ `colorsConflict` IS NOT THIS QUESTION, AND THE DIFFERENCE COST RYAN A PLATE (2026-08-17, CSI).
 * It measures ΔE — "would these two read as ONE EDGE" — which is a question about whether two
 * colours are the same. Legibility of TYPE on a surface is a question about LIGHTNESS, and the two
 * come apart completely on a pair that differs in hue and chroma while matching in luminance:
 *
 *     gold #FCCB0C plate / cream #FAF2CB number   ΔE says "distinct"   WCAG  1.36:1
 *     cream #FAF2CB plate / white #FFFFFF ring    ΔE says "distinct"   WCAG  1.13:1
 *     gold #FCCB0C plate / black #000000 ring     ΔE says "distinct"   WCAG 13.69:1
 *
 * All three clear `colorsConflict` identically, and only the third is readable. That is how the CSI
 * plate arrived at a cream 10 LB on a gold face, with the ring rescue picking WHITE for it and
 * `dropAutoOutlinesWhenFillReads` then deleting that ring as unnecessary — three passes agreeing,
 * all three asking the wrong question.
 *
 * ⚠ THIS DOES NOT REPLACE `colorsConflict` ANYWHERE. "Are these the same colour" is still the right
 * test for peeling a merged ring, deduping a palette and offering a neutral chip. This is the
 * additional test for the narrower question those passes were borrowing it for.
 *
 * ⭐ 2.0 IS MEASURED, NOT PICKED. Across the 39 CFA schools whose mark yields two palette roles —
 * and primary-vs-secondary IS the plate/number pair whenever the plate is dyed one of them — the
 * sorted contrast ratios have their widest low-end gap at **1.75 → 2.03**: twelve schools below it
 * (CSI 1.34, Chatham 1.28, T-Birds 1.25, Amherst 1.49 …) and a continuous distribution above. The
 * bar sits in that gap. It is deliberately FAR below WCAG's 3:1 large-text bar, because the job here
 * is to catch pairs nobody could call legible, not to re-lint designs Ryan has already approved.
 * Re-measure before moving it.
 *   ⚠ `CONTRAST_BLACK_MIN` in ui/state.js is a DIFFERENT bar (3, the real WCAG large-text floor) for
 * a different remedy: it decides whether to MOVE a colour that fails on a black product. Moving a
 * colour is a big enough act to deserve the standard's bar; adding a ring is a small enough one that
 * firing it on 22 of 39 CFA designs would be a style change nobody asked for. Two bars on purpose. */
export const READABLE_RATIO = 2.0;

/** WCAG relative luminance of a '#RRGGBB' hex.
 *  ⚠ THE ONE COPY. This lived in `ui/state.js` until 2026-08-17, whose own comment on it is the
 *  reason it moved rather than being duplicated: *"a second copy of the WCAG curve is the 'two
 *  places need the same answer, one will be wrong' trap this project has paid for repeatedly, and
 *  `0.2126R + 0.7152G + 0.0722B` on LINEARISED channels is exactly the kind of arithmetic that gets
 *  approximated into something that looks fine on the six colours you tested."* It sits here now
 *  because `readsOn` below needs it and design-profile is the layer state.js imports FROM. */
export function relLuminance(hex) {
  const h = normalizeHex(hex) || '#000000';
  const [r, g, b] = [1, 3, 5]
    .map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio (1–21) between two '#RRGGBB' hexes. */
export function contrastRatio(hexA, hexB) {
  const [l1, l2] = [relLuminance(hexA), relLuminance(hexB)].sort((a, b) => b - a);
  return (l1 + 0.05) / (l2 + 0.05);
}

/** True when `fgHex` is legible against `bgHex`. Missing hexes answer TRUE — an unknown colour is
 *  not evidence of a problem, and every caller here is only ever ADDING a safeguard. */
export function readsOn(bgHex, fgHex) {
  const a = normalizeHex(bgHex);
  const b = normalizeHex(fgHex);
  if (!a || !b) return true;
  return contrastRatio(a, b) >= READABLE_RATIO;
}

/** WD 475: the white plate (every channel >= 230) - the plate a white numeral vanishes into. */
function isNearWhitePlate(hex) {
  const h = normalizeHex(hex);
  if (!h) return false;
  return [1, 3, 5].every((i) => parseInt(h.slice(i, i + 2), 16) >= 230);
}
/** WD 475: the published text-legibility contrast (WCAG AA body text). */
const TEXT_LEGIBLE_RATIO = 4.5;

/** True if a hex reads as black rubber (the undyed plate) → no peel then. */
export function isNearBlackHex(hex) {
  const h = normalizeHex(hex);
  if (!h) return true;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  return r <= 24 && g <= 24 && b <= 24; // ≤ ~#181818 (matches ui/state isBlackBody)
}

/* ── DEFAULT PLATE COLOURS FOR A MULTI-CONFIGURATION SET (Ryan 2026-08-09) ──────────────────────
 * A buyer who picks the same competition bumper more than once should not have to dye each one from
 * black. The defaults come off THEIR OWN LOGO, so a two-tone school mark produces a two-tone set of
 * plates before they touch anything.
 *
 * ⭐ THE TABLE IS RYAN'S, VERBATIM, and it is not a plain cycle — black appears in exactly two of
 * the five rows, which is the part a "just rotate the palette" implementation would get wrong:
 *
 *     non-black tones │ instances 1 … 5
 *     ───────────────┼──────────────────────────────
 *                  1 │ C1, BLACK, C1, BLACK, C1
 *                  2 │ C1, C2, C1, C2, C1
 *                  3 │ C1, C2, C3, C1, C2
 *                  4 │ C1, C2, C3, C4, BLACK
 *                 5+ │ C1, C2, C3, C4, C5
 *
 * So black is a FILLER, used only where the alternative is worse: with one real colour there is
 * nothing to alternate against, and with four it completes a set of five distinct plates. At two or
 * three the cycle repeats instead, because a repeat of the buyer's own colour reads as intentional
 * where an unexplained black plate in the middle of a coloured set does not.
 *
 * ── ⭐ SUPERSEDED 2026-08-10 — THE STOCK FINISHES JOIN THE SEQUENCE (Ryan) ──────────────────────
 * The table above is kept because it is the reasoning this replaced, and because one clause of it
 * survived and one did not.
 *
 * The rule is now: **their colours, then WHITE, then BLACK, then repeat from the start.**
 *
 *     non-black tones │ instances 1 … 5
 *     ───────────────┼──────────────────────────────
 *                  1 │ C1, WHITE, BLACK, C1, WHITE
 *                  2 │ C1, C2, WHITE, BLACK, C1
 *                  3 │ C1, C2, C3, WHITE, BLACK
 *                  4 │ C1, C2, C3, C4, WHITE
 *                 5+ │ C1, C2, C3, C4, C5      (unchanged)
 *
 * 🔑 WHAT CHANGED THE ARGUMENT IS THAT WHITE IS NOT BLACK. The 2026-08-09 reasoning against a filler
 * was written about black — *"an unexplained black plate in the middle of a coloured set"* — and it
 * does not carry to white. White and black are the two finishes every competition bumper ships in,
 * so a set running green, yellow, white, black reads as a deliberate RANGE where green, yellow,
 * green, yellow reads as the tool running out of ideas. A school ordering four sets wants four
 * plates it can tell apart across a gym floor.
 *
 * ⚠ WHAT SURVIVED: black is still never the FIRST plate, and a buyer's own colours are still spent
 * before any stock finish is offered. The promise that the colours come off their logo is intact;
 * the stock finishes only fill the tail.
 *
 * ⛔ AND A FINISH THE LOGO ALREADY OWNS IS SKIPPED, or the sequence hands back a duplicate and the
 * "five distinct plates" the whole table exists to produce quietly becomes four. A white-and-navy
 * crest must not be offered white twice.
 *
 * ⚠ THE TONES ARE `shownLogoTones`, not the raw palette. That is the function whose own note says it
 * is "what Ryan is counting" when he says a 4-tone logo — the same list the Add Logo chips show — so
 * this rule and the buyer's mental model of "my colours" cannot disagree.
 */
export const PLATE_STOCK_WHITE = '#FFFFFF';

export function plateDefaultSequence(tones, count = 5, { avoid = [] } = {}) {
  const nb = (Array.isArray(tones) ? tones : [])
    .map((h) => normalizeHex(h))
    .filter((h) => h && !isNearBlackHex(h));
  const out = [];
  /* ── ⛔ TWO CASES ARRIVE HERE AND THEY ARE NOT THE SAME (Ryan 2026-08-10, the RIGAR mark) ────────
   * `nb` being empty used to mean one thing — "the stock plate" — and it is actually two:
   *
   *   • NOTHING READ AT ALL (no logo yet, or an unreadable mark). Stays black, and must: the plate
   *     is black until the buyer clicks "Design My Weights", which is 259's rule and Ryan's words
   *     ("the plate should be completely black up until I click Design My Weights — it has no way of
   *     knowing how to design the plate").
   *   • A MARK WHOSE ONLY TONES ARE BLACK — a black wordmark on transparent, which is what RIGAR is.
   *     That is a real, readable logo; it simply has no colour to dye a plate with. Handing it five
   *     identical black plates makes the multi-configuration feature look broken on exactly the kind
   *     of mark a gym is most likely to upload.
   *
   * ⭐ SO A MONOCHROME MARK FLIP-FLOPS THE TWO STOCK FINISHES, WHITE FIRST. Ryan: *"RIGAR should go
   * to a white plate with black letters, then black plate with white letters, simply flip-flopping
   * each time — always starting with the more 'custom' look, which is white plate first."* Starting
   * on white is the whole point: a white plate reads as something they chose, a black one reads as
   * the plate they would have got anyway.
   *
   * ⚠ THE LETTERS ARE NOT SET HERE and do not need to be. Each configuration derives its own ink
   * against the plate it lands on (`adoptProduct`'s cascade), so black-on-white and white-on-black
   * fall out of the contrast rules rather than being spelled out twice. */
  if (!nb.length) {
    const readable = (Array.isArray(tones) ? tones : []).map((h) => normalizeHex(h)).filter(Boolean);
    const ring = readable.length ? [PLATE_STOCK_WHITE, DEFAULT_BODY_COLOR] : [DEFAULT_BODY_COLOR];
    for (let i = 0; i < count; i++) out.push(ring[i % ring.length]);
    return out;
  }
  /* WHITE is skipped when the mark already wears one, or the set shows two plates nobody can tell
     apart. ⛔ `colorsIndistinguishable` WAS THE WRONG RADIUS FOR A PLATE (Ryan 2026-08-18, the
     Hanover four-set: *"the 4x plate colors went white, blue, white, red… why didn't we use a black
     option in there too vs. duplicating the white?"*). Its ~ΔE-5 merge is calibrated for swatch
     chips; Hanover's #E5EBEF sits at ΔE 7.9 from #FFFFFF — distinct as chips, THE SAME PLATE across
     a gym floor — so the set dealt both and ran out of slots before black. `colorsConflict` (ΔE 14,
     "would these read as one") is the plate-scale question: measured over the corpus's near-whites,
     it moves exactly the ≤14 family (E5EBEF 7.9, F4F4F5 3.8, FDFDFD 0.7) and leaves genuinely
     different plates alone (cream #FAF2CB 20.7, columbia blue 33.9).
     ⛔ BLACK IS NEVER SKIPPED, and the first cut of this got it wrong by testing the raw tone list —
     a white-and-black logo then had both stock finishes removed and produced FIVE IDENTICAL WHITE
     PLATES. The dedupe exists to stop two indistinguishable PLATES, and a black tone in the artwork
     cannot cause one: `isNearBlackHex` has already stripped every near-black tone out of `nb`, so
     the stock black is guaranteed distinct from everything in front of it. Black ink in a logo and a
     black plate are not the same fact. */
  /* `avoid` — plate colours this MARK cannot survive, currently R13's one-tone detached pieces
     (see `dealPlateDefaults`). It has to be applied HERE and not only to `tones`, because the stock
     white is minted inside this function: ee-logo's white piece is swallowed by `PLATE_STOCK_WHITE`,
     which no filter over the incoming tone list can ever see. Black is exempt, as everywhere in this
     function — a near-black tone is already stripped from `nb`, and the undyed plate is the floor
     the set falls back to. */
  const off = (Array.isArray(avoid) ? avoid : []).map((h) => normalizeHex(h)).filter(Boolean);
  const banned = (h) => off.some((a) => colorsConflict(a, h));
  const stock = [PLATE_STOCK_WHITE, DEFAULT_BODY_COLOR]
    .filter((s) => s === DEFAULT_BODY_COLOR || (!banned(s) && !nb.some((h) => colorsConflict(h, s))));
  const ring = [...nb, ...stock];
  for (let i = 0; i < count; i++) out.push(ring[i % ring.length]);
  return out;
}

/* ── NAMING A PLATE COLOUR FOR A HUMAN (Ryan 2026-08-09) ─────────────────────────────────────────
 * A buyer who picks the competition bumper twice gets two quote blocks that are otherwise word for
 * word identical, so the colour has to be sayable: *"Competition Urethane (white): 10 pairs of 45s
 * / Competition Urethane (green): 8 pairs of 25s."*
 *
 * ⚠ THE NAME NEVER TRAVELS ALONE — every caller pairs it with the hex (`plateColorLabel`). This is
 * an approximate word for a precise value, and a rep matching a proof needs the value: two dyes a
 * shade apart can both be honestly called "green", and quoting one as the other is a re-run of a
 * customer's whole order. The word is for reading; the hex is for matching.
 *
 * ⚠ AND IT IS DELIBERATELY COARSE. Eight hues plus the neutrals, with one lightness qualifier. A
 * finer table ("chartreuse", "cerulean") reads as precision this cannot actually deliver and would
 * put a rep in the position of arguing about a word instead of reading a hex.
 */
const HUE_NAMES = Object.freeze([
  [15, 'red'], [45, 'orange'], [70, 'yellow'], [160, 'green'],
  [195, 'teal'], [256, 'blue'], [291, 'purple'], [345, 'pink'], [360, 'red'],
]);

/**
 * A plain-English word for a hex: 'white', 'dark blue', 'gray'. Never null.
 *
 * ⛔ LIGHT / DARK IS DECIDED ON CIELAB L*, NOT ON HSL LIGHTNESS, and that is the difference between
 * this working and it being nonsense. HSL lightness is `(max + min) / 2` on the raw channels, which
 * ignores that the eye reads green as far brighter than blue at the same number: the house green
 * `#2E7D32` and navy `#1E3A8A` sit 0.006 apart on that scale (0.335 vs 0.329) while looking nothing
 * alike, so NO threshold on it can call one plain "green" and the other "dark blue". On L* they are
 * 45 and 27 — eighteen points apart, exactly as they look. `labOfHex` is already here for the
 * perceptual colour comparisons, so this costs an existing function call.
 *
 * The HUE still comes from HSL, which is the right tool for that half: hue angle is what the
 * familiar colour names are actually named after, and Lab's hue angle is rotated away from them.
 */
export function colorWord(hex) {
  const h = normalizeHex(hex);
  if (!h) return 'black';                       // the undyed plate, and the safest thing to say
  const [L, aStar, bStar] = labOfHex(h);
  const chroma = Math.hypot(aStar, bStar);      // how much colour there is at all
  // NEUTRALS FIRST. A near-black plate with a stray point of saturation is still black to anyone
  // looking at it, and calling it "dark blue" would be a worse answer than no answer.
  if (L <= 14) return 'black';
  if (L >= 92 && chroma < 10) return 'white';
  if (chroma < 14) return L >= 75 ? 'light gray' : (L <= 35 ? 'dark gray' : 'gray');
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  let hue = 0;
  if (d !== 0) {
    if (max === r) hue = ((g - b) / d) % 6;
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue = (hue * 60 + 360) % 360;
  }
  const name = (HUE_NAMES.find(([edge]) => hue < edge) || HUE_NAMES[HUE_NAMES.length - 1])[1];
  if (L <= 35) return `dark ${name}`;
  /* ⚠ NO "light yellow". Yellow's intrinsic L* is ~85 — it is the lightest hue there is — so a
     lightness qualifier fires on very nearly every yellow and says nothing, while a genuinely dark
     yellow is not dark yellow at all, it is brown. The word alone is the more useful answer. */
  if (L >= 68 && name !== 'yellow') return `light ${name}`;
  return name;
}

/** Hue angle 0–360 off HSL — the same half of `colorWord`'s answer, and for the same reason: hue
 *  angle is what the familiar colour names are named after, where Lab's is rotated away from them. */
function hueOfHex(hex) {
  const h = normalizeHex(hex);
  if (!h) return null;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  if (d === 0) return null;                       // achromatic — it HAS no hue to compare
  let hue = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return (hue * 60 + 360) % 360;
}

/** How much colour a hex actually carries, in Lab. A pale blend scores low; a brand colour high. */
function chromaOfHex(hex) {
  const [, aStar, bStar] = labOfHex(hex);
  return Math.hypot(aStar, bStar);
}

/** Below this the plate is a neutral (black, white, grey) and "far from its hue" is meaningless. */
const RING_PLATE_MIN_CHROMA = 10;
/** Hue distances closer than this are a coin flip — inside one band, the vivid tone wins. */
const RING_HUE_BAND = 30;

/* ── RANKING THE BUYER'S OWN TONES FOR THE *RING* ROLE (Ryan 2026-08-10) ─────────────────────────
 * *"the whole point of this tool is to create the most on-brand weights possible… for the outline
 * colour on the logo, sometimes by % terms it'll be the least prevalent, but it makes it stand out
 * IF it does have one."*
 *
 * He is right, and the cause was precise: the bench of extra logo tones is held in POPULATION order,
 * because that is the order the extractor found them in and it is the right order for a FILL. An
 * outline is small by definition, so ranking ring candidates by area systematically buries exactly
 * the tone a designer chose to outline the mark with. On the Angry Panda badge the orange rim
 * (#E16434, 1023px) sat fourth behind #CC84B6 (1842px) — which is a TINT OF THE PLATE'S OWN PURPLE.
 *
 * ⛔ THIS IS NOT "LEAST PREVALENT WINS", AND THAT DISTINCTION IS THE WHOLE DESIGN. The app knows
 * areas, not roles: the one component that could say "this tone is the outline" is the band detector,
 * and it declines this class of mark (`autoStyle` is null for the panda — see changelog 259 on the
 * Denver badge, whose outermost "band" came back as the white anti-alias rim). So smallest-first
 * would not be reaching for the outline, it would be reaching for whatever tone is smallest, which
 * across real uploads is as often a shadow, an eye highlight, or a JPEG fringe.
 *
 * ⭐ SO IT RANKS BY HUE DISTANCE FROM THE PLATE instead — "of the colours that already work here,
 * which one is least like the thing behind it" — which is a question about the DESIGN rather than a
 * guess about the artwork.
 *   • Quantised to 30° bands, and inside a band the more CHROMATIC tone wins. Without this the panda
 *     picks its pale peach #F2B9A9 over the orange by four degrees — and that peach is very likely an
 *     anti-alias blend of the cream and the orange, which is the exact failure mode the paragraph
 *     above refuses to walk into by another route. Vividness is the honest tie-break: a brand colour
 *     carries chroma, a blend does not.
 *   • ⚠ A NEUTRAL PLATE DISABLES IT ENTIRELY. Black, white and grey have no hue to be far from, so
 *     the order is returned untouched — which means the undyed black plate every single-plate design
 *     opens on is provably unaffected by any of this.
 *   • ⚠ IT ONLY REORDERS. Every candidate still has to pass `ringFitTest` unchanged, so this can
 *     never buy a colour past a contrast rule; it decides between colours that already cleared them.
 *   • ⚠ THE PALETTE ROLES ARE NOT TOUCHED. `secondary` still leads `assignOutlineColors`, because
 *     "the ring the design opened with is the ring it returns to" is what the 2026-08-08
 *     route-independence fix bought, and reordering the roles would spend it.
 */
export function ringPreferenceOrder(hexes, plateHex) {
  const list = (Array.isArray(hexes) ? hexes : []).map(normalizeHex).filter(Boolean);
  const plateHue = hueOfHex(plateHex);
  if (plateHue === null || chromaOfHex(plateHex) < RING_PLATE_MIN_CHROMA) return list;
  const dist = (hex) => {
    const h = hueOfHex(hex);
    if (h === null) return 0;                     // an achromatic tone claims no distance of its own
    const d = Math.abs(h - plateHue);
    return Math.min(d, 360 - d);                  // 0…180
  };
  return list
    .map((hex, i) => ({ hex, i, band: Math.floor(dist(hex) / RING_HUE_BAND), c: chromaOfHex(hex) }))
    // ⚠ `i` is the final tie-break, so equal-band equal-chroma tones keep their extraction order and
    // the result stays deterministic across runs.
    .sort((a, b) => (b.band - a.band) || (b.c - a.c) || (a.i - b.i))
    .map((x) => x.hex);
}

/* ── A LOGO'S OWN NEAR-NEUTRALS ARE NEUTRALS (Ryan 2026-08-10) ───────────────────────────────────
 * `assignOutlineColors` has always said, in as many words, *"House neutrals LAST — a real brand
 * colour always outranks them when picking a ring colour."* It applied that only to the HOUSE set
 * (#FFF, #000), never to a near-neutral sitting in the buyer's own palette — so the Angry Panda's
 * cream `#FBE8D4` led the search purely because it happens to be stored as the `secondary` ROLE
 * rather than as a house accent. That is an implementation accident, not a decision: at Lab chroma
 * 12.5 it is a warm off-white.
 *   The symptom was a ring that would not stay put. Orange won the reveal only because the cream was
 * busy being the FILL; pick any other text colour and the cream came free and took the ring back
 * (*"it did this for every colour I tried except the original one"*). Demoting it is what makes the
 * ring a stable property of the design instead of a leftover of what the fill is not.
 *
 * ⛔ NOT A CONTRAST RULE, AND IT CANNOT HURT LEGIBILITY. Every candidate reaching this ordering has
 * already passed `ringFitTest` against BOTH of its neighbours, and the FILL is what carries
 * legibility anyway (guarded separately by `correctIllegibleFill`, `effectiveTextStyle` and
 * `syncFillContrastOutline`). This only ever chooses between colours that already work.
 *
 * ⚠ THE THRESHOLD IS MEASURED, NOT PICKED. Across the fixture palettes the near-neutrals land at
 * 0–12.5 (Elite's white 0.5, Cinderwood's off-white 1.2 and its near-black brown 11.2, the panda's
 * cream 12.5) and the chromatic brand colours start at 24.4 (the panda's palest peach). Nothing real
 * falls in between, so 18 sits in an empty band and no plausible tone is near the edge.
 *   ⚠ It is NOT `colorWord`'s 14. That number answers "would a person call this grey", a different
 * question, and 14 sits right where the cream is — a slightly warmer cream would flip sides.
 *
 * ⚠ STABLE, so the caller's own priority survives inside each tier: chromatic candidates keep the
 * order they arrived in (roles before bench, so `secondary` still leads whenever it is a real
 * colour — the 2026-08-08 route-independence agreement is untouched for every palette but this one
 * shape), and the neutrals keep theirs behind them.
 *
 * ⚠ AND IT IS NOT GATED ON THE PLATE. A near-neutral is a near-neutral whatever it is painted on, so
 * unlike `ringPreferenceOrder` this applies on the black plate too — which is what Ryan asked for
 * when he saw the black-plate result: *"#FBE8D4 outline (good, but I still think orange would be a
 * better choice here)"*. A palette with nothing chromatic to promote is unaffected by construction.
 */
export const RING_NEUTRAL_MAX_CHROMA = 18;

/** True if a hex carries too little colour to count as one of the brand's actual colours. */
export function isNearNeutralHex(hex) {
  return chromaOfHex(hex) < RING_NEUTRAL_MAX_CHROMA;
}

/**
 * Move near-neutral candidates behind the chromatic ones, preserving order within each tier.
 * @param {Array} items candidates in the caller's own priority order
 * @param {(x:any)=>string} [toHex] resolves an item to a hex (refs → palette lookup); defaults to identity
 */
export function demoteNearNeutrals(items, toHex = (x) => x) {
  const list = Array.isArray(items) ? items : [];
  const colour = [];
  const neutral = [];
  for (const it of list) (isNearNeutralHex(toHex(it)) ? neutral : colour).push(it);
  return [...colour, ...neutral];
}

/** "white (#FFFFFF)" — the readable word AND the value a proof has to match. */
export function plateColorLabel(hex) {
  const h = normalizeHex(hex);
  return h ? `${colorWord(h)} (${h})` : colorWord(null);
}

/** True if two hexes collide as an adjacent edge (perceptually too close). */
export function colorsConflict(hexA, hexB) {
  return deltaE(labOfHex(hexA), labOfHex(hexB)) < CONFLICT_DELTA_E;
}

/**
 * A NEAR-merge against a base surface: not close enough for `colorsConflict`'s 14, but close
 * enough in ΔE that failing the luminance bar leaves the ink a ghost (Ryan 2026-08-18, BC
 * Tornados: navy + slate linework on the dealt slate plate — both tones sit at ΔE ≈ 15 from the
 * plate AND fail `readsOn`, so the whole spiral rendered as a smudge he read as "super blurry").
 * The dealt plates come from the mark's own tones snapped toward orderable dyes, so
 * band-vs-plate pairs land in this 14–26 no-man's-land BY CONSTRUCTION — `colorsConflict` alone
 * can never see them.
 *   ⚠ BOTH clauses, deliberately: ΔE alone at 26 would catch legible chroma pairs; `readsOn`
 * alone would catch orange-on-teal (1.1:1) — E Logo's protected keep, ΔE 76, visibly fine
 * because chroma carries it. Measured points: BC 15.3 (catch), white-on-cream 20.3 (catch —
 * genuinely illegible), E Logo orange-on-teal 76 (safe), chromatic distinct pairs 40+.
 */
export const NEAR_MERGE_DELTA_E = 26;
export function nearMerges(baseHex, inkHex) {
  if (!baseHex || !inkHex) return false;
  const d = deltaE(labOfHex(baseHex), labOfHex(inkHex));
  if (d >= NEAR_MERGE_DELTA_E) return false;
  return d < CONFLICT_DELTA_E || !readsOn(baseHex, inkHex);
}

/**
 * Roughly the just-noticeable difference in Lab. DELIBERATELY far tighter than CONFLICT_DELTA_E (14):
 * that one answers "would these two read as one edge?", which is a much looser question than "are
 * these the same colour?". Reusing 14 to dedupe a palette would merge genuinely distinct brand
 * colours — two reds a school actually uses — and silently take a choice away from the buyer.
 */
export const SAME_COLOR_DELTA_E = 2;

/**
 * True if the eye cannot separate two hexes (Ryan 2026-07-25). Palette extraction on a logo with
 * anti-aliased white edges routinely yields several near-identical whites — #FDFDFD and #FEFEFE
 * differ by one step per channel — and an exact-hex dedupe keeps them all, so the colour rows show
 * duplicate chips that look like one colour listed twice. Any white-heavy logo hits this, not just
 * the one that surfaced it.
 */
export function colorsIndistinguishable(hexA, hexB) {
  return deltaE(labOfHex(hexA), labOfHex(hexB)) < SAME_COLOR_DELTA_E;
}

/** The buyer's loaded color candidates for an auto-recolor: palette roles + saved swatches,
 *  deduped — exactly the list the Colors panel shows. Carried on the (transient) render palette
 *  as `.pool` so a fallback never invents a color the buyer can't see (Ryan 2026-07-23). */
/**
 * HOUSE NEUTRALS — always available to pick, on every design (Ryan 2026-07-25).
 *
 * These are NOT brand colours and NOT the buyer's: they must never live in `profile.swatches`, which
 * is the bench of colours the buyer actually saved and which rides into the quote as their curation.
 * A one-tone logo briefly got them pushed onto that bench (entry 63) — right behaviour, wrong shelf:
 * "Reset to original" could strip them, they counted against the swatch cap, and the design carried
 * two colours nobody had chosen. Keeping them as DEFAULTS means every design has something to build
 * contrast from without a mono/multi special case.
 *
 * Ordered white-then-black, and appended LAST everywhere, so a real brand colour always outranks a
 * neutral when the auto-contrast chain is choosing.
 */
export const DEFAULT_ACCENTS = Object.freeze(['#FFFFFF', '#000000']);

export function contrastPoolFrom(palette, swatches) {
  const p = palette || {};
  const list = [p.primary, p.secondary, p.tertiary, ...(Array.isArray(swatches) ? swatches : [])];
  const seen = [];
  for (const c of list) { const h = normalizeHex(c); if (h && !seen.includes(h)) seen.push(h); }
  // House neutrals last, and only the ones that would READ as a new colour here (Ryan 2026-07-25).
  // Exact-hex dedupe isn't enough: a near-black logo (#0B0C0F) is not #000000, so both would sit in
  // the pool as two indistinguishable blacks — a duplicate chip, and a "contrast" partner that
  // merges with the very colour it is meant to contrast. colorsConflict is the right test (ΔE<14 =
  // "these two read as one"); SAME_COLOR_DELTA_E (ΔE<2) is for literal identity and lets #0B0C0F by.
  for (const n of DEFAULT_ACCENTS) {
    if (!seen.some((h) => colorsConflict(h, n))) seen.push(n);
  }
  return seen;
}

/** The house neutrals worth offering ALONGSIDE `taken` — i.e. those that don't merge with a colour
 *  already on the row. Shared by the swatch row and the contrast pool so they can't disagree. */
export function usableAccents(taken) {
  const have = (Array.isArray(taken) ? taken : []).map(normalizeHex).filter(Boolean);
  return DEFAULT_ACCENTS.filter((n) => !have.some((h) => colorsConflict(h, n)));
}

/**
 * The color ref MOST perceptually distinct from `baseHex`, chosen from the buyer's OWN loaded
 * swatches (`palette.pool` = roles + saved swatches; falls back to just the three roles if no pool
 * was threaded). White/black are a LAST RESORT only — used when no loaded swatch is distinct enough
 * from the base to read (ΔE floor), never in preference to a real swatch. This is why a yellow fill
 * on a yellow plate becomes the loaded DARK GREEN, not an invented black (Ryan 2026-07-23). Returns
 * a role ref when the winner is a palette role, else the literal hex.
 */
export function bestContrastRef(palette, baseHex) {
  const p = palette || {};
  const baseLab = labOfHex(baseHex);
  const pool = (Array.isArray(p.pool) && p.pool.length ? p.pool : [p.primary, p.secondary, p.tertiary])
    .map(normalizeHex).filter(Boolean);
  let best = null, bestDE = -1;
  for (const c of pool) { const de = deltaE(baseLab, labOfHex(c)); if (de > bestDE) { bestDE = de; best = c; } }
  // Escape to a legible neutral ONLY when the best loaded swatch would still merge with the base
  // (so the text never vanishes) — never in preference to a swatch that reads fine.
  if (!best || bestDE < CONFLICT_DELTA_E) {
    // Whichever HOUSE NEUTRAL is furthest from the base. Was two hardcoded hexes, which quietly
    // duplicated DEFAULT_ACCENTS — change the neutrals and this would have kept its own pair
    // (Ryan 2026-07-25 audit).
    best = DEFAULT_ACCENTS.reduce((a, c) => (deltaE(baseLab, labOfHex(c)) > deltaE(baseLab, labOfHex(a)) ? c : a));
  }
  const h = normalizeHex(best);
  if (h === normalizeHex(p.primary)) return 'primary';
  if (h === normalizeHex(p.secondary)) return 'secondary';
  if (p.tertiary && h === normalizeHex(p.tertiary)) return 'tertiary';
  return best;
}

/**
 * Contextual-contrast remap for a LOGO's own brand colors on a dyed plate — the raster
 * analogue of effectiveTextStyle's fill peel (Ryan 2026-07-23). An outline-less logo bakes
 * its colors into the raster, so a green mark on a freshly-dyed green plate would vanish. For
 * each brand color that MERGES with the base (ΔE), swap it to the brand color that best stands
 * out — i.e. the other, non-conflicting brand color most distinct from the base (green↔yellow),
 * falling back to a neutral (white/black) only if every other brand color also collides.
 *
 * Pure + reversible: derived from the base each call, never stored. Black/undyed base → no
 * remap (returns []). Caller applies it as a raster color-replace and keys it into the raster
 * cache so un-dyeing brings the original colors straight back.
 * @param {string[]} colors  the logo's brand colors (palette roles), any order
 * @param {string} bodyColor the dyed plate base
 * @returns {{from:string,to:string}[]} normalized-hex swap pairs (from ≠ to)
 */
/* ⚠ TWO OPT-IN OPTIONS, ADDED 2026-08-10, BOTH DEFAULTED OFF so every existing caller is byte-for-byte
 * unchanged. They exist for ONE caller — main.js's band repaint — and the defaults are what keep the
 * band-less fill swap's asserted behaviour ("black/undyed plate → no logo remap") exactly as it was.
 *
 *   • `allowDark` — consider a near-black base instead of bailing. A BAND that merges with a black
 *     plate is invisible and wants rescuing; a band-less mark's FILL on the stock black plate is the
 *     artwork as drawn and does not. Same function, two questions, so the caller says which.
 *   • `avoid` — a colour the swap target must NOT land on, because the band physically touches it.
 *     Without this the Cherry Valley crest repaints its black keyline to the ONLY other brand colour
 *     available — the rose fill it wraps — producing a flat rose silhouette with the outline gone in
 *     a different way. `safe[0]` optimises against the PLATE and is blind to the band's other
 *     neighbour, which is the same two-neighbours lesson `ringFitTest` already encodes for text.
 *     With the fill excluded there is no safe brand colour left, so it falls to the neutral — white
 *     on black, which is exactly what Ryan asked for and what the eye expects. */
/* ⛔ AND A THIRD, `alsoRescue`, 2026-09-17 — because `conflicts` ASKS THE WRONG QUESTION on a plate
 * the ink merely disappears into. `colorsConflict` is ΔE < 14: *"are these the same colour."* Ryan,
 * reading layton-high-school cfg3 (navy mark, black plate): *"on the dark blue, the logo changes
 * colour so it doesn't conflict… if we did a similar thing on the black, since the black is so
 * close in colour/darkness."* Measured, his navy `#0F1F36` against the black plate `#0B0C0F` is
 * ΔE **17.8** — NOT the same colour, so the swap declines — at contrast **1.18:1**, which is
 * invisible. Two different questions, and this function only ever asked the first.
 *   ⚠ IT IS OPT-IN BECAUSE THE WIDER TEST IS A BAD TRADE UNGATED: "conflicts OR cannot be seen"
 * fires on 104 plates of the 528 — 11 flagged, 93 approved — and moves Kenyon, E Logo, Malone and
 * Wexmoor. The caller owns the gate; this parameter only lets a caller ADD a tone to the conflict
 * set. `bandConflictView` passes nothing and is byte-identical. */
export function fillContrastRemap(colors, bodyColor, { avoid = null, allowDark = false, validate = null, bothNeutrals = false, alsoRescue = null } = {}) {
  const base = normalizeHex(bodyColor);
  if (!base || (!allowDark && isNearBlackHex(base))) return [];
  const pool = [...new Set((colors || []).map(normalizeHex).filter(Boolean))];
  if (pool.length < 1) return [];
  const baseLab = labOfHex(base);
  const conflicts = pool.filter((c) => colorsConflict(base, c) || (alsoRescue ? alsoRescue(c) : false));
  if (!conflicts.length) return [];
  const near = normalizeHex(avoid);
  // Non-conflicting brand colors, most-distinct-from-base FIRST = the swap targets.
  const safe = pool
    .filter((c) => !conflicts.includes(c))
    .filter((c) => !(near && colorsConflict(near, c)))
    .sort((a, b) => deltaE(baseLab, labOfHex(b)) - deltaE(baseLab, labOfHex(a)));
  /* ⛔ THE LADDER USED TO END AT ONE HOUSE NEUTRAL AND THEN GIVE UP WITH THE OTHER ONE SITTING
   * RIGHT THERE (Ryan 2026-09-15, Cherry Valley: *"The D7A8A3 section of the logo should have
   * changed to WHITE on this plate."*). On its rose plate the only brand colour left is the black
   * wreath, `validate` vetoes it for fusion, and the single neutral is BLACK too — most distinct
   * from a light plate — so it is vetoed by the same adjacency and the walk ends empty. White was
   * never offered, and white is the answer he named.
   *   ⚠ OPT-IN, because "no survivor" is not the same outcome for both callers. For
   * `bandConflictView` a missing pair means PEEL — a real, deliberate fallback that shows the plate
   * through — and handing it a second neutral turns peels into repaints: measured, malonepioneers
   * cfg3 (a named hard control) stops peeling its silver band and repaints it white. For
   * `contextualFillSwap` there is no fallback at all; "no survivor" means the ink stays invisible.
   * So the interior swap asks for both rungs and the band rescue keeps exactly today's ladder. */
  /* ⛔ AND A NEUTRAL IS A TARGET, SO IT OBEYS THE SAME INVARIANT AS EVERY OTHER TARGET: it must not
   * merge with the base. `safe` gets that for free (it is the pool MINUS the conflicts); the neutral
   * tail never did, and with one neutral — always the more distinct of the two — it never mattered.
   * The second rung is the near one by construction, and unguarded it produced five swaps of an
   * off-white to pure white ON AN OFF-WHITE PLATE (120039000594 cfg2 and four never-judged plates,
   * measured on the pass-three render). That is not a rescue; it is a repaint to the plate. */
  const neutrals = ['#FFFFFF', '#000000']
    .filter((h) => !colorsConflict(base, h))
    .sort((a, b) => deltaE(baseLab, labOfHex(b)) - deltaE(baseLab, labOfHex(a)));
  const tail = bothNeutrals ? neutrals : neutrals.slice(0, 1);
  const out = [];
  for (const c of conflicts) {
    /* `validate(from, to)` — an opt-in veto per candidate, walked down the ladder rather than
     * applied to a single pre-chosen winner. Its one caller (2026-08-18) passes the tone-adjacency
     * test: a target the source tone TOUCHES in the artwork would fuse two regions into one shape
     * (Edison's blue linework repainted white against its own white teeth), which no plate-side
     * contrast test can see. A conflict with NO surviving candidate is OMITTED from the result —
     * `bandConflictView` reads a missing pair as "peel", which shows the plate through instead,
     * and a plate-merging tone already looked plate-coloured, so nothing is lost that was visible.
     * Default null → the first candidate always wins, byte-identical to before. */
    const to = safe.concat(tail)
      .find((t) => t && normalizeHex(t) !== c && (!validate || validate(c, normalizeHex(t))));
    if (to) out.push({ from: c, to: normalizeHex(to) });
  }
  return out;
}

/**
 * The logo's tones AS THE BUYER WAS SHOWN THEM — one entry per swatch that appeared on the Add Logo
 * tab, in the same order, as hexes.
 *
 * ⚠ THIS EXISTS BECAUSE TWO SURFACES WERE COUNTING COLOURS DIFFERENTLY (Ryan 2026-07-28: "uploaded a
 * 3-colour logo, changed all 3 on the Add Logo tab, then the default swatch under Logo color was only
 * split in half — it hasn't picked up on the third colour"). The reveal chips took primary and
 * secondary unconditionally plus any accent over 3% of pixels, deduped by exact hex.
 * `significantLogoTones` — the only thing the CD swatch had to go on — clusters at ΔE < 24 and
 * demands 6%. So a third tone could be plainly visible as its own chip and still be merged or
 * filtered out of the swatch that claims to show the same logo. Nothing to do with his edits; the two
 * had simply never been asked to agree in front of a logo that could tell them apart.
 *
 * The rule lives here, once, and both call it. Which of the two rules to keep was not really a
 * choice: the chips are what the buyer has already been told their logo contains, so anything else
 * claiming to show "the logo's colours" has to match that or be wrong by definition.
 *
 * `significantLogoTones` below is unchanged and still the right answer for its own question — which
 * tones are big and distinct enough to reason about for CONTRAST — where merging near-duplicates is
 * the point rather than a loss.
 *
 * @param {object} palette the extractor palette (`state.ui.palette`)
 * @param {number} [minFraction] pixel share an accent must reach — the reveal's own 3%
 * @returns {string[]} hexes, or [] when there is no confident palette (a resume may have none)
 */
export function shownLogoTones(palette, minFraction = 0.03) {
  const p = palette || {};
  if (p.confidence !== 'high' || !p.primary) return [];
  const all = Array.isArray(p.all) ? p.all : [];
  const total = all.reduce((s, c) => s + (c && c.population > 0 ? c.population : 0), 0) || 1;
  const out = [];
  const seen = new Set();
  const push = (sw) => {
    const hex = normalizeHex(sw && sw.hex);
    if (!hex || seen.has(hex)) return;
    seen.add(hex);
    out.push(hex);
  };
  push(p.primary);
  push(p.secondary);
  for (const a of p.accents || []) {
    if ((a && a.population > 0 ? a.population : 0) / total >= minFraction) push(a);
  }
  /* ⚠ CAPPED AT SEVEN (Ryan 2026-08-08: *"I'm considering adding up to 1 more color on the extraction
     from logos… do you see any issue with it extracting a maximum of 7 instead?"*). Six until then.
       ⛔ THIS NUMBER AND `significantLogoTones`' MUST MOVE TOGETHER. They are two answers to "what
     colours does this logo have", and the note on that function records what it cost when they last
     disagreed: a tone with its own chip here was missing from the Original swatch claiming to show
     the same logo. Changing one alone re-opens exactly that.
       The original cap was justified by the colour picker's tab strip splitting a popover's width
     between tones. That strip no longer exists in `color-picker.js` — the phone picker is a
     full-width sheet — so the constraint that set the number is gone. What DOES still bind was
     measured before this moved: the Add Logo tab's chip row, which now shrinks its swatches to fit
     one line rather than wrapping (weight-designer.css), and holds 7 at 38.8px on a 390px phone.
       ⚠ And the SHARED BENCH is the real cost, not the pixels: logo tones live in the same pool as
     the buyer's own colours, so a 7th tone spends one of the free slots. `SWATCH_CAP` went 8 → 9 in
     the same change to pay for it. Raising this again without raising that quietly takes a colour
     away from the buyer. */
  return out.slice(0, 7);
}

/**
 * The logo's significant distinct TONES for the "Original" fill swatch (Ryan 2026-07-23) — the
 * extracted-palette colors that each own at least `minFraction` of the art, population-desc and
 * deduped, so the swatch can be split into that many clean even bands (2-tone → halves, 3 →
 * thirds, 4 → quarters, …). The fraction floor drops anti-alias strays so a clean 2-tone logo
 * reads as 2, not 4. Falls back to the named palette roles when no extraction is on hand, and
 * caps at 7 — ⛔ THE SAME NUMBER AS `shownLogoTones`, and moved with it on 2026-08-08. See the note
 * at that cap: these are two answers to one question and they have already been out of step once.
 * At 7 the Original swatch's bands are ~6.6px inside a 46px chip, against 7.7px at six.
 * @param {{hex:string,population:number}[]} all  extracted swatches (state.ui.palette.all)
 * @param {string[]} roles  [primary, secondary, tertiary] fallback
 * @param {number} [minFraction=0.06]
 * @returns {string[]} normalized hexes, most-populous first (≤7)
 */
export function significantLogoTones(all, roles, minFraction = 0.06) {
  const list = Array.isArray(all) ? all : [];
  const total = list.reduce((s, c) => s + (c && c.population > 0 ? c.population : 0), 0);
  if (total > 0) {
    // CLUSTER perceptually-near-duplicate colors so a clean 2-tone logo doesn't sprout a thin 3rd
    // band from an anti-alias near-dup — two slightly different yellows read as ONE tone (Ryan
    // 2026-07-23). Population accumulates onto each cluster's representative; `all` is population-
    // desc so the representative (first seen) is the most-populous of its cluster, and the visual
    // left→right order the user already likes is preserved.
    const clusters = [];
    for (const c of list) {
      const hex = normalizeHex(c && c.hex);
      const pop = c && c.population > 0 ? c.population : 0;
      if (!hex || pop <= 0) continue;
      const lab = labOfHex(hex);
      const near = clusters.find((k) => deltaE(k.lab, lab) < TONE_MERGE_DELTA_E);
      if (near) near.population += pop;
      else clusters.push({ hex, lab, population: pop });
    }
    const seen = clusters.filter((k) => k.population / total >= minFraction).map((k) => k.hex);
    if (seen.length >= 2) return seen.slice(0, 7);
  }
  const r = [];
  for (const x of (roles || [])) { const h = normalizeHex(x); if (h && !r.includes(h)) r.push(h); }
  return r.slice(0, 7);
}

/**
 * EFFECTIVE fill/outline stack for a text/weight element on a given plate base —
 * a pure, reversible view over the element's real stack. Outside-in: drop each outermost outline
 * that merges with the base, stopping at the first distinct ring. Then, if the FILL merges with the
 * base, recolor the NUMBER to the most-distinct loaded colour.
 *
 * Two matching-plate behaviours, keyed by `palette.logoOutlined` (Ryan 2026-07-23):
 *   • Logo has NO natural outlines (the text's single ring is synthetic) → go SOLID: recolor the
 *     fill even if that ring survives, and drop the now-redundant ring → "green, no outline",
 *     symmetric with the peel (whichever brand colour matches the plate is dropped, the OTHER fills
 *     the glyph).
 *   • Logo IS outlined → keep the SAME layered stack the logo has (insulated fill): only recolor
 *     the fill once every ring has peeled, so the text mirrors the logo's outline count.
 *
 * Never reorders. Because it derives from the element's OWN stack every call (not a prior peeled
 * result), it is NON-CUMULATIVE. Black/undyed base → unchanged.
 *
 * LOCKS (Ryan 2026-07-23 — "plate = master switch, but I can override anything below"):
 * choosing a plate colour cascades this auto-contrast view, but a MANUAL edit locks that
 * piece so the view leaves it alone. `element.fillLocked` → never auto-recolour the fill
 * (keep the buyer's green even on a green plate); an outline's `locked` → never auto-peel it
 * (and it stops the outside-in peel, like a distinct ring). Picking a plate colour again
 * clears the locks (re-cascade); Reset clears them too.
 * @returns {{fillRef:string, outlines:{colorRef:string,thickness:number}[]}}
 */
/* `resolveStyle` renders four rings, so a fifth is invisible weight - the same ceiling the old
   outermost-only mirror obeyed when it refused to append to a stack already four deep. */
const MIRRORED_RING_MAX = 4;

export function effectiveTextStyle(element, palette, bodyColor) {
  const e = element || {};
  /* ── THE FILL MIRROR (Ryan 2026-09-17) ────────────────────────────────────────────────────────
   * The outermost mirror below answers "what does the eye meet first on each". This answers the
   * other half, which he stated himself and then corrected twice:
   *     "whichever colour is more prominent by %, the # should have that as its fill colour"
   *     "white center parts and green outer parts.. so the text should follow suit"
   * `palette.logoDominantTone` is that colour, computed per plate by `markDominantTone` (which owns
   * the flip: a tone that owns the mark's OUTER edge goes on the number's outline, not its fill).
   *
   * ⛔ A VIEW, NEVER A MUTATION — derived from the CURRENT plate on every call, nothing stored, the
   * same contract as `logoGap` and `logoOuterTone`. A stored fill would have to be chased by every
   * dye and un-dye.
   * ⛔ AND IT YIELDS TO THE BUYER, ALWAYS. `fillUserSet` means they picked a colour in the panel and
   * a derived rule that overwrites a person's own choice is a bug, not a feature.
   * ⚠ IT ALSO YIELDS WHEN THE ANSWER CANNOT BE SEEN. On 65 of the 528 plates the mark's dominant
   * tone IS the plate, so mirroring it paints the number the colour it sits on; there the stored
   * fill stands and the peel/recolour ladder below does its normal work. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 459, 2026-10-29) — A DARK MIRROR ON THE BLACK PLATE WITH
   * NOTHING AROUND IT. Ryan, athletics-amherst cfg3: *"the dark purple text fill color on the black
   * plate is hard to see. should have made it white"*; msubsports cfg4: *"the dark blue fill color
   * blends in with the dark black plate color"* (its only ring is black). On a near-black plate the
   * mirror yields when the dominant tone does not read there AND no ring — its own or one it mirrors off the logo — does either —
   * the stored fill (the role the seeder chose to read on this plate) stands instead.
   * ⛔ THE RING CLAUSE IS LOAD-BEARING. Without it the same yield moves 8 APPROVED plates, Kenyon
   * cfg4 (a named control) among them — every one a dark fill wrapped in a ring that reads on black
   * (white, gold, grey, red). His two flags are the only dark fills on black with no readable ring.
   * Rung 5's warning stands: this is not a contrast threshold on the fill alone. */
  const unseenOnBlack = (hex) => isNearBlackHex(bodyColor) && !readsOn(bodyColor, hex)
    && ![
      ...(Array.isArray(e.outlines) ? e.outlines : []).filter((o) => o && o.enabled !== false)
        .map((o) => normalizeHex(resolvePaletteRef(o.colorRef, palette))),
      // the rings the number will MIRROR off the logo count too (stallions-na cfg3, approved)
      ...((palette && Array.isArray(palette.logoStack)) ? palette.logoStack : []).map((b) => normalizeHex(b && b.hex)),
    ].some((h) => h && readsOn(bodyColor, h));
  let fillRef = e.fillRef || 'primary';
  const domTone = normalizeHex(palette && palette.logoDominantTone);
  if (domTone && e.fillUserSet !== true && e.fillLocked !== true
      && !colorsConflict(domTone, bodyColor)
      && !unseenOnBlack(domTone)) {
    fillRef = domTone;
  }
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 459, 2026-10-29) — A ONE-COLOUR MARK'S NUMBER WEARS ITS
   * COLOUR ON THE BLACK PLATE. Ryan, Bluffton / gobuffs cfg3: *"The logo went all purple while the
   * weight number went all white … Either both need to be purple or both need to be white."* Shown
   * both, plus a version that ringed logo and number in white, he chose the plain match: *"the first
   * one was better"*; the ringed one *"adds a really thick obnoxious white outline to everything."*
   * Such a mark has no census, so `logoDominantTone` is null and the stored fill (white, picked to
   * read) stood. Here the number takes the mark's one colour instead.
   * ⚠ bctornados / hiuroyals move with it and he accepted that: their artwork carries a thin light
   * outline the census cannot see, so the number lacks it — *"a little off … but it's still the
   * best of the 4 options"*. Detecting that keyline is WD 424's band re-basis, not this rule.
   * ⛔ Never when the mark's colour IS the plate (rigar: black on black). */
  let oneColourOnBlack = false; // WD 487 - see the ring drop under `kept` below
  if (!domTone && e.fillUserSet !== true && e.fillLocked !== true && isNearBlackHex(bodyColor)
      && palette && palette.primary && !palette.secondary && !palette.tertiary
      && !(Array.isArray(palette.accents) && palette.accents.length)) {
    const markHex = normalizeHex(palette.primary);
    if (markHex && !colorsConflict(markHex, bodyColor)) { fillRef = markHex; oneColourOnBlack = true; }
  }
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 481 candidate, 7d) — THE MAIN COLOUR ON ITS OWN PLATE, OVER A BLACK FALLBACK.
     Ryan, loyola 45 lb: *"the logo's main fill is red, the number should fill red too (still black)"*. The mirror above
     skips a dominant tone that IS the plate, so the stored cascade fill stands. Rung 1 (WD 473) took the plate colour
     whenever a ring read and made msubsports 1 / mvclions 1 / ncurams 4 worse - each had a yellow/yellow/white number
     before. Loyola's was BLACK. So only when the number would otherwise be near-black. */
  let mainOnOwnPlate = false; // the ring carries the contrast; step 2 below must not undo it
  if (domTone && e.fillUserSet !== true && e.fillLocked !== true && colorsConflict(domTone, bodyColor)
      && !isNearBlackHex(bodyColor) && !isNearWhitePlate(bodyColor)
      && isNearBlackHex(normalizeHex(resolvePaletteRef(fillRef, palette)))) {
    const ringReads = [
      ...(Array.isArray(e.outlines) ? e.outlines : []).filter((o) => o && o.enabled !== false)
        .map((o) => normalizeHex(resolvePaletteRef(o.colorRef, palette))),
      ...((palette && Array.isArray(palette.logoStack)) ? palette.logoStack : []).map((b) => normalizeHex(b && b.hex)),
    ].some((h) => h && !colorsConflict(h, domTone) && readsOn(bodyColor, h));
    if (ringReads) { fillRef = domTone; mainOnOwnPlate = true; }
  }
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 475, 2026-10-31) — A LIGHT NUMBER ON THE WHITE PLATE WITH NOTHING DARK
   * AROUND IT: the white-plate twin of `unseenOnBlack` above. Ryan, newmanjets cfg2: *"I'm surprised the weight
   * number didn't change to dark navy here."* The hero rule (WD 434) made the mark's white its fill; WD 453's veto
   * then rightly dropped the black ring the logo never needed, and the number was left white on the white plate
   * held only by the mark's 1-unit pale-blue keyline (2.4:1). Seven corpus plates put a white number on a white
   * plate; the six he approved all wear a DARK ring (10:1 and up), so the number reads as an outlined numeral.
   * Here the number's fill IS the plate and no ring it wears or mirrors reaches `TEXT_LEGIBLE_RATIO`, so it takes
   * the mark's first tone that does. Shown the navy number beside a white number with a navy ring, he took either;
   * the ring version would put a ring on the number the logo does not wear (his WD 455 rule), so it is the fill.
   * ⚠ 4.5 is the published text-legibility contrast, not a fitted bar: the gap it sits in runs 2.4 -> 10+. */
  if (e.fillUserSet !== true && e.fillLocked !== true && isNearWhitePlate(bodyColor)) {
    const cur = normalizeHex(resolvePaletteRef(fillRef, palette));
    const held = [
      ...(Array.isArray(e.outlines) ? e.outlines : []).filter((o) => o && o.enabled !== false)
        .map((o) => normalizeHex(resolvePaletteRef(o.colorRef, palette))),
      ...((palette && Array.isArray(palette.logoStack)) ? palette.logoStack : []).map((b) => normalizeHex(b && b.hex)),
    ].some((h) => h && contrastRatio(bodyColor, h) >= TEXT_LEGIBLE_RATIO);
    if (cur && colorsConflict(cur, bodyColor) && !held) {
      const dark = ((palette && palette.logoToneOrder) || []).map(normalizeHex)
        .find((h) => h && contrastRatio(bodyColor, h) >= TEXT_LEGIBLE_RATIO);
      if (dark) fillRef = dark;
    }
  }
  // Disabled outlines are dropped from the render stack (kept in state for the UI). Keep the
  // `locked` flag on the working copy so the peel can respect it, strip it from the output.
  let kept = Array.isArray(e.outlines)
    ? e.outlines.filter((o) => o && o.enabled !== false).map((o) => ({ colorRef: o.colorRef, thickness: o.thickness, locked: o.locked === true }))
    : [];
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — …AND IT WEARS NO RING THE PLAIN LOGO DOES NOT WEAR. The rule
   * above gives a one-colour mark's number the mark's colour on black ("it has to be A" - the plain match, the ringed
   * version rejected). It set the FILL only, so a rescue ring stored earlier for a different fill rode along: DB Logo
   * with See-through (its white cut away, so a one-colour green mark) showed a green logo beside green type in a white
   * ring - Ryan, UX run 1: *"shouldn't be a discrepancy"*, and his WD 455 rule *"the weight numbers should never have
   * a ring the logo doesn't have"*. Only when the logo presents no outline here, and never a ring the buyer added or
   * coloured himself. */
  if (oneColourOnBlack && !((palette && Array.isArray(palette.logoStack)) ? palette.logoStack : []).length) {
    const own = (Array.isArray(e.outlines) ? e.outlines : []).filter((o) => o && o.enabled !== false);
    kept = kept.filter((_, i) => own[i] && (own[i].added === true || own[i].userSet === true));
  }

  /* ── THE FILL MUST NOT BE ONE OF ITS OWN OUTLINES (Ryan 2026-09-17) ───────────────────────────
   * *"the 'WITH THE RULE' goes with a black text fill with black outline color.. never do two same
   * colors on top of each other like this with text"* — and again, unprompted, on four more marks he
   * had just voted FOR: *"we can't have blue text fill with blue outline... that needs to be fixed
   * before we call this done and dusted."* Every plate he rejected in the mirror trade carries this
   * and nothing else; Kenyon, the one control he chose the new rule on, is the one with no clash. So
   * it was a single defect sitting on top of the fill mirror, not a fault in the mirror.
   *
   * ⭐ HE CHOSE THE REMEDY FROM PICTURES, 11 of 12 to 1, and the single exception is the whole shape
   * of the rule: STEP THE FILL to the mark's next tone and keep the rings — except on
   * `gopatriotathletics|cfg3`, where he picked DROP THE RING and wrote *"the blue outline against
   * the blue text fill looks wrong. that's never acceptable."*
   *
   * ⛔ AND cfg3 IS NOT A PREFERENCE, IT IS THE CASE WHERE STEPPING CANNOT WORK. That mark owns three
   * tones and on that plate every one is unavailable: navy is already the outline, white is already
   * the second outline, and red IS the plate. So the fill has nowhere to step and, alone, would
   * leave the clash he calls never acceptable. Dropping the ring always works — it needs no colour
   * that does not exist. Hence: step the fill, and fall back to dropping the ring when there is
   * nothing to step to. Neither half is a threshold and neither was chosen by this session.
   *
   * ⚠ ORDER MATTERS AND IT IS NOT OBVIOUS: the fallback must be tried per plate, not per mark. The
   * same mark steps its fill on three plates and drops a ring on the fourth. */
  const resolveClash = () => {
    const fillHex = normalizeHex(resolvePaletteRef(fillRef, palette));
    /* ⛔ ONLY THE RING THAT TOUCHES THE FILL COUNTS, and reading it as "any ring" was wrong.
     * `render-engine.smoke` caught it: a deliberate three-outline stack whose OUTERMOST ring is the
     * fill colour, with two other rings in between, stopped emitting one of its seven bands. That
     * stack is not what Ryan is objecting to — his words are *"a black text fill with black outline
     * color"*, two surfaces that meet. Two rings apart, the same colour twice is a legitimate
     * layered look and the app has always drawn it.
     * ⚠ `kept` runs INNER → OUTER (see `effectiveTextStyle`'s header), so the ring against the fill
     * is `kept[0]`, not the last one. All twelve plates he judged clash on exactly that ring, so
     * narrowing the test changes none of his answers. */
    /* ⚠ TWO DIFFERENT TESTS, AND COLLAPSING THEM INTO ONE BREAKS IT EITHER WAY.
     *   TRIGGERING is about ADJACENCY — only the ring that touches the fill, because that is what
     *     Ryan objected to (*"a black text fill with black outline color"*). Any-ring triggering
     *     fires on a legitimate layered stack; `render-engine.smoke` is the canary for it.
     *   CHOOSING a replacement avoids EVERY ring, because a fill that lands on an outer ring's
     *     colour has simply moved the duplicate rather than removed it. Relaxing this to adjacency
     *     too re-drew half the plates Ryan had already judged — malonepioneers cfg2 went to the grey
     *     it wears two rings out, where he picked the white. */
    const ringHex = kept.map((o) => normalizeHex(resolvePaletteRef(o.colorRef, palette)));
    /* ⛔ `kept[0]` IS NOT WHAT THE RENDERER DRAWS AGAINST THE FILL WHEN THE MARK HAS A GAP
     * (2026-09-17, found by the pass-six render on Hanover cfg2 — a NAMED CONTROL).
     *   `strip` is `mirrorStack(withGap(...))`, and `withGap` PREPENDS a plate-coloured moat, so on
     * a gap mark the ring against the fill is the moat and the fill never touches `kept[0]` at all.
     * Reading the pre-gap stack fired the remedy on a clash the buyer cannot see, dropped the only
     * real ring, and took the moat with it (`withGap` needs a surviving ring to hang off): Hanover
     * cfg2 went `['#F1F3F3@1', '#990100@1']` -> `[]`, a bare red number on a near-white plate, on
     * the very mark the gap mirror was built for.
     *   FIFTH occurrence in this file of a rule reading a colour the plate never shows — the same
     * fault as R10's band read, the Kenyon repaint trap, `syncLogoContrastRing`'s drawn-vs-presented
     * primary, and WD 416's near-black swap.
     *   And it resolves to "no clash" rather than to a different clash: `gapToneFor` only returns a
     * moat when the fill does NOT conflict with the plate, so a drawn moat is by construction a ring
     * the fill cannot clash with. 3 plates move — hanover cfg2, cmlions cfg1 + cfg4. */
    const gapTone = gapToneFor(kept, fillRef);
    const innerRing = gapTone || (ringHex.length ? ringHex[0] : null);
    const touchesFill = (h) => !!(h && innerRing && colorsConflict(innerRing, h));
    const anyRing = (h) => ringHex.some((c) => c && h && colorsConflict(c, h));
    if (!fillHex || !touchesFill(fillHex)) return;
    // FIRST: step the fill down the mark's own tone order, to one that duplicates nothing
    const order = (palette && palette.logoToneOrder) || [];
    const next = order.map(normalizeHex).filter(Boolean)
      .find((h) => !anyRing(h) && !colorsConflict(h, base));
    if (next) { fillRef = next; return; }
    // OTHERWISE the mark has no tone left to offer — drop the ring that touches the fill
    kept = kept.slice(1);
  };
  /* ⛔ A "BARE WHERE THE MARK IS BARE" DROP LIVED HERE FOR ~AN HOUR (2026-08-18) AND ATE THE
   * BUYER'S OWN RINGS — Ryan: "no matter what combination of outlines I add to this text on this
   * white plate, nothing shows up." It filtered to `locked` only, and a hand-ADDED ring isn't
   * locked. His actual design call is the opposite anyway: keep the layered treatment everywhere
   * and ADD a contrasting outer ring on the plate that merges the keyline — see the contextual
   * outer ring in the composite path (`contextualOuterRing`) and the note at `withGap`. */
  /* ── THE MARK'S OWN GAP, MIRRORED (Ryan 2026-08-18) ────────────────────────────────────────────
   * A mark whose keyline stands OFF its fill — Hanover: red letters, a keyed-out moat where the
   * navy field was, then the white keyline — shows the PLATE through that gap on every plate. The
   * type beside it wore only the keyline, so the two treatments never matched. Ryan: "should we
   * implement a forced blue outline followed by a white outline around the text to match that
   * same aesthetic?" — and a plate-coloured inner ring IS a gap, rendered.
   *   A VIEW, not a mutation (the contextual-contrast rule): derived here from the CURRENT plate
   * every call, so it tracks every dye and un-dye with nothing stored and nothing for the cascade
   * to chase. `palette.logoGap` is set by the render-palette builders from the structure
   * (`logoGapFromStructure`: the innermost band does not touch the fill). Deliberately exempt
   * from `ringFitTest` thinking — this ring does not want contrast with the plate, it wants to BE
   * the plate; the ring outside it carries the separation, which is why it is only added while at
   * least one real ring survives (a gap with nothing beyond it is nothing). Thickness mirrors the
   * ring beside it, matching the mark's own even moat. Skipped at 4 rings — `resolveStyle` caps
   * the stack and prepending would push the buyer's outermost ring off the render. */
  /* ⛔ ONE ANSWER, ONE PLACE. `resolveClash` needs to know whether a gap ring will be DRAWN before
   * it can say which ring touches the fill, and `withGap` needs the same answer to draw it. Two
   * copies of this predicate is how the copies disagree — the fault this file already names twice
   * ("WHEN TWO PLACES NEED THE SAME ANSWER, ONE OF THEM WILL BE WRONG"). Returns the moat's tone,
   * or null when no moat is added. */
  const gapToneFor = (list, fillRefUsed) => {
    const b = normalizeHex(bodyColor);
    if (!(palette && palette.logoGap) || !b || !list.length || list.length >= 4) return null;
    /* ⛔ A MOAT THE COLOUR OF THE NUMBER IS NOT A MOAT (Ryan 2026-09-15, Mount Mercy cfg1).
     * The gap ring is plate-coloured on purpose — it renders the keyed-out gap the mark itself
     * carries. That reads as a gap only while the fill differs from the plate. When the mark's
     * primary IS the plate colour, the moat lands on a fill of its own colour, separates nothing
     * from nothing, and simply fattens the glyph before the first real ring. Ryan, shown Mount
     * Mercy's yellow number on its yellow plate: *"the weight number looks very big and bulky…
     * if we simply just added a yellow outline to an already yellow number, then that's where the
     * 'no no' stems from."* He had it exactly — `[#FDB517@1.5, #033C26@1.5]` on a `#FDB517` fill.
     *   Measured over all 548 plates of the corpus before it was written: FOUR plates do this, and
     * three of them are his own flags — newmanjets cfg1, kingtornado cfg1 and cmlions cfg1, all
     * three filed as "really thick obnoxious multiple outlines". The fourth is Mount Mercy. No
     * threshold, nothing tuned, and nothing else in the corpus moves. */
    const fillHex = normalizeHex(resolvePaletteRef(fillRefUsed, palette));
    if (fillHex && colorsConflict(fillHex, b)) return null;
    return b;
  };
  const withGap = (list, fillRefUsed) => {
    const b = gapToneFor(list, fillRefUsed);
    if (!b) return list;
    /* ⭐ THE MOAT IS WEIGHED FROM THE ARTWORK, NOT FROM THE RING BESIDE IT (Ryan 2026-09-16,
       corpus pass three — kingtornado: *"really thick/obnoxious outlines again"*, one of the
       seven plates in his 76 that are a stack complaint).
         It used to mirror `list[0].thickness`, on the stated reasoning that the mark's own moat is
       even with its keyline. But `list[0]` is a BAND ECHO, and `analyzeStructure` normalises a
       mark's bands so their median lands on 2.5 whatever the linework actually measures — the same
       fact `presentedOuterOutline` already names when it refuses to mirror a band's stored weight
       ("A BAND CARRIES NO THICKNESS"). So on a finely-ruled gap mark the moat was minted at 2.5 on
       top of a stack that had already spent 2.5, and nothing budgeted the total: kingtornado's
       number wore 6.0 units of ring where the mark's own line measures 1.0.
         `palette.logoInkWeight` is `outlineThicknessFor` — the artwork's measured linework, on the
       same 0.5 grid and under the same 2.5 ceiling as every other derived ring. Measured over all
       20 gap plates in the corpus before it was written: Hanover x4, Mount Mercy x4, cmlions x4 and
       kingtornado cfg1 are BYTE-IDENTICAL (their moats already equalled their linework), and the
       only plates that move are six Ryan flagged. Zero approved plates change. */
    /* ⛔ CLAMPED HERE, NOT TRUSTED FROM THE PALETTE. `logoInkWeight` is `outlineThicknessFor`
       today and that function already caps itself, so this changes nothing on any live path — which
       is exactly why it has to be written down: the guarantee "no derived ring out-weighs one text
       ring" belongs where the ring is MINTED, not in the caller that happens to honour it now.
       `derived-ring-weight.check.mjs` asserts it against a runaway input for that reason. */
    const ink = Number(palette && palette.logoInkWeight);
    const raw = ink > 0 ? ink : (Math.max(0, Number(list[0].thickness) || 0) || 1);
    const moat = Math.max(0, Math.min(DEFAULT_OUTLINE_THICKNESS, raw));
    return [{ colorRef: b, thickness: moat }, ...list];
  };
  /* ── THE MIRROR: THE NUMBER WEARS WHAT THE MARK WEARS OUTERMOST (Ryan 2026-09-15) ─────────────
   * His criterion for the whole corpus audit, in one sentence he gave twenty times over on
   * `RING-QUESTION.html`:
   *
   *   > **The outermost outline of the MARK and the outermost outline of the NUMBER must read as
   *   > the same colour.**
   *
   * `palette.logoOuterTone` is the left-hand side, computed per plate by `presentedOuterOutline`
   * and handed down by both render-palette builders beside `logoOutlined` / `logoGap` /
   * `logoBandsVisible`. This is the right-hand side, and like every one of those it is a VIEW
   * derived from the CURRENT plate on every call — nothing stored, nothing for the cascade to
   * chase, and it un-dyes by itself.
   *
   * ⭐ IT ADDS, IT NEVER RECOLOURS. That is Ryan's own standing rule from the 08-24 pass, stated
   * there about the MARK and true here about the type for the same reason: the ring the number
   * already wears is usually the mark's INNER band, mirrored correctly by `assignOutlineColors`'s
   * band echo, and repainting it would trade one mismatch for another. His words on the two
   * cleanest cases are literally "missing":
   *     benu cfg1     *"the plate number only has a thin black outline around it and it's plain to
   *                    see that it's MISSING the white outline that the logo has."*
   *     120018000234  *"The logo has a very visible black outline then a white one outside of that.
   *                    the weight number only has the white one, no black."*
   *   Vikings is the proof that "add" is the whole answer rather than a first guess: he read it as
   * *"flip-flopped"*, and the dump says the number already wears the mark's two bands in the right
   * order and is simply missing the white rescue ring outside them. Appending it makes the two
   * stacks identical.
   *
   * ⛔ FOUR GUARDS, EACH ONE A PLATE HE JUDGED:
   *   • nothing to mirror (`logoOuterTone` null) — a mark with no outline has no outermost outline.
   *   • the tone must READ on this plate, or the mirrored ring is invisible bulk: the thing he
   *     called *"very big and bulky… a big no no"*.
   *   • it must not already read as the same colour as what the number shows outermost —
   *     philadelphia-pd cfg1 is the control: thick yellow ring on the mark, a bare yellow number,
   *     and *"this combination works together because technically the outside of the weight number
   *     is still yellow, and so is the logo."* Adding a yellow ring to a yellow number is the Mount
   *     Mercy defect, not a fix. Judged with `colorsConflict`, the app's own "these read as one
   *     edge" test, so Kenyon's #FFFEFF-vs-#FFFFFF is a match and not a violation.
   *   • the stack ceiling: `resolveStyle` renders four, so a fifth is invisible weight. */
  const mirrorStack = (list, fillRefUsed) => {
    /* /!\ ILLUSTRATION ESCAPE HATCH - `app/tools/probe-stack-variants.mjs` ONLY (WD 453), and the
     * third of its kind beside `__wdNoContextualSwap` and `__wdNoBandRepaint`. One of the options
     * Ryan was shown is "the logo may keep its added ring; the weight number should not mirror it",
     * and that picture cannot be rendered while this function re-applies the ring the variant just
     * removed. Set through `addInitScript` before boot; nothing a buyer can reach sets it. */
    if (globalThis.__wdNoTextMirror) return list;
    const b = normalizeHex(bodyColor);
    const stack0 = (palette && Array.isArray(palette.logoStack)) ? palette.logoStack : [];
    /* WD 480 candidate (Ryan, Bauer: the number's outline thicker than the logo's): a logo with no stack still shows
       an edge - its band-less wrap (`logoEdgeWrap`) - and the number mirrors that rather than keeping its own ring. */
    const stack = stack0.length ? stack0 : ((palette && palette.logoEdgeWrap) ? [palette.logoEdgeWrap] : []);
    if (!b || !stack.length) return list;

    /* ONE RING PER EDGE THE LOGO SHOWS, in the logo's own order. `logoStack` runs OUTER -> inner
       and an element's `outlines` run inner -> OUTER, so it is reversed exactly once, here. The
       weight is that band's own measured proportion, snapped to the same 0.5 grid and held under
       the same ceiling as every other derived ring (`outlineThicknessFor`, whose arithmetic this
       is) - never a constant, which is the half of "very big and bulky... a big no no" a mirrored
       ring could otherwise re-create. */
    /* ⛔ THE PLATE MOAT IS NOT A RING AND IS NOT MIRRORED INK. `strip` is `mirrorStack(withGap(...))`
     * and `withGap` PREPENDS a ring in the plate's own colour so a gap mark's number does not fuse
     * with the rubber — it is the plate showing through, not an outline. Counting it as one is the
     * SIXTH occurrence in this file of a rule reading a colour the plate never shows (see
     * `resolveClash`'s `gapToneFor` note, which names the first five), and it cost five plates:
     * four stood the mirror down entirely and mountmercy cfg4 matched the logo's #000000 band TO
     * the moat and drew that band in the plate, where it cannot be seen. It is held out of both
     * tests below and put back, innermost, at the end.
     *
     * ⛔ AND IT IS `colorsIndistinguishable`, WHICH IS THE APP'S OWN ANSWER (`isPlateMoat`) — not
     * `colorsConflict`, and not the position `withGap` put it in. Both narrower tests were written
     * and both were wrong, each on a plate that says so:
     *   `colorsConflict(ring, plate)` calls abbeyathletics' genuine #000000 band a moat on the
     *     #0B0C0F plate — a named CONTROL, whose ring then came out twice. It is a "do these read
     *     as one edge" test and a black ring on a black plate does, without being a gap.
     *   position alone (count what `withGap` prepended) misses `hclarks-com` cfg3, where the moat
     *     is one of the number's own STORED rings: a red number, a #FEB500 plate-coloured ring,
     *     then the logo's red keyline outside it. `withGap` never fired there (`logoGap` false) and
     *     the gap is real all the same. A ring in the plate's own colour is the plate showing
     *     through, wherever it came from. */
    const isMoat = (o) => {
      const h = normalizeHex(resolvePaletteRef(o.colorRef, palette));
      return !!(h && colorsIndistinguishable(h, b));
    };
    const moat = list.filter(isMoat);
    const ink = list.filter((o) => !isMoat(o));
    const shown = ink.map((o) => normalizeHex(resolvePaletteRef(o.colorRef, palette)));
    const want = stack.slice(0, MIRRORED_RING_MAX).reverse().map((band) => {
      const hex = normalizeHex(band.hex);
      /* /!\ A RING THE NUMBER ALREADY WEARS KEEPS THE THICKNESS IT ALREADY HAS. Pricing every
         mirrored ring from its band's own measured width is rule 5c - "make sure the thickness of
         the blue outline matches that of the logo better" - and 5c is UNMEASURED. Measured as a
         side effect of this build it moved 8 plates, 5 of them ones Ryan had approved, which is a
         second rule shipping inside the first one's net. Only a ring being ADDED takes the band's
         weight, because it has no thickness of its own to keep. */
      const had = hex ? ink[shown.findIndex((h) => h && colorsConflict(h, hex))] : null;
      if (had) return { colorRef: had.colorRef, thickness: had.thickness, hex };
      const w = Number(band.weight);
      return {
        colorRef: hex,
        hex,
        thickness: w > 0 ? (w < 1 ? Math.max(0.2, Math.round(w * 10) / 10)   // a measured hairline (WD 476, `wrapEdgeWeight`)
          : Math.min(DEFAULT_OUTLINE_THICKNESS, Math.round(w * 2) / 2))
          : DEFAULT_OUTLINE_THICKNESS,
      };
    }).filter((o) => o.colorRef);
    if (!want.length) return list;

    /* ⭐ THE NUMBER'S STACK IS THE LOGO'S STACK. NOT "the logo's plus whatever it had" — EQUALS.
     * Ryan, 2026-09-22, asked for the rule in one sentence and gave it in one:
     *
     *   > *"the rule is, take the outlines from the logo (whether organic or added on later) and
     *   > add that to the text. period."*
     *   > *"the weight numbers should never have a ring the logo doesn't have, because the weight
     *   > numbers in their default standard state should simply mirror the look of the logo."*
     *
     * ⛔ SO THE ADD-ONLY BAIL IS GONE. It used to `return list` untouched the moment the number
     * wore anything the logo did not, on the reasoning that removing a ring needed its own round of
     * evidence. It does not: the removal is not a separate rule, it is the same sentence read
     * forwards. What that bail actually produced was a number wearing an outline the logo has not
     * got AND missing the one it has — failing the criterion in both directions at once.
     * ⚠ Two things it is NOT. The plate moat above is not a ring, so it is never what "the logo
     * has not got" means. And a locked ring is the buyer's own: `effectiveTextStyle`'s peel
     * respects `locked` and so must this, or a forced ring silently disappears on the next plate. */
    const forced = ink.filter((o) => o.locked === true
      && !want.some((w) => colorsConflict(w.hex, normalizeHex(resolvePaletteRef(o.colorRef, palette)))));

    /* AND THE INNERMOST RING MUST NOT BE THE FILL. `resolveClash` runs after this and would step
       the fill or drop a ring to fix it, but the cheaper and more faithful answer is not to mirror
       a band the number is already painted in - the logo's innermost edge very often IS its body
       colour, and the number has borrowed that for its fill through the fill mirror. It is his own
       rule twice over: *"never do two same colors on top of each other like this with text"*. */
    /* ⛔ AND ON A GAP MARK NOTHING TOUCHES THE FILL BUT THE MOAT, so this must not fire there.
     * This is the SEVENTH occurrence in this file of a rule reading a colour the plate never
     * shows, and the sixth was `resolveClash` making the identical mistake about the identical
     * plate: *"on a gap mark the ring against the fill is the moat and the fill never touches
     * `kept[0]` at all… Hanover cfg2 went ['#F1F3F3@1', '#990100@1'] -> []."* Written without this
     * line the equals rule re-created that exact picture on that exact plate — a red number with
     * its red keyline stripped, when the gap between them is what makes the pair legible and is
     * what the mark itself looks like. `hclarks-com` cfg3 is the second. */
    const fillHex = moat.length ? null : normalizeHex(resolvePaletteRef(fillRefUsed, palette));
    while (want.length && fillHex && colorsConflict(fillHex, want[0].hex)) want.shift();
    /* ⚠ AND WHEN THE WHOLE STACK COLLAPSES INTO THE FILL the number wears the moat and nothing
       else — which is what the logo looks like there too, a body with no edge the eye can find. */
    return [...moat, ...forced, ...want];
  };
  /* ⚠ `locked` IS CARRIED THROUGH TO THE MIRROR and stripped at the very end. The buyer's own
     forced ring is the one thing the peel above respects, and the mirror has to respect it too —
     dropped here, a ring someone deliberately switched on vanishes the next time the plate
     changes. */
  /* ⛔ `locked` IS STRIPPED HERE, ON EVERY PATH OUT. It is a working-copy flag the peel and the
     mirror both read, and it is not part of the render stack — `ui-logic.smoke` asserts that.
     Stripping it inside `mirrorStack`'s final return is not enough: that function has four early
     exits (`__wdNoTextMirror`, no plate, no logo stack, no bands to want) and each one hands the
     list straight back. One map at the boundary cannot be forgotten by a later branch. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 488, UX run 2, 2026-11-01) — TEXT RINGS THE BUYER HAS TAKEN OVER ARE NOT
     MIRRORED. Ryan chose "hand-edit = text's own": the first press on a text outline +, ✕ or ✎ copies what the plate
     shows into the element (`adoptTextRings`, ui/state.js) and sets `ringsOwn`; from then the stored stack IS the
     text's stack and the logo-derived additions (moat, mirror, bare-edge) stand down for that element. No default
     design ever sets `ringsOwn`, so every seeded plate takes the unchanged path below (corpus-baseline proves it). */
  const strip = (list, fillRefUsed) => (e.ringsOwn === true
    ? list.map((o) => ({ colorRef: o.colorRef, thickness: o.thickness }))
    : bareNumberEdge(mirrorStack(
      withGap(list.map((o) => ({ colorRef: o.colorRef, thickness: o.thickness, locked: o.locked === true })), fillRefUsed),
      fillRefUsed,
    ), fillRefUsed).map((o) => ({ colorRef: o.colorRef, thickness: o.thickness })));
  /* WD 480 - A NUMBER THE MIRROR LEFT BARE, THAT CANNOT BE READ, WEARS THE LOGO'S OWN EDGE (`logoEdgeWrap`). Only
     when nothing but a moat is left and the fill fails `readsOn` on the plate - so every legible bare number, and
     every number that wears a ring, is untouched. */
  function bareNumberEdge(list, fillRefUsed) {
    const b = normalizeHex(bodyColor);
    const edge = palette && palette.logoEdgeWrap;
    if (!b || !edge || !normalizeHex(edge.hex)) return list;
    const ink = list.filter((o) => { const h = normalizeHex(resolvePaletteRef(o.colorRef, palette)); return !(h && colorsIndistinguishable(h, b)); });
    if (ink.length) return list;
    const fill = normalizeHex(resolvePaletteRef(fillRefUsed, palette));
    if (!fill || readsOn(b, fill) || colorsConflict(fill, edge.hex) || !readsOn(b, edge.hex)) return list;
    const w = Number(edge.weight);
    const thickness = w > 0 ? (w < 1 ? Math.max(0.2, Math.round(w * 10) / 10) : Math.min(DEFAULT_OUTLINE_THICKNESS, Math.round(w * 2) / 2))
      : DEFAULT_OUTLINE_THICKNESS;
    return [...list, { colorRef: normalizeHex(edge.hex), thickness }];
  }
  const base = normalizeHex(bodyColor);
  /* ⛔ THE CLASH RESOLVER RUNS ON THIS PATH TOO, and leaving it off the end of the function was a
   * real bug Ryan caught from the pictures: gopatriotathletics cfg4 came back with BOTH remedies
   * having done nothing while cfg3 — same mark, red plate — applied one. This branch returns before
   * the resolver ever ran, so no near-black plate was ever resolved. A `return` that predates your
   * new logic looks exactly like the logic not working. */
  if (!base || isNearBlackHex(base)) { resolveClash(); return { fillRef, outlines: strip(kept, fillRef) }; }
  // 1. Peel outermost outlines that merge with the base — but a LOCKED ring stops the peel
  //    (the buyer forced it on) and is kept, exactly like a distinct ring.
  while (kept.length) {
    const last = kept[kept.length - 1];
    if (last.locked) break;
    if (colorsConflict(base, resolvePaletteRef(last.colorRef, palette))) kept.pop();
    else break;
  }
  // 2. Fill merges with the base (and isn't locked) → recolor the NUMBER to the most-distinct loaded
  //    colour. For a NO-natural-outline logo, go solid even if the synthetic ring survives + drop the
  //    now-redundant ring; for an OUTLINED logo, only recolor once every ring has peeled (keep its
  //    layered stack). `palette.logoOutlined` (set on the render palette) selects the branch.
  if (e.fillLocked !== true && !mainOnOwnPlate && colorsConflict(base, resolvePaletteRef(fillRef, palette))) {
    const soloNumber = !(palette && palette.logoOutlined); // no natural logo outlines → solid number
    if (soloNumber || !kept.length) {
      const newRef = bestContrastRef(palette, base);
      const newHex = normalizeHex(resolvePaletteRef(newRef, palette));
      return { fillRef: newRef, outlines: strip(kept.filter((o) => normalizeHex(resolvePaletteRef(o.colorRef, palette)) !== newHex), newRef) };
    }
  }
  resolveClash();
  return { fillRef, outlines: strip(kept, fillRef) };
}

/**
 * Does this mark wear a fill / GAP / keyline sandwich — a band standing OFF the fill, with the
 * plate showing through the moat between them? The measured tell is the INNERMOST band's
 * `bandTouchesFill` being false: a keyline that abuts its fill has no gap; one that doesn't is
 * separated by something the key removed (Hanover's navy field) or the artwork left transparent.
 * ONE definition, consumed by both render-palette builders (render-engine + design-panel), so the
 * texture and the panel's swatch mirror cannot disagree about whether the text wears a gap ring.
 */
export function logoGapFromStructure(structure) {
  const s = structure || {};
  if (s.confidence !== 'high' || !Array.isArray(s.bands) || !s.bands.length) return false;
  const touches = Array.isArray(s.bandTouchesFill) ? s.bandTouchesFill : [];
  // bands run OUTER → inner; the innermost is the one whose contact with the fill decides a gap.
  return touches[s.bands.length - 1] === false;
}

// ── Design elements ────────────────────────────────────────────────────────────

/** Valid ring anchor slots (mid of each side of the printable ring). */
export const SLOTS = Object.freeze(['top', 'bottom', 'left', 'right']);

/** Weight display formats — number-only, inline, or a stacked unit "lockup"
 *  (Fable §2.7 lockup choices: -LB- / LB / LBS / POUNDS / lb). */
export const WEIGHT_FORMATS = Object.freeze([
  'plain', 'inline-lb', 'ruled', 'stacked-dash', 'stacked-lb', 'stacked-lbs', 'stacked-pounds', 'stacked-lb-lower',
]);

/**
 * Free-text cap for an arched-text element — **ONE number for every product** (Ryan 2026-07-30:
 * *"we need to have the max characters match across all products… so someone doesn't type in
 * 'Arcadia High School Anchovies' into the text field on one product, then have it only return
 * 'cadia High Scho' on another"*).
 *
 * It was 24 here and 22 on the grip plate (`TEXT_MAX_LENGTH_GRIP`, deleted), which meant a line the
 * bumper's field accepted was a line the grip's field would not let you extend — the same words,
 * two different products, two different answers.
 *
 * ⚠ THIS IS A TASTE LIMIT AND IT WAS NEVER THE SAFETY MECHANISM, which is the part that made the
 * old split look reasonable. What actually keeps a name on the plate is the arc FIT, and until
 * 2026-07-30 that fit stopped at a size floor and let `<textPath>` drop whatever ran past the end of
 * its path — off BOTH ends, because the text is centred. So the cap could never have been sized to
 * make text fit anyway: at the floor, 24 of the 136 faces truncate before reaching even 24
 * characters, and the widest (BBH Bartle) starts dropping glyphs at 15. See `fitArcFont`, where the
 * floor is now an aspiration rather than a wall.
 *
 * With that fixed, one shared number is both possible and correct: nothing a buyer can type into any
 * field on any product can fail to paint in full on any other.
 */
export const TEXT_MAX_LENGTH = 24;

/**
 * The cap that PERSISTENCE applies — deliberately far above the field's.
 *
 * The field cap governs what a human may type. This one governs what may be STORED, and the two are
 * not the same number because **the app writes arc text as well as the buyer does**: crossing to
 * another product joins every distinct typed line into one string and puts it in every slot
 * (`applyLayoutPreset`, 2026-07-30), so a bumper carrying "ARCADIA HIGH SCHOOL" + "ANCHOVIES"
 * legitimately produces a 29-character line on the grip plate.
 *
 * ⛔ `serializeElement` used to cut that back to `TEXT_MAX_LENGTH` on the way to the wire, which
 * destroyed the buyer's own words mid-word ("…ANCHO") on every autosave — Ryan's exact complaint,
 * arrived at from the storage end instead of the render end. A cap here can only ever be a sanity
 * bound against a hostile payload; it must never be a size the app itself can reach. Three gaps of
 * the full field cap joined is 74, so this clears the worst case the combine rule can build by a
 * comfortable margin.
 */
export const TEXT_STORE_MAX = 120;
/** Cap for the WHOLE org / mascot name (it gets SPLIT across arcs, so it's longer than
 *  one arc line) — matches the identity + who-card inputs' maxlength. */
export const NAME_MAX_LENGTH = 40;

let _idCounter = 0;
/** Deterministic id (monotonic within a module load — testable, no Math.random). */
export function nextElementId(type) {
  _idCounter += 1;
  return `${type}-${_idCounter}`;
}

/**
 * @typedef {Object} Outline
 * @property {string} colorRef  - 'primary'|'secondary'|'tertiary'|'#RRGGBB'.
 * @property {number} thickness - outline width in ring/viewBox units (added outward).
 */

/**
 * @typedef {Object} DesignElement
 * @property {string} id
 * @property {'logo'|'weight'|'arctext'} type
 * @property {'top'|'bottom'|'left'|'right'} slot
 * @property {LogoProfile} [logo]
 * @property {'plain'|'inline-lb'|'stacked-dash'|'stacked-pounds'} [weightFormat]
 * @property {string} [text]
 * @property {number} [letterSpacing]
 * @property {string} [font]        - fontId.
 * @property {number} [sizeScale]   - 0.4..1 fraction of the slot's auto-fit max.
 * @property {string} [fillRef]     - palette ref for the glyph fill.
 * @property {Outline[]} [outlines] - 0..4, ordered THIN→THICK (outermost last).
 */

/**
 * The size a text/weight piece is BORN at — 90% of the slot's auto-fit max (Fable QW-2 §2.5:
 * defaults nudged down one notch because today's text read a fraction heavy).
 *   Exported 2026-07-25 because the render engine needs it by name. A logo beside a weight is
 * size-matched to that weight, and the match has to read the weight at its FACTORY size rather
 * than at whatever the buyer has since dialled — otherwise the number's control silently resizes
 * the logo too. Hardcoding "1" there was the same bug in the other direction: it matched against a
 * weight 11% bigger than any weight actually ships at, and every fresh logo came out oversized
 * (Ryan 2026-07-25: "I upload the Wallabies logo and it's at 100%, when 85% looks much better").
 *   One constant, read by both, so the default relationship can't drift again.
 */
export const DEFAULT_ELEMENT_SIZE_SCALE = 0.9;

/**
 * Where the LOGO and the WEIGHT NUMBER open, as a percentage of the room they have (Ryan
 * 2026-07-25 — 80%, then 75% on seeing it). 100% is the piece a hair from the edge of its slot, so
 * this is "use three quarters of the space and leave a clear margin"; the last quarter is there to
 * be claimed deliberately with the +/- rather than spent by default.
 *   ARC TEXT is deliberately not on this: it opens as large as it fits (100%, less when the words
 * are long), because a name that doesn't fill its arc reads as a mistake rather than as restraint.
 *   One constant for both, so the two pieces that sit opposite each other on the plate can't drift
 * to different defaults.
 */
export const DEFAULT_SIZE_PCT = 75;

/**
 * THE GRIP PLATE OPENS AT 65%, NOT 75% — and paints exactly the same size doing it (Ryan 2026-07-30:
 * *"love the logo and weight number sizes, but I think at THAT size you have them at, it should
 * correspond to say 65% not 75%, meaning if someone boosts it up to 100%, it has a little extra room
 * to get bigger"*).
 *
 * ⚠ THIS IS A RE-PEG, NOT A RESIZE, and it only works as a PAIR: the mark's room grows by `1 /` this
 * factor in `gripZone`, and its opening share shrinks by this factor here. The two cancel, so nothing
 * on the plate moves — what changes is what the dial CALLS the size it is already at, and therefore
 * how much of the dial is left above it. Change one without the other and every grip plate resizes.
 *
 * Why it was worth doing rather than just raising the cap: `sizeScale` is a share of the room (see
 * `wd-sizescale-is-a-share-not-a-number`), so "give it more headroom" can only mean "make the room it
 * is a share OF bigger". The mark at 100% now reaches 1.54× its default where it reached 1.25×, and
 * the geometry has the space — at full dial it still clears both the hub and the lettering above it.
 *
 * ⚠ 65/80, NOT 65/75, AND THE 80 IS MEASURED. `DEFAULT_SIZE_PCT` is 75, but that is the share of the
 * auto-fit the seeder AIMS at; what actually reaches the dial is that target divided by the room and
 * then snapped to the 5% grid, which for a grip logo lands on **80%**. Deriving the factor from the
 * constant instead of from the dial put the seeded value at 0.6725, which snapped DOWN to 65% and
 * quietly painted the logo 6% smaller — the one thing this change must not do, since Ryan's sentence
 * opens with "love the logo and weight number sizes".
 *   Off the measured 80 it is exact: 0.80 / 1.2308 = 0.65, dead on the grid, so the snap has nothing
 * to round and the painted size is bit-for-bit what he approved.
 */
export const GRIP_SIZE_OPEN_FACTOR = 65 / 80;

/**
 * ⚠ MIRRORED from `geometry/grip-plate.js`'s `SMALL_GRIP_MAX_LB`, for the same reason
 * `render-engine.js` mirrors the SMALL_GRIP_* block rather than importing it: this module is on the
 * first-paint path and `geometry/grip-plate.js` drags a 61KB mesh generator. `render-engine.smoke.mjs`
 * asserts the two agree, which is what makes the duplication safe rather than a drift waiting to happen.
 */
const SMALL_GRIP_MAX_LB_MIRROR = 5;

/**
 * The factor a product's opening share is scaled by. 1 everywhere but the CUTOUT grip plates.
 *
 * ⛔ THE 2.5 AND 5 LB ARE EXCLUDED, AND THE EXCLUSION IS THE WHOLE POINT (Ryan 2026-08-02:
 * *"the logos/weight numbers are a little too small [on the 5 and 2.5]"*).
 *
 * The re-peg above only works as a PAIR — the room grows by `1 /` this factor in `gripZone` and the
 * opening share shrinks by it here, so the painted size does not move and only the headroom above it
 * changes. `gripZone`'s cutout-free branch returns BEFORE that divide (there is no lettering lane on
 * these two to donate the headroom from — the mark's box already IS the whole band). So they were
 * getting the shrunken share against an ungrown room: **the exact "change one without the other and
 * every grip plate resizes" failure this constant's own header warns about, live on two denominations
 * since 2026-07-31.** They painted at 0.8125 of what every other grip plate's rule gives.
 *
 * ⚠ WEIGHT-AWARE, NOT MODEL-AWARE, and that is why the signature grew a second argument. The model
 * is `'grip'` for all six denominations — `isSmallGrip` is the only thing that tells them apart. A
 * caller that cannot say which denomination gets the cutout plate's factor, which is the safe error:
 * it is the value every grip has used since the re-peg landed.
 */
export function openShareFactorFor(model, weight) {
  if (model !== 'grip') return 1;
  if (Number.isFinite(Number(weight)) && Number(weight) <= SMALL_GRIP_MAX_LB_MIRROR) return 1;
  return GRIP_SIZE_OPEN_FACTOR;
}

/**
 * ── BADGE LOGO NUDGE (Ryan 2026-07-29) ──────────────────────────────────────────────────────────
 * "add a toggle of some kind which appears when you choose one of these layout types that allows
 * you to nudge the logo up/down/left/right."
 *
 * A Badge logo has NO anchor point — `bigLogoImageBox` places it by a rule (far edge flush with the
 * rim, or inner edge pinned at the hub crossing) and the renderer clips it to ring B. The size dial
 * shrinks it while HOLDING that anchor, which is what keeps the flush edge these layouts are named
 * for; this offset is the escape hatch for putting it somewhere else afterwards.
 *
 * ⚠ STORED AS A FRACTION OF THE PLATE RADIUS, NOT PIXELS. Every quantity in this app that was
 * pegged to a box has had to be re-pegged when the box moved — `sizeScale` is a share and not a
 * number, and that family has produced five bugs. `PLATE_R` is a constant of the viewBox, so a
 * nudge measured against it means the same thing after a size change, an aspect change, a layout
 * switch or a product switch. There is nothing to re-derive, which is the point.
 *
 * ⚠ AND IT IS NOT GEOMETRICALLY CLAMPED, deliberately. The obvious bound — "keep the box on the
 * paintable surface" — is meaningless here: a big logo's box is oversized ON PURPOSE and then
 * clipped, so for a wide mark at a side slot its centre already sits at u ≈ −2.2, well off the
 * plate. Any clamp derived from that box would either be inert or would refuse a nudge the buyer
 * can plainly see working. So the bound is a flat cap on the offset itself: ±0.35 R is 7 steps
 * either way, enough to re-place a mark and not enough to lose it, and "Reset logo" undoes it.
 *
 * These live HERE rather than in render-engine because the serializer below needs them too and
 * render-engine already imports from this file — the other direction would be a cycle.
 */
export const BIG_NUDGE_STEP = 0.05;
/* ⛔ 0.35 → 1 (Ryan 2026-08-09: *"there shouldn't be limits on BIG BADGE (x4) and FULL FACE layouts
   how far the logo can be moved, so long as a part of it is always on the canvas… you can move it
   from far left to far right"*).
     ⚠ 1 AND THEN 1.5 WERE BOTH TOO SMALL, and guessing twice is what earned a measurement. Driving
   the renderer on Ryan's own case — a 135% Big Badge on a competition bumper — the mark's left edge
   starts at −424 and the rim it has to reach is +195: a travel of 620 units, or **2.75 plate radii**,
   because a blown-up badge is itself wider than the plate. 3 clears it with room, and the number
   matters less than which thing stops the buyer: past this the GEOMETRY binds (`keepMarkOnSurface`),
   which is the whole design — the dial should never be the wall.
     Same split the number already runs on: this is the DIAL's range, and the safety is a separate,
   geometric one applied at paint time — `keepMarkOnSurface` in render-engine keeps a piece of the
   mark on the printable area no matter how far the dial is pushed. A range that doubled as the guard
   is what kept this at 0.35, and it is why it could not simply be widened before. */
export const BIG_NUDGE_MAX = 3;
/* The NUMBER reaches the whole face, not a third of it (Ryan 2026-08-09: *"weight number should be
   able to move anywhere within the canvas area — shouldn't be restricted to certain halves or
   whatever"*). A full plate radius in every direction, which covers any point on the face from any
   starting slot.
     ⚠ THIS IS A DIAL RANGE, NOT A SAFETY BOUND, and the distinction is the whole design. 0.35 was
   doing both jobs for the logo — small enough that a badge could not be pushed somewhere silly — and
   that is exactly why it could not be widened. The number is bounded at PAINT time instead, against
   the zone's real paintable band (`weightPaintAnchor`), so the dial can be generous and the piece
   still cannot leave the surface. See Ryan's third point in the same message. */
export const WEIGHT_NUDGE_MAX = 1;
/* Half the logo's step (Ryan 2026-08-09: *"have its movement increments be about half of what it
   is"*). The number travels a whole radius where a badge travels a third, so the same step that
   reads as a fine adjustment on a badge reads as a jump on the number — the range grew and the
   grain has to grow with it, in the opposite direction. */
export const WEIGHT_NUDGE_STEP = 0.025;
/** The grain an element's nudge moves on. Paired with `nudgeMaxFor`; see `clampBigNudge`, which
 *  SNAPS to this grid — pass the two together or a 0.025 value is rounded away by a 0.05 snap. */
export function nudgeStepFor(e) { return (e && e.type === 'weight') ? WEIGHT_NUDGE_STEP : BIG_NUDGE_STEP; }
/** Which range an element's nudge answers to. One place, so the dial, the clamp and the serializer
 *  cannot disagree about how far a piece may be pushed. */
export function nudgeMaxFor(e) { return (e && e.type === 'weight') ? WEIGHT_NUDGE_MAX : BIG_NUDGE_MAX; }

/**
 * A BADGE logo — the `big` family (Big Badge / Flip / Rise / Crown), which is placed by a rule
 * against the ring and clipped to it.
 *
 * ⚠ EXCLUDES the dumbbell "Full Face" disc, which is also `big`. That one inscribes the whole plate
 * face, so it has no anchor to hold, no room to shrink into that wouldn't just expose plate, and
 * nothing for a nudge to mean. One predicate so the size path, the nudge pad's visibility and the
 * reset can't disagree about which layouts this applies to.
 */
export function isBadgeLogo(e) {
  return !!(e && e.type === 'logo' && e.big === true && e.bigFace !== 'disc');
}

/**
 * Does this design's layout let the WEIGHT NUMBER be nudged? (Ryan 2026-08-09.)
 *
 * True on any BIG-logo layout — Big Badge and its three flips, AND the dumbbell's Full Face, which
 * `isBadgeLogo` deliberately excludes. That difference is the point rather than an oversight: what
 * makes full-face wrong for the LOGO is exactly what makes it right for the NUMBER. The disc has no
 * anchor to hold and nowhere to move to, so nudging the mark only slides artwork off one edge; the
 * number is painted OVER that mark at a fixed slot, on a full-bleed image with no clear ground, and
 * finding a readable patch of it is the one thing a buyer cannot otherwise do (see `db-full-face`,
 * whose own note calls the overlap "real rather than resolved").
 *
 * ⚠ Answers false for the GRIP PLATE at every layout, and that is correct, not a gap — it ships no
 * big-logo layout at all (8 of 8, verified 2026-08-09).
 */
/**
 * Which logos the nudge pad may move. (Ryan 2026-08-09: *"need to be able to… move it around too in
 * case it's off center or whatever and you need it aligned differently."*)
 *
 * ⚠ WIDER THAN `isBadgeLogo`, ON PURPOSE, and the reason is a change in the world rather than a
 * change of mind. That predicate excludes the full-face disc because a mark inscribing the whole
 * plate "has no room to shrink into that wouldn't just expose plate, and nothing for a nudge to
 * mean" — true for exactly as long as the disc was a fixed size. `fullFaceLogoBox` now honours the
 * size dial, so a dialled-back disc has room around it and centring it by eye is the whole ask.
 *   `isBadgeLogo` keeps its narrower meaning for the RING geometry that genuinely is badge-only.
 */
export function isNudgeableLogo(e) {
  return !!(e && e.type === 'logo' && e.big === true);
}

export function canNudgeWeight(profile) {
  const els = (profile && Array.isArray(profile.elements)) ? profile.elements : [];
  return els.some((e) => e && e.type === 'logo' && e.big === true)
    && els.some((e) => e && e.type === 'weight');
}

/**
 * `{ [productKey]: '#RRGGBB' }`, normalised and pruned. Null when no configuration has been dyed.
 *
 * ⚠ NORMALISED THROUGH `normalizeHex`, NOT COPIED. This map is compared against `bodyColor` to
 * decide whether a configuration's colour needs restoring, and `bodyColor` is itself normalised on
 * the way in — so an un-normalised `#fff` here would never equal the `#FFFFFF` it means, and the
 * restore would fire on every hop, re-recording history for a colour that had not changed.
 */
/** The manual-dye latch, as a plain `{key: true}` map — see `bodyColorUserSet` on the profile. */
function serializeUserSetMap(m) {
  if (!m || typeof m !== 'object') return null;
  const out = {};
  for (const [k, v] of Object.entries(m)) if (k && v) out[String(k)] = true;
  return Object.keys(out).length ? out : null;
}

function serializeSavedBodyColor(m) {
  if (!m || typeof m !== 'object') return null;
  const out = {};
  for (const [k, v] of Object.entries(m)) {
    const hex = normalizeHex(v);
    if (k && hex) out[String(k)] = hex;
  }
  return Object.keys(out).length ? out : null;
}

/** One authority for the nudge bound — snapped to the step grid and capped. Non-finite → 0. */
/** `{ [productKey]: { u, v } }`, clamped and pruned. Null when nothing has been positioned. */
function serializeSavedWeightNudge(m) {
  if (!m || typeof m !== 'object') return null;
  const out = {};
  for (const [k, rec] of Object.entries(m)) {
    if (!k || !rec || typeof rec !== 'object') continue;
    const u = clampBigNudge(rec.u, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP);
    const v = clampBigNudge(rec.v, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP);
    // A record of ZERO is kept deliberately: "I put it back to centre on this product" is an answer,
    // and dropping it would let another product's position be restored over it later.
    out[String(k)] = { u, v };
  }
  return Object.keys(out).length ? out : null;
}

export function clampBigNudge(n, max = BIG_NUDGE_MAX, step = BIG_NUDGE_STEP) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  const m = Number.isFinite(max) && max > 0 ? max : BIG_NUDGE_MAX;
  /* ⚠ THE SNAP GRID IS A PARAMETER, and it has to be. This rounds to the grid before clamping, so a
     caller that widened the range but left the step behind would have every finer value quietly
     rounded back onto the coarse grid — the control would move in the old increments and look like
     the step change never shipped. */
  const g = Number.isFinite(step) && step > 0 ? step : BIG_NUDGE_STEP;
  const snapped = Math.round(v / g) * g;
  return Math.round(Math.min(m, Math.max(-m, snapped)) * 1000) / 1000;
}

/**
 * The share of the room a weight number OPENS at, by number style (Ryan 2026-07-28).
 *
 * "Make it so their default size is set to 90% for the one with the lines, and 95% for the one
 * without. These two are naturally smaller than the rest so could use the boost."
 *
 * They are smaller for a structural reason rather than a stylistic one: `plain` and the stacked
 * lockups are capped by the slot box or the ring band — the raw wall — whereas "45LB" and the ruled
 * banner are fitted to `inlineMaxW`, which is 0.88 of the box. Their 100% already carries a side
 * margin the others buy with the default 75%, so the same percentage buys them a second one and
 * lands them visibly short.
 */
export const WEIGHT_SIZE_PCT = Object.freeze({ ruled: 90, 'inline-lb': 95 });

/** The opening share for `weightFormat`, falling back to the house default. */
export function weightSizePctFor(weightFormat) {
  return WEIGHT_SIZE_PCT[weightFormat] ?? DEFAULT_SIZE_PCT;
}

/**
 * ── THE GRIP'S NUMBER OPENS AT 81% OF ITS OWN WALL (Ryan 2026-08-06) ──────────────────────────
 *
 * *"the 75% size is too small… I want the 75% reading to align with the size of the number at 100%
 * now."* Measured, today's dial-100% sits at 81% of the point where the number physically stops
 * growing (tools/probe-grip-num-headroom.mjs), so that sentence is exactly "open at 81% of the
 * wall, and keep calling it 75%".
 *
 * ⛔ A SEPARATE CONSTANT FROM `GRIP_SIZE_OPEN_FACTOR`, AND THAT IS THE WHOLE POINT. That one is
 * PAIRED WITH GEOMETRY — `gripZone` divides `pointBox` by it, so the room grows by 1/f as the
 * opening share shrinks by f and the painted MARK does not move. Its own header warns: *"change one
 * without the other and every grip plate resizes."* Re-pointing it at the number would have moved
 * the logo on all six denominations. This factor touches no geometry at all: the wall stays exactly
 * where it is and only the share of it the number opens at changes.
 *
 * ⚠ ALL SIX DENOMINATIONS, unlike `openShareFactorFor`. The rule is stated against each plate's OWN
 * wall, so the cutout-free 2.5 / 5 lb — whose wall their old dial-100% already sat on — get the
 * same 81% and the same headroom above it as the rest of the ladder. They gain less (+5% and +8%
 * against +33%) because that is all the room they physically have; see the probe's table.
 */
export const GRIP_NUM_OPEN_SHARE = 0.81;
/** …expressed as what the dial has to call 100% for the default reading to stay 75. */
export const GRIP_NUM_OPEN_FACTOR = GRIP_NUM_OPEN_SHARE / (DEFAULT_SIZE_PCT / 100); // 1.08

/**
 * What the weight NUMBER's dial calls 100%, as a share of the geometric wall.
 *
 * ⚠ ABOVE 1 ON THE GRIP, which is the reverse of every other `pctOfMax` dial in the app and is why
 * `ui/stepper.js` no longer clamps its edge fraction to 1. A factor under 1 makes 100% a taste
 * ceiling BELOW the wall and the grid runs PAST 100 to keep the wall reachable; a factor over 1
 * puts 100% above the wall, so the grid stops at 100 and the + button greys at ~92% instead. Ryan
 * chose that trade with the numbers in front of him.
 *
 * ⚠ NO `weight` ARGUMENT, deliberately — see GRIP_NUM_OPEN_SHARE. The share is of each plate's own
 * wall, and the wall is already per-denomination, so the factor does not need to be.
 */
export function numberEdgeFractionFor(model) {
  return model === 'grip' ? GRIP_NUM_OPEN_FACTOR : 1;
}

/**
 * The dial percentage the number OPENS at — the ONE answer the engine's seeder and the dial's
 * `defaultPct` both read.
 *
 * ⛔ IT EXISTS BECAUSE THEY DISAGREED (measured 2026-08-06,
 * tools/probe-weight-style-seed-agreement.mjs). `seedDefaultSizes` has always opened the ruled
 * banner at 90 and the "45LB" one-liner at 95 — they are fitted to 0.88 of the box, so the margin
 * the stacked lockups buy with 75% is already inside their 100%. The panel handed its stepper a
 * FLAT `DEFAULT_SIZE_PCT`, so every panel rebuild re-seeded 75 over the engine's 90: those two
 * styles popped −17% and −21% on EVERY product, the same defect as the grip's hop one axis over.
 *
 * The cap is what makes the grip's factor safe for those styles: 90 × 1.08 would seed the ruled
 * banner past its own wall, where the dial can only walk back down again. Floored to the dial's own
 * 5% grid, because a seed off-grid is a value the buyer can see and never return to.
 */
export function weightOpenPctFor(weightFormat, model) {
  const ef = numberEdgeFractionFor(model);
  const ceilPct = Math.floor((100 / ef) / 5) * 5;
  return Math.min(weightSizePctFor(weightFormat), ceilPct);
}

/** Builds a defaulted element of `type` at `slot`. */
export function createElement(type, slot, overrides = {}) {
  const base = {
    id: nextElementId(type),
    type,
    slot,
    // 2.5 = one 0.5-step thinner default outline (same Fable pass).
    sizeScale: DEFAULT_ELEMENT_SIZE_SCALE,
    fillRef: 'primary',
    outlines: [{ colorRef: 'secondary', thickness: 2.5 }],
  };
  if (type === 'logo') {
    base.logo = createDefaultLogo();
    delete base.sizeScale;
    delete base.fillRef;
    delete base.outlines;
  } else if (type === 'weight') {
    base.weightFormat = 'stacked-dash';
  } else if (type === 'arctext') {
    base.text = '';
    base.letterSpacing = 3;
  }
  return { ...base, ...overrides };
}

// ── Design profile ─────────────────────────────────────────────────────────────

/**
 * @typedef {Object} DesignProfile
 * @property {Palette} palette
 * @property {string} layoutPreset  - id that seeded `elements` (elements are the truth).
 * @property {DesignElement[]} elements
 * @property {number} weightValue   - the number the weight element renders.
 * @property {'lb'|'kg'} unit
 * @property {string|null} createdAt
 */

export const DEFAULT_LAYOUT_PRESET = 'logo-l-weight-r-arch-tb';

/** Builds a fresh, empty Design Profile (no elements until a preset is applied). */
// Max saved bench colors (the "your colors" library beyond the 3 active roles). One
// shared pool feeds both Design colors (4th, 5th…) and the Plate-color quick options;
// adding past the cap evicts the oldest (Ryan 2026-07-20).
//   8 → 9 on 2026-08-08, and NOT as a round-up: the logo's own tones live in this pool, so raising
// the extraction cap 6 → 7 (`shownLogoTones`) spends one of the slots the buyer had for colours of
// their own. At 8 a 7-tone logo would have left exactly one free before the "remove one to add
// another" wall. The two numbers are one decision — move them together.
export const SWATCH_CAP = 9;

export function createDefaultProfile() {
  return {
    palette: { primary: '#1E3A8A', secondary: '#FBBF24', tertiary: null },
    // Bench of extra colors parked "in the wings" for quick access — NOT used by the
    // design until swapped into a role (1st/2nd/3rd). Shared with the plate-color picker.
    swatches: [],
    bodyColor: DEFAULT_BODY_COLOR, // plate rubber color (B6 §4) — black by default
    font: DEFAULT_FONT_ID, // ONE font for the whole plate (words + numbers)
    allCaps: true, // plate-wide caps mode (§2.7): ALL CAPS default / "As typed"
    layoutPreset: DEFAULT_LAYOUT_PRESET,
    elements: [],
    // Structure-aware auto-style template (B1). When a confident layered logo is
    // detected (logo-structure.js), this holds the fill + ordered outline refs the
    // text/weight pieces should wear to MATCH the logo (teal fill / white / rust).
    // Transient: NOT serialized (elements carry the resolved styling); re-applied by
    // smartStart so a name-driven relayout keeps the look. null = plain default.
    autoStyle: null,
    // The logo's own OUTLINE bands (outer→in hexes) + confidence, for the logo-outline
    // peel (logo-outline.js). Set on a confident upload; drives the "Logo outlines" panel
    // + the auto-conflict peel on a dyed plate. Serialized (small). null = nothing to peel.
    logoStructure: null,
    // HOW MANY TONES the uploaded mark has — `shownLogoTones`' count, i.e. exactly the chips the Add
    // Logo tab shows and the bands the Customize Design swatch is divided into. Set by applyPalette
    // (Ryan 2026-07-28) and serialized, because it decides whether the TEXT keeps an auto outline on a
    // dyed plate: a 1–2 tone design wants solid letters, a 3+ tone mark wants the ring that echoes it.
    // 0 = unknown (no confident palette) → every rule that reads it falls back to its old behaviour.
    logoToneCount: 0,
    // The artwork's characteristic stroke width as a fraction of its long edge (logo-structure.js,
    // medial axis). Drives the text lockup's DEFAULT ring weight through `outlineThicknessFor`, so a
    // finely-ruled badge doesn't get a chunky outline beside it. 0 = unmeasured.
    logoStrokeFrac: 0,
    // The keyed-out CHROMATIC background the seeding judged a SCHOOL COLOUR (Ryan 2026-08-18,
    // Hanover's navy) — set by `seedKeyedFieldSwatch` (hex, or null when the field was a neutral /
    // photo matte / duplicate). It marks the matching bench swatch as brand rather than
    // buyer-added: the Add Logo chips show it, `orderedPool` withholds its ✕, and
    // `applyDefaultPlateColors` deals it into the multi-configuration mix. Serialized — it
    // governs colour decisions, so a reload must not silently demote the colour to "added".
    keyedFieldHex: null,
    // ── HAS THE BUYER HAND-STYLED THIS DESIGN YET? (Ryan 2026-08-08) ─────────────────────────────
    // A one-way LATCH, and the thing it gates is narrow: the "let weight show through enclosed
    // areas" flip re-analyses the artwork it just produced and re-derives the auto style from it
    // (see `applyLogoStructure`'s `sameArtwork` option). That is right while the styling is still
    // the app's own derivation — the text should not wear a tone the mark no longer has — and wrong
    // the moment it isn't: Ryan's rule is *"as soon as someone goes to the Customize Design tab,
    // adds/removes an outline, then they return to the Add Logo tab and check or uncheck that box
    // again, it won't affect any outlines after that point."*
    //   Set by every hand edit to a colour or an outline (`markStyleUserEdited`); cleared only by a
    // NEW logo and by "Reset colors", the two actions that make the derivation authoritative again.
    // ⚠ A LATCH, NOT A DIFF. Auto passes rewrite the outline stack constantly — the plate cascade,
    // `assignOutlineColors`, the contrast rules — so "does the stack still match `autoStyle`" cannot
    // answer this question. Only the buyer's own taps can, so they are what records it.
    styleUserEdited: false,
    // ── DID THE BUYER DELETE THE LOGO'S DERIVED RING? (Ryan 2026-09-21) ─────────────────────────
    // A second one-way latch, deliberately separate from the one above. A ring is DERIVED
    // (`auto: true`) or BUYER-ADDED, and neither state can say "the buyer removed the derived one"
    // — so the plate cascade re-derived it every time, and there was no way to keep it gone.
    //   Set ONLY by removing an `auto` ring (`dropRing`); read at the top of
    // `syncLogoContrastRing`; cleared by a NEW logo and by "Reset colors", the same two actions
    // that clear `styleUserEdited`. ⛔ It is NOT that flag: `styleUserEdited` is set by any hand
    // edit, and standing the rescue down because someone recoloured a band would let a mark vanish
    // into its plate with nothing to catch it.
    logoRingSuppressed: false,
    // Post-upload COLOR baseline (palette + swatches) for "Reset to original". Small.
    styleBaseline: null,
    // The uploaded logo, remembered independently of the current layout so it
    // survives switching through a text-only layout (see applyLayoutPreset).
    savedLogo: null,
    // Signed path to the buyer's ORIGINAL upload, once one has been retained — see the serializer.
    originalArtwork: null,
    // …and how that logo is PAINTED — the tones the buyer recoloured on the Add Logo tab. Remembered
    // beside the art for the same reason and re-seeded onto every logo slot (see applyLayoutPreset).
    savedLogoToneSwaps: null,
    // Arch wording the buyer TYPED, remembered per slot for the same reason as savedLogo — so it
    // survives a layout with no arc slots. Only `textUserSet` lines land here; derived text is
    // re-derived from orgName/mascot instead (see applyLayoutPreset).
    savedArcText: null,
    // Hand-set SIZES, remembered per layout family — `{ [family]: { 'type:slot': sizeScale } }`.
    // Fonts and the number style are deliberately shared across every model; size is not, because it
    // is a matter of taste against a particular face (Ryan 2026-07-30). Only pieces the buyer sized
    // by hand land here; anything untouched is re-seeded to the destination family's own default,
    // which is what makes the defaults still feel like one-size-fits-all. See `applyLayoutPreset`,
    // which is the only writer.
    //   ⚠ COLOURS USED TO BE ON THAT "SHARED" LIST TOO. They are now remembered per plate colour —
    // see `savedInk` below.
    savedSizes: null,
    // Hand-set INK, remembered per PLATE COLOUR — `{ [contextKey]: { 'type:slot': {...colours} } }`.
    //
    // Bumpers are the only weight that can be dyed; everything else is black. So ink chosen to pop on
    // a light-yellow plate (black text, black mark) is exactly the ink that disappears on a black
    // dumbbell, and the white chosen for the dumbbell is what disappears back on the yellow. One
    // shared set cannot satisfy both, and whichever the buyer touched last silently ruined the other
    // (Ryan 2026-08-01).
    //
    // ⚠ THE AXIS IS THE PLATE COLOUR, NOT THE PRODUCT. Keying by product looks equivalent and is not:
    // a competition bumper and a TRAINING bumper share a layout family but only the first can be
    // dyed, and two dyeable bumper kinds wearing the same dye want the same ink. `inkContextKey`
    // resolves both to the colour actually being painted against, so every non-dyeable product plus a
    // bumper set back to black share ONE bucket ('black') and each distinct dye gets its own. That is
    // what makes "put it back to black and it rejoins the others" fall out for free.
    //
    // Stores COLOUR ONLY, never structure — ring counts, thicknesses and which bands are off stay on
    // the elements, where the plate cascade's "STRUCTURE survives, COLOUR re-derives" split already
    // puts them. Restoring paints onto whatever rings currently exist, by index.
    savedInk: null,
    /* ⭐ Per-CONFIGURATION plate colour — `{ [productKey]: hex }` (Ryan 2026-08-09).
     *
     * A school buying white 45s and green 25s picks the SAME competition bumper twice, and the
     * ONLY thing that distinguishes the two picks is the colour of the rubber. `bodyColor` is a
     * single field, so without this dyeing one configuration dyes them both and the feature does
     * not exist — everything else about multiple configurations is plumbing around this one fact.
     *
     * ⚠ KEYED BY THE FULL PRODUCT KEY, INSTANCE AND ALL, which is the one memory on this profile
     * that genuinely wants the suffix. Its neighbours are deliberately keyed the other way round —
     * `savedInk` by PLATE COLOUR, `savedArcText`/`savedSizes` by FAMILY, `savedWeightNudge` by the
     * BASE product — because those are all properties of the one shared design. The plate colour
     * is the single thing an instance owns, so it is the single thing keyed by instance.
     *
     * ⚠ ONLY DYEABLE PRODUCTS ARE STAMPED. A dumbbell renders black whatever this field says
     * (`productSupportsBodyColor` gates the override), so recording a colour against one would be
     * storing an answer to a question it is never asked — and worse, would let a hop through a
     * dumbbell overwrite the colour of the bumper the buyer is coming back to.
     *
     * ⚠ ABSENCE MEANS INHERIT, NOT BLACK. A configuration added second opens wearing whatever the
     * design is already dyed, because it IS the same design; the buyer then re-dyes it, which is
     * the action they added it for. Defaulting to black would read as the new configuration having
     * lost the design's colour. */
    savedBodyColor: null,
    /* WHICH configurations the buyer dyed BY HAND. The defaults above are only allowed to write
       where this says nothing — see `applyDefaultPlateColors`. Kept beside `savedBodyColor` rather
       than inferred from it, because "black" is both the stock plate AND a colour somebody can
       deliberately choose, and those two must not be the same fact. */
    bodyColorUserSet: null,
    /* Per-PRODUCT weight-number position — `{ [productKey]: { u, v } }`. See `rememberWeightNudge`
       in ui/state.js for why the position is a property of the product rather than of the design. */
    savedWeightNudge: null,
    // The buyer's "who's this for?" answers, kept on the profile so the Add Logo tab's
    // persistent Organization / Mascot fields survive a logo swap + a resume, and stay
    // the friendly source of the plate's arc text (Ryan 2026-07-18 — tab 2 = identity home).
    orgName: '',
    mascot: '',
    // ⛔ ALWAYS POUNDS. See UNIT IS A LABEL below — `unit` does not change what this number means.
    weightValue: 45,
    unit: 'lb',
    createdAt: null,
  };
}

/**
 * WHO this design is for, as one comparable string — the identity, not the arrangement.
 *
 * ⛔ THIS IS NOT `designFingerprint`, AND THE DIFFERENCE IS THE WHOLE REASON IT EXISTS. That hashes
 * the artwork INCLUDING `elements`, and `elements` are re-slotted per family by
 * `applyLayoutPreset` — so a bumper and a dumbbell carrying the SAME logo fingerprint differently
 * by construction. It can answer *"has this plate changed?"* and it cannot answer *"is this still
 * the same customer?"*, which is the only question a captured per-family design needs to be asked.
 *
 * What it covers is exactly what the three families SHARE: the uploaded mark, the Organization /
 * Mascot answers the arc text is derived from, and the typeface. Deliberately NOT the plate colour,
 * the layout, the weight, or the element text — every one of those legitimately differs per family
 * (that is what `savedBodyColor` / `savedArcText` / `lastPresetByFace` are for), and folding any of
 * them in would report every captured design stale on every single publish.
 *
 * The mark is keyed by LENGTH + TAIL rather than by its whole ~86 kB data URL: this is compared a
 * handful of times per dialog, and an artwork swap that preserves both the exact byte count and the
 * final 48 base64 characters is not something that happens by accident.
 */
export function identityKey(profile) {
  const p = profile || {};
  let art = (p.savedLogo && p.savedLogo.dataUrl) || '';
  if (!art) {
    // A design sitting on a text-only layout has no logo ELEMENT; one that predates `savedLogo`
    // being carried has no saved copy. Asking both is what makes this stable across either.
    const el = (p.elements || []).find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
    art = el ? el.logo.dataUrl : '';
  }
  // JSON rather than a joined string, so no separator has to be a character the fields cannot
  // contain: "Ridge" + "View" and "Ridge View" + "" must never key alike.
  return JSON.stringify([
    art ? `${art.length}:${art.slice(-48)}` : 'none',
    String(p.orgName || ''),
    String(p.mascot || ''),
    String(p.font || ''),
    p.allCaps !== false,
  ]);
}

/* ============================================================================
 * UNIT IS A LABEL, NOT A WEIGHT (Ryan 2026-08-02)
 * ============================================================================
 * *"when I select the KG toggle, the plates often get smaller in size. This should never happen.
 * Instead, the weight that you're on needs to stay the same size and simply adjust the weight
 * number label accordingly."*
 *
 * ⛔ `weightValue` IS ALWAYS IN POUNDS. `unit` chooses how that number is PRINTED and nothing else.
 *
 * It used to mean "the number, expressed in `unit`", and that single fact caused every symptom:
 *   • The LB/KG toggle called `setWeight(defaultWeightFor(product, 'kg'))` — it did not convert the
 *     buyer's denomination, it ABANDONED it and jumped to the category's kg sample (45 → 20).
 *   • Every geometry module then had to re-interpret the number, and they disagreed.
 *     `gripPlateDims(weight)` takes NO unit at all ("lb only — grip plates are spec'd in lb"), so a
 *     20 kg grip plate rendered as a **20 lb** one: the visible shrink. `gripZone` has the same
 *     hole on the 2D face. The bumper survived only because `KG_TO_LB[20]` happens to be 45.
 *   • 204 replaced the dumbbell head chart's SNAP with interpolation, so 50 lb stopped landing
 *     exactly on the 22.5 kg knot it used to round to — which is what made the dumbbell start
 *     moving on the flip too, and why this surfaced as "something changed recently".
 *
 * With the number pinned to lb, geometry never sees a unit, so a flip CANNOT resize the model —
 * the bug is impossible by construction rather than fixed in four modules. It also retires, rather
 * than implements, the grip plate's missing kg support: there is no kg for it to support.
 *
 * ⚠ THE COROLLARY: anything PRINTING the number must convert. `displayWeightText` is the one place
 * that does it — the plate face, the Size tiles, the announcements, the quote step and the PDF all
 * go through it, so they cannot drift from each other.
 */

/** Kilograms per pound — the exact international avoirdupois definition. */
export const KG_PER_LB = 0.45359237;

/**
 * The KG label for a denomination in pounds, to one decimal, with a bare integer left bare.
 *
 * Ryan's own table is the spec, and it is one decimal throughout: 2.5→1.1, 5→2.3, 10→4.5,
 * 25→11.3, 35→15.9, 45→20.4. Extending it costs nothing — the half-steps land at 7.5→3.4 and
 * 12.5→5.7, the dumbbell rack runs to 150→68 and the house barbell set 20→9.1 … 110→49.9.
 *
 * ⚠ THE TRAILING `.0` IS DROPPED, so a 150 lb dumbbell reads "68" and not "68.0". A denomination
 * is a name on a plate, not a measurement, and no plate in the world is stamped 68.0.
 *
 * ⚠ AND THESE ARE TRUE CONVERSIONS, NOT COMPETITION SIZES. A 45 lb bumper prints 20.4, not the
 * round 20 kg a metric comp plate is sold as — Ryan's table says 20.4 and the plate on the stage
 * genuinely is a 45 lb plate. If comp bumpers should ever print the round number instead, that is a
 * per-category label override here, NOT a change to what `weightValue` means.
 */
export function kgLabel(lb) {
  const n = Number(lb);
  if (!Number.isFinite(n) || n <= 0) return '';
  // toFixed(1) then strip a trailing ".0" — `parseFloat` alone would render 68.0 as "68" but also
  // turn 20.40 into "20.4" by a different route, and doing it in two explicit steps is greppable.
  const s = (n * KG_PER_LB).toFixed(1);
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}

/**
 * The denomination as the buyer should SEE it, given the display unit. The single conversion point
 * — see the corollary above. Pounds pass through untouched (they are already the stored truth).
 */
export function displayWeightText(lbWeight, unit) {
  if (unit === 'kg') return kgLabel(lbWeight);
  const n = Number(lbWeight);
  return Number.isFinite(n) ? String(n) : '';
}

/**
 * Derives a coarse quality bucket from raster dimensions (logo upload helper).
 */
export function assessQuality(width, height) {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  const min = Math.min(width, height);
  if (min >= 1000) return 'high';
  if (min >= 500) return 'medium';
  return 'low';
}

/** Resolves a logo's display aspect ratio (width/height) for fit math. */
export function resolveLogoAspect(logo) {
  if (!logo) return DEFAULT_LOGO_ASPECT;
  if (Number.isFinite(logo.aspectRatio) && logo.aspectRatio > 0) return logo.aspectRatio;
  const { width, height } = logo;
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) return width / height;
  return DEFAULT_LOGO_ASPECT;
}

// ── structure-aware auto-style (B1) ──────────────────────────────────────────
// The fill + ordered outline stack a detected layered logo dictates (see
// logo-structure.js / applyLogoStructure). Lives on profile.autoStyle as
// { fillRef, outlines:[{colorRef, thickness}] }. These pure helpers stamp it onto
// text/weight pieces; they live HERE (not ui/state.js) so applyLayoutPreset
// (layouts.js) can re-stamp NEW pieces on every relayout without a circular import
// — otherwise a second weight or fresh arc-text added by a layout change reverts
// to the plain default outline (Ryan 2026-07-17: "must apply to ALL parts of
// numbers/text on ALL sides of ALL layouts for ALL weights").

/** Stamp the auto-style onto ONE text/weight element, if a template exists. Pure. */
/**
 * The outline stack a fresh text/weight piece opens with. ONE ring in the logo's second colour —
 * unless the logo HAS no second colour, in which case: none (Ryan 2026-07-25).
 *   A one-tone logo used to come out outlined in #FBBF24, the app's default secondary, because
 * extraction correctly returned `secondary: null` and nothing cleared the default sitting under it.
 * The buyer got a "brand" colour their mark never contained. A single-colour mark is a legitimate
 * design — leave it solid, and let them add a ring from the swatch row if they want one.
 * @param {{secondary?: string|null}} palette
 */
/** The text ring's weight when the logo told us nothing about its own linework. */
export const DEFAULT_OUTLINE_THICKNESS = 2.5;

/**
 * The `logoStrokeFrac` a mark needs before its text ring earns the FULL default weight
 * (Ryan 2026-07-28). Measured across his 14 real logos — the drop folder's other 58 files are
 * product photos and textures, not marks, and were excluded:
 *
 *     Cinderwood 0.45% · Drop Bear / Ridge View / Secondary PNG / Wexmoor / BRONCOS 1.36%
 *     BauerRevamp / Cherry Valley v2 1.67% · Cherry Valley 2.58% · Wallabies 2.88%
 *     Body State U 3.18% · RIGAR 5.00% · McDonald's 5.30% · E Logo 5.91%
 *
 * 2.6% sits just above the middle of that spread, which puts every chunky mark — the varsity E, the
 * McDonald's arches, RIGAR, the Wallabies — at the full 2.5 they already wear, and lightens the
 * finely-ruled half toward the floor.
 *
 * ⛔ THIS IS A CORPUS MEDIAN, NOT AN EXCHANGE RATE, AND IT SPENT ITS FIRST FORTNIGHT BEING BOTH
 * (Ryan 2026-08-15). Read as "what fraction is a typical mark's linework?" it is a measured fact and
 * still true. Read as "how many viewBox units is a fraction of the logo worth?" — which is what
 * `outlineThicknessFor` and the logo ring's dial both did with it — it claims the logo paints ~96
 * units across, and the logo paints HALF that (see `LOGO_BOX_NOMINAL_VB`). Every ring derived
 * through it therefore came out ~1.85x heavy, which is the defect Ryan reported against the
 * Springboro panther. Its ONE remaining reader is the added-ring fallback in `ui/design-panel.js`,
 * which wants a typical linework fraction and nothing else. Do not re-merge them.
 */
export const LOGO_STROKE_FRAC_REF = 0.026;

/**
 * How wide the logo actually PAINTS, in viewBox units — the exchange rate between "a fraction of the
 * logo" and "a text ring's units", which is the honest answer to a question that used to be
 * answered by a taste constant (Ryan 2026-08-15).
 *
 * MEASURED off the live SVG at the default layout (`tools/probe-logo-band-vb.mjs`), long edge of the
 * painted `<image>` on a 450-unit plate:
 *
 *     Springboro 55.1 · Cherry Valley 54.8 · Angry Panda 52.1 · E Logo 49.3
 *
 * ⚠ THE SPREAD DOES NOT MATTER, BECAUSE THE GRID SNAP IS A CEIL. Springboro's 2.27% linework lands
 * anywhere in 1.12–1.25 units across that entire range and every one of them ceils to the same 1.5 —
 * so this is a robust peg rather than a fitted one, and re-measuring it on another layout will not
 * move a seeded ring. That is the property to preserve if this number is ever revisited.
 */
export const LOGO_BOX_NOMINAL_VB = 52;
/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — THE LOGO TAKES AS MANY OUTLINES AS THE TEXT. Ryan: *"you can
   add more outlines to text in the WD than you can to logos -- should they match?"* - yes, a total of 4 each. The text
   counts its whole stack; the logo counts the outlines drawn into the artwork (bands still showing) plus the ones the
   buyer added, so a plain logo can take 4 added rings and a logo with 2 bands can take 2. This is the storage/render
   ceiling for added rings; the panel's Add button enforces the total. */
export const OUTLINES_MAX = 4;

/**
 * How thick should a text/weight outline be BY DEFAULT, given the logo it sits beside?
 *
 * Ryan 2026-07-28: "the logo utilises a very thin white outline followed by a medium orange inside of
 * that, whereas the text and numbers, when adopting outlines, immediately grab a pretty thick one."
 *
 * The old answer was the constant 2.5 with nothing behind it, and the MEASURED path could not have
 * helped: `analyzeStructure` normalises a logo's bands so their MEDIAN always lands on 2.5, so it
 * carries the ratios between one logo's own bands and discards the absolute weight of its linework.
 * `logoStrokeFrac` is the missing number — the artwork's characteristic stroke width as a fraction of
 * its long edge, read off the medial axis (see logo-structure.js).
 *
 * ⚠ IT ONLY EVER LIGHTENS. The multiplier is capped at 1, so no logo gets a HEAVIER ring than it does
 * today. That is deliberate and it is what makes this safe to ship on a measurement calibrated
 * against fourteen logos: the reported defect is finely-ruled marks getting too much, so a mark at or
 * above the reference is left exactly where it is. A design Ryan already likes cannot move.
 *
 * Floor of 1 because below that an outline stops reading as an outline at plate scale.
 * 0 / absent `logoStrokeFrac` means unmeasured — fall back to the constant, as before.
 *
 * ⛔ IT IS NOW GEOMETRY RATHER THAN A RATIO OF A TASTE CONSTANT (Ryan 2026-08-15). `logoStrokeFrac`
 * is a fraction of the artwork's long edge and the artwork paints `LOGO_BOX_NOMINAL_VB` units wide,
 * so the mark's own keyline paints `frac x box` units — the SAME units a text ring is stored in.
 * Multiplying those two is the whole derivation, and it is checkable against a screenshot: the
 * Springboro panther's white keyline measures 1.25 units and its text ring used to seed at 2.5.
 * Scaling `DEFAULT_OUTLINE_THICKNESS` by `frac / LOGO_STROKE_FRAC_REF` was the same arithmetic with
 * a corpus median standing in for the box, which is where the ~1.85x came from.
 *
 * ⚠ ROUND, NOT CEIL, ONTO THE 0.5 GRID — reversed 2026-08-17 (Ryan, looking at the UVA Wise plate on
 * Live: *"the text outline thickness on the weight number is set to 2, but it looks like it'll match
 * the outline around the logo more accurately if it's set to 1.5"*). His eye landed on this exact
 * operator: that mark's linework is 1.595u, which ceil pushes to 2.0 (+25%) and round puts at 1.5 (-6%).
 *
 * ⛔ THE ORIGINAL ARGUMENT FOR CEIL HAD ALREADY BEEN ANSWERED ELSEWHERE. It read: "a ring a half-step
 * heavier still reads as the same family; one rounded DOWN can fall under the floor the whole function
 * exists to hold." But the floor is enforced independently by the `Math.max(1, …)` on the next line, so
 * the thing ceil was guarding against cannot happen — which is what made this safe rather than a
 * trade. Rounding is also never further from the true value than ceiling, by definition.
 *
 * Measured over the 54 college marks in the CFA corpus before switching: the two differ on **5 of 54
 * (9%)** — not the ~half a first estimate assumed, because most marks land near a grid point or clamp
 * at the 1.0 floor / 2.5 ceiling. Mean error against the true linework: ceil 0.062u, round 0.049u, and
 * **ceil was heavier on 5 of 5** — its error is one-directional by construction, which is why it read
 * as a consistent heaviness rather than as noise.
 *
 * The 0.5 grid itself stays: it is what keeps this peg insensitive to which logo you measure the box
 * on (see `LOGO_BOX_NOMINAL_VB`) and what matches every other ring the stepper offers.
 */
export function outlineThicknessFor(profile) {
  const frac = profile && Number.isFinite(profile.logoStrokeFrac) ? profile.logoStrokeFrac : 0;
  const w = ringWeightFromFrac(frac);
  return w == null ? DEFAULT_OUTLINE_THICKNESS : w;
}

/**
 * A ring's weight from a MEASURED linework fraction — the arithmetic above, named, because two
 * questions now spend it and the second one is per BAND (WD 438).
 *
 * `outlineThicknessFor` asks it of the mark's MEDIAN stroke (`logoStrokeFrac`); `seedOutlinesFrom`
 * asks it of each band's own (`widthFrac`, measured by the same medial-axis pass in
 * logo-structure.js). Same currency — a fraction of the artwork's long edge — the same
 * `LOGO_BOX_NOMINAL_VB` exchange rate, the same 0.5 grid and the same 1..2.5 clamp, which is the
 * point of having one function rather than two: this file already names what happens when two
 * places answer the same question and one of them is wrong.
 *
 * @returns {number|null} null when the fraction is absent or unmeasured — NOT a default, because the
 *   two callers fall back differently (the constant, vs the ratio chain).
 */
/**
 * How many times the mark's own linework a detected "band" may measure before it stops being a
 * keyline and starts being a filled shape — see `seedOutlinesFrom`, which is its only reader and
 * carries the ten-mark measurement that sets it. Ryan's answers separate at 3.70x / 2.39x with
 * nothing in between; this is the middle of that gap, not a fitted edge of it.
 */
export const BAND_IS_A_FIELD_RATIO = 3;

/**
 * The floor a field-band's replacement ring will not go under (Ryan 2026-09-20, answering the two
 * plates where it bottomed out). The replacement is the mark's own measured linework, and on
 * `goldentigersports` and `120081000896` that measurement is finer than the app can draw, so it
 * clamped to `ringWeightFromFrac`'s 1.0 — a 60% cut where he had asked for *"maybe 30% thinner or
 * so"*. Shown 2.5 / 1.5 / 1.0 side by side on both he picked **1.5 on both**.
 *   ⚠ It is a floor, not a target: `mountmercymustangs` measures 1.5 on its own and is untouched by
 * this, and no mark whose linework is heavier than 1.5 is affected. Only the two that bottom out
 * move, which is why this is a separate constant from the ratio rather than a change to it.
 */
export const FIELD_BAND_RING_MIN = 1.5;

export function ringWeightFromFrac(frac) {
  if (!Number.isFinite(frac) || !(frac > 0)) return null;
  const t = Math.round(frac * LOGO_BOX_NOMINAL_VB * 2) / 2; // 0.5 steps, like every other ring
  return Math.max(1, Math.min(DEFAULT_OUTLINE_THICKNESS, t));
}

/**
 * The seeded ring for a fresh text/weight element.
 *
 * ⛔ THIS USED TO BE A FIXED SLOT — `secondary`, unconditionally (Ryan 2026-08-05, the Bauer's Elite
 * logo). That palette's `secondary` is #010101, so the very first render after upload put a
 * near-black ring on the undyed #0B0C0F plate: present in the model, invisible on the plate, and
 * `assignOutlineColors` could not correct it because its plate test was disabled on black. Two
 * independent faults, one symptom — a buyer reaching for "+ Add" to fix a ring that was already there.
 *
 * `secondary` STAYS FIRST, so every palette where it reads keeps exactly the ring it has today; this
 * only changes the answer where the old one was invisible. The candidate must clear BOTH the plate
 * behind it and the fill it wraps — an outline has two neighbours, and picking against one of them is
 * how you land a ring that is legible on the plate and vanishes against its own letter.
 *
 * @param {object} palette
 * @param {number} [thickness]
 * @param {{base?: string, fillHex?: string}} [opts] `base` = the plate the ink is painted on (see
 *   ui/state.js `effectivePlateHex` — NOT `bodyColor` on a product that cannot be dyed).
 */
export function defaultOutlinesFor(palette, thickness = DEFAULT_OUTLINE_THICKNESS, opts = {}) {
  const pal = palette || {};
  if (!normalizeHex(pal.secondary)) return [];
  const base = normalizeHex(opts.base);
  const fill = normalizeHex(opts.fillHex);
  const clears = (ref) => {
    const h = normalizeHex(pal[ref]);
    if (!h) return false;
    if (base && colorsConflict(base, h)) return false;
    return !(fill && colorsConflict(fill, h));
  };
  /* Order is the rule: keep today's answer wherever it still reads, then fall through the palette.
     ⚠ AND WHEN NOTHING CLEARS BOTH, CLEARING THE PLATE WINS. A two-colour palette on its own plate
     colour has no candidate that clears the base AND the fill, and the first cut fell straight back
     to `secondary` — i.e. re-seeded the invisible ring it exists to prevent. The two failures are not
     equal: a ring matching the FILL merely reads as a slightly bolder letter, while a ring matching
     the PLATE is gone. They look identical on the day, but the choice is stamped into this plate
     colour's ink bucket, so it is the answer restored later when the fill moves to something else —
     at which point one of them is a visible outline and the other still is not. */
  const ref = ['secondary', 'primary', 'tertiary'].find(clears)
    || ['secondary', 'primary', 'tertiary'].find((r) => {
      const h = normalizeHex(pal[r]);
      return h && !(base && colorsConflict(base, h));
    })
    || 'secondary';
  return [{ colorRef: ref, thickness }];
}

/**
 * Which of the logo's detected outline bands may be SEEDED as text outlines, given the plate the
 * text will actually sit on.
 *
 * ── THE BUG THIS FIXES (Ryan 2026-08-07, Bauers Elite) ───────────────────────────────────────────
 * Their mark is #0B588E blue, #010101 near-black, #818285 grey. Auto-styling took the detected layer
 * order literally and seeded the near-black as the text outline — on a #000000 plate. Ryan: *"when
 * 010101 is SO close to black it should forego this as the outline colour… instead go with no
 * outline (if it was a 2-tone logo), or the grey 818285 in this case."*
 *
 * ⚠ THE RULE ALREADY EXISTED AND WAS SIMPLY NOT CONSULTED HERE. `colorsConflict` is ΔE < 14 in
 * CIELAB, and it answers Ryan's question exactly: #000000 vs #010101 is ΔE ≈ 0.3 (it does not even
 * clear `colorsIndistinguishable`, ΔE < 2), while #818285 clears both the plate AND the blue fill.
 * `defaultOutlinesFor` has consulted it since the plate-dye work; `applyLogoStructure` built
 * `autoStyle` straight from the detected order and then `applyAutoStyleToElement` overwrote the
 * careful answer with the raw one. So this is a missing call, not a new policy.
 *
 * ⛔ THEREFORE: NO "NEAR-BLACK EXCEPTION LIST". Ryan asked *"what colours, similar to but not exactly
 * black, need to be exceptions?"* — the honest answer is none of them by name. A list would have to
 * be re-derived the moment a plate is dyed (comp bumpers ARE dyeable), and it would be silent about
 * navy on a navy plate. Measuring against the ACTUAL plate colour covers black today, covers his own
 * white-default hypothetical for free, and needs no maintenance.
 *
 * ⚠ THE COLOUR IS NOT REMOVED FROM THE PALETTE. #010101 stays their secondary and stays on the swatch
 * row: it is a real brand colour, it is pickable, and it becomes perfectly usable the moment the
 * buyer dyes the plate. What changes is only what we SEED unprompted — an explicit pick is still
 * theirs to make, per the standing rule that we never overwrite a colour the buyer chose.
 *
 * ⚠ SEED-TIME ONLY, WHICH IS WHY MUTATING HERE IS SAFE. Contrast that reacts to a CHANGEABLE input
 * has to be a render-time view or it erodes cumulatively (the plate-dye peel lesson). This runs once
 * per upload, against the plate `applyLogoStructure` has just reset to the default — a fixed base,
 * not a moving one — and the render-time view still owns everything that happens after. Two layers,
 * two jobs.
 *
 * @param {Array<{hex: string, thickness: number}>} outs  detected bands, inner → outer
 * @param {object} palette                                the profile palette (roles already assigned)
 * @param {{base?: string, fillHex?: string, thickness?: number}} opts
 * @returns {Array<{colorRef: string, thickness: number}>} possibly EMPTY — see below
 */
/**
 * Record `from → to` in a tone-swap list, COMPOSING rather than stacking.
 *
 * Every entry must stay keyed to a colour the ORIGINAL raster actually contains, because the pixel
 * pass applies the first matching swap and stops: after {blue→red}, a second entry {red→green} is
 * unreachable for a pixel already rewritten to red, so re-editing a tone has to move the existing
 * entry's target instead of appending a hop.
 *
 * ⛔ EXTRACTED BECAUSE THERE ARE TWO LISTS NOW (Ryan 2026-08-12). A recolour made BEFORE
 * "Design My Weights" has no logo element to write to — the element is not created until the build
 * beat — so it lands on `profile.savedLogoToneSwaps` instead. Two copies of a rule this subtle is
 * the §0b trap; one function, two callers.
 *
 * @returns {boolean} whether the list changed.
 */
export function composeToneSwap(list, from, to) {
  const F = normalizeHex(from);
  const T = normalizeHex(to);
  if (!Array.isArray(list) || !F || !T) return false;
  let hit = false;
  const prior = list.find((s) => normalizeHex(s && s.to) === F);
  if (prior) { prior.to = T; hit = true; }
  else if (!list.some((s) => normalizeHex(s && s.from) === F)) { list.push({ from: F, to: T }); hit = true; }
  // A swap that lands back on the colour it started from is not a swap.
  for (let i = list.length - 1; i >= 0; i--) {
    if (normalizeHex(list[i].from) === normalizeHex(list[i].to)) { list.splice(i, 1); hit = true; }
  }
  return hit;
}

/**
 * A logo element that carries no paint of its own inherits the profile's stash.
 *
 * ⛔ ONE RULE, THREE ARRIVALS. A logo element can appear from a layout change (`applyLayoutPreset`
 * re-seeding a slot after a text-only layout), from `setLogo` creating the first one at the build
 * beat, or from a preset swap — and all three must hand it the same paint, or the mark comes back in
 * its original colours depending on which door it came through. `applyLayoutPreset` has always done
 * this for the first case; the build beat needed it once a recolour could happen BEFORE the element
 * existed (Ryan 2026-08-12).
 *
 * ⚠ ONLY FILLS AN EMPTY ONE. An element that already carries swaps has its own, more specific answer.
 */
export function inheritSavedToneSwaps(profile) {
  const saved = profile && profile.savedLogoToneSwaps;
  if (!Array.isArray(saved) || !saved.length) return false;
  let hit = false;
  for (const e of (profile.elements || [])) {
    if (e.type !== 'logo') continue;
    if (Array.isArray(e.logoToneSwaps) && e.logoToneSwaps.length) continue;
    e.logoToneSwaps = saved.map((s) => ({ ...s }));
    hit = true;
  }
  return hit;
}

/* ⛔ THE TEMPLATE NO LONGER FILTERS AGAINST THE SEED PLATE (Ryan 2026-08-18, Abbey Athletics:
 * *"the logo has a clearly defined thick black outline then a thinner white one. None of the
 * plates produced this combination at all."*). This used to drop any band that merged with
 * `opts.base` — and the base at seed time is the DEFAULT BLACK plate, so a mark's black keyline
 * was erased from `autoStyle` itself: not hidden on the black plate, gone from the design's
 * memory, unrecoverable on the red plate where it reads perfectly. The template is the record of
 * what the MARK wears; which slots read on the CURRENT plate is `applyAutoStyleToElement`'s
 * per-plate stamp question (and the band mirror's), asked fresh on every plate. The Bauers
 * protection this filter was built for (near-black ring seeded on black) is not lost — the stamp
 * filter still withholds a merging non-band slot, and a merging BAND slot now goes through the
 * band mirror, which repaints it exactly as the raster repaints the band itself. */
export function seedOutlinesFrom(outs, palette, opts = {}) {
  const pal = palette || {};
  const base = normalizeHex(opts.base);
  const fill = normalizeHex(opts.fillHex);
  const list = Array.isArray(outs) ? outs : [];
  const readsOnPlate = (hex) => !!hex && !(base && colorsConflict(base, hex));
  const thick = (o) => (Number.isFinite(o && o.thickness) && o.thickness > 0 ? o.thickness : 2.5);

  /* ⛔ A BAND'S `thickness` IS A RATIO, NOT A WEIGHT — and `opts.thickness` was being computed,
     passed in, and then used on the FALLBACK path only (Ryan 2026-08-15, the Springboro panther:
     *"the text defaults to a 2.5 thickness outline, but the logo uses 1.5 … seems backwards"*).
       `analyzeStructure` normalises a mark's bands so their MEDIAN lands on `defaultOutline`
     (logo-structure.js: `scale = o.defaultOutline / medW`). That deliberately keeps the ratios
     BETWEEN one logo's own bands and discards the absolute weight of its linework — so a mark with a
     single band always arrives here at exactly 2.5, however finely it is ruled. Mapping that straight
     onto the text is what put a 2.5 ring beside a keyline measured at 1.25 viewBox units.
       `outlineThicknessFor` is the missing absolute: the caller already measured the artwork
     (`logoStrokeFrac`) and hands the answer down as `opts.thickness`. Anchoring the median to it
     restores the weight while the normalisation keeps the ratios — the silver rim stays thin against
     the black body, the whole stack just stops being heavier than the mark it came from.
     ⚠ IT ONLY EVER LIGHTENS, for the same reason `outlineThicknessFor` caps its own multiplier at 1:
     a mark at or above the reference is left exactly where it is, so no design gets a HEAVIER ring
     than it does today. Floor of 1 and the 0.5 grid are that function's, so both dials agree.
     ⚠ SEEDS ONLY. A stored thickness is serialized with the design; nothing already saved moves. */
  const anchor = Number.isFinite(opts.thickness) && opts.thickness > 0
    ? Math.min(1, opts.thickness / DEFAULT_OUTLINE_THICKNESS)
    : 1;

  /* Role refs are positional — slot 0 is `secondary`, slot 1 is `tertiary` — so a kept band MUST
     carry its ORIGINAL index. (The plate filter that used to live here moved to the per-plate
     stamp — see the header ⛔. Bands with no usable hex are still dropped.) */
  const kept = list
    .map((o, i) => ({ o, i, hex: normalizeHex(o && o.hex) }))
    .filter(({ hex }) => !!hex);

  /* ⭐ EACH RING TAKES THE WEIGHT OF THE BAND IT MIRRORS (Ryan 2026-09-18 — eight of his fifteen
     pass-seven notes are this one axis, and four of them sit on plates he answered `after`, taking
     the colour and flagging the weight in the same breath):
         *"Need to confirm that the line thickness on numbers closely resembles the logo"*
             www-nccspartans cfg1/cfg2/cfg4 · hclarks cfg1
         *"Need to make sure the thickness of the outline on the number matches the logo as best as
          possible"*   uisprairiestars cfg2/cfg3
         *"The black outline on after is too prominent"*   gopetrels cfg4
       ⛔ AND THE CHAIN BELOW CANNOT DO IT — NOT BADLY, AT ALL. Read it as arithmetic: the bands
     arrive normalised so their MEDIAN is 2.5; `anchor` multiplies the stack by
     `outlineThicknessFor / 2.5` to put the mark's measured linework back; and then the
     ratio-preserving floor multiplies it by `1 / minW`. The moment the thinnest ring lands under
     1.0 those two cancel EXACTLY, and the stack comes out at the raw normalised ratios again — the
     anchor having done nothing at all.
       That is not a corner. Measured over the pass-seven dumps (132 marks, 99 seeded stacks):
     **every one of the 35 multi-band stacks has its minimum at exactly 1.0**, i.e. the lift fired on
     all of them, and **33 of those 35 end up heavier than the mark's own measured linework**.
     Thirteen wear a 2.5 ring on artwork measuring 0.24–1.18 viewBox units; bobcats-phsc-edu's
     linework measures 0.24u and its number wears 2.5. So "IT ONLY EVER LIGHTENS", two paragraphs
     down, is void on a third of the corpus — and void precisely on the finely-ruled marks the
     promise was written to protect.
       With each band carrying its own medial-axis width the question needs neither dial: the band's
     measured weight, through the same exchange rate, on the same grid, under the same ceiling.
     ⚠ ALL OR NOTHING. A stack half-measured and half-normalised would carry ratios from two
     different scales, which is worse than either; a mark with any unmeasured band (too few ridge
     samples — see `minRidgeSamples`) keeps the chain below verbatim, as does every design saved
     before this shipped.
     ✅ `abbeyathletics-com` IS THE CONTROL, because it is the mark the ratio-preserving floor was
     built for (Ryan 2026-08-23: *"it only gave 1.5 to the black when irl it looks closer to 2"*).
     It measures 2.5 / 1.0 either way — the fix that needed the lift does not need it once the bands
     are measured directly. */
  if (kept.length) {
    /* ⭐ A BAND SEVERAL TIMES THE MARK'S OWN LINEWORK IS A FIELD, NOT A KEYLINE (Ryan 2026-09-20,
     * answering ten rendered plates: *"Outlines seem really thick on the AFTER"* on
     * goldentigersports and *"Line thickness is also way too thick for the yellow outline on the
     * weight number"* on Mount Mercy).
     *   `ringWeightFromFrac` holds a measured width inside `[1, DEFAULT_OUTLINE_THICKNESS]`, and on
     * these marks the measurement lands ABOVE that window — so the clamp picked the ring and the
     * measurement picked nothing. Mount Mercy's yellow band measures 6.42 units, 12% of the
     * artwork's long edge; that is a filled shape, and echoing its width onto a two-digit number is
     * a category error rather than a ring that is merely too heavy.
     *   ⛔ THE CEILING ITSELF IS LOAD-BEARING AND STAYS. Ten marks carry an over-ceiling band and
     * SEVEN of them Ryan calls correct — `e-logo` and `abbeyathletics` among them, both named
     * controls, and Abbey is the mark whose ring he asked to be HEAVIER (*"it only gave 1.5 to the
     * black when irl it looks closer to 2"*). Pricing every over-ceiling band at the mark's linework
     * would take Abbey to 1.5 and re-break his own complaint. So the test is the RATIO, not the
     * ceiling.
     *   ⭐ THE BAR IS MEASURED, NOT CHOSEN. All ten over-ceiling marks were rendered and put to him
     * one by one (artifact UHRwBnQ19DP1ybpCrds4tC, collection `verdicts`). Sorted by band ÷ the
     * mark's own linework his answers separate with nothing in between:
     *       4.28x mountmercy · 4.02x goldentigersports · 3.70x 120081000896   TOO HEAVY
     *       2.39x 120159008515 · 1.80x mcdonalds · 1.80x cmlions · 1.73x abbey
     *       1.48x wsuathletics · 1.23x e-logo · 1.04x polkeagles              LOOKS RIGHT
     *   Any bar in (2.39, 3.70] reproduces all ten; 3 sits in the middle of that gap, furthest from
     * both sides. ⚠ `120081000896` is why the gap is trustworthy: at 3.70x it was the nearest
     * APPROVED mark when this was measured against pass 8 alone, which put the only candidate bar
     * one plate from breaking — and shown the picture he called it heavy too.
     *   The replacement is `opts.thickness`, the mark's own measured linework, which is what
     * `presentedOuterOutline` already falls back to for a band it cannot measure. Same currency,
     * same grid, same clamp; no new arithmetic. Because `outlineThicknessFor` floors at 1.0, a band
     * can only pass this test at over 3.0 units — i.e. it is always one the ceiling was already
     * deciding, so nothing under the ceiling moves. Corpus net: 3 marks, 7 hold, both controls hold. */
    const own = Number.isFinite(opts.thickness) && opts.thickness > 0 ? opts.thickness : null;
    const measured = kept.map(({ o }) => {
      const w = ringWeightFromFrac(o && o.widthFrac);
      if (w == null || !own) return w;
      const units = (o && o.widthFrac) * LOGO_BOX_NOMINAL_VB;
      return units > own * BAND_IS_A_FIELD_RATIO ? Math.max(FIELD_BAND_RING_MIN, own) : w;
    });
    if (measured.every((x) => x != null)) {
      return kept.map(({ o, i, hex }, k) => ({
        colorRef: i === 0 ? 'secondary' : i === 1 ? 'tertiary' : hex,
        thickness: measured[k],
      }));
    }
  }

  if (kept.length) {
    /* ⛔ THE FLOOR IS RATIO-PRESERVING (Ryan 2026-08-23, Abbey Athletics: *"it only gave 1.5 to
       the black when irl it looks closer to 2"*). The mark measured black 2.5 : white 1; the
       anchor (×0.6 — the Springboro only-lighten rule) took them to 1.5 / 0.6, and the per-ring
       floor then lifted ONLY the white back to 1.0 — crushing the measured ratio to 1.5:1. When
       the thinnest ring needs the floor, the WHOLE stack lifts by that factor instead (each ring
       still capped at DEFAULT_OUTLINE_THICKNESS), so the proportions the artwork was measured to
       have are what the text wears: Abbey seeds 2.5 / 1.0. A stack whose thinnest ring already
       clears 1 is untouched (E Logo's 1 / 2.5 — no lift; Springboro's single band — no lift),
       so the only-lighten promise bends only where honouring it would misdraw the mark. */
    const raw = kept.map(({ o }) => thick(o) * anchor);
    const minW = Math.min(...raw);
    const lift = minW < 1 ? 1 / minW : 1;
    return kept.map(({ o, i, hex }, k) => ({
      colorRef: i === 0 ? 'secondary' : i === 1 ? 'tertiary' : hex,
      thickness: Math.max(1, Math.min(DEFAULT_OUTLINE_THICKNESS, Math.round(raw[k] * lift * 2) / 2)),
    }));
  }

  /* Nothing the logo drew clears the plate. Before giving up, try the buyer's other roles — this is
     the Bauers case when the grey is a ROLE rather than a detected band, and it is the difference
     between using their third colour and ignoring it. Requires clearing the FILL too: a ring the
     same colour as the letter it wraps is not an outline. */
  const fallbackRef = ['secondary', 'tertiary', 'primary'].find((r) => {
    const h = normalizeHex(pal[r]);
    return readsOnPlate(h) && !(fill && colorsConflict(fill, h));
  });
  if (fallbackRef) {
    return [{ colorRef: fallbackRef, thickness: opts.thickness || thick(list[0]) }];
  }

  /* ⛔ AND IF NOTHING CLEARS THE PLATE, SEED NOTHING. Ryan named this case himself: a two-tone logo
     whose only non-fill colour is the plate colour should get NO outline, not an invisible one. An
     empty stack is honest — the buyer sees no outline chip and can add one — whereas a seeded ring
     nobody can see occupies a slot, looks like a styled design in the panel, and rides into the
     quote as a colour we never actually painted. */
  return [];
}

/**
 * MIGRATION ONLY — is this design hand-styled, judged from the elements? (Ryan 2026-08-08.)
 *
 * `styleUserEdited` is a latch precisely because it cannot be derived reliably: auto passes rewrite
 * the outline stack constantly, and a REMOVED default leaves no trace at all (`removeOutlineAll`
 * splices it out). So this is not a substitute for the latch and must never be called as one — it
 * exists for the designs already sitting in someone's localStorage, saved before the flag existed,
 * where the alternative is defaulting them all to "untouched" and letting one checkbox re-derive
 * over outlines they chose weeks ago.
 *
 * Deliberately OVER-eager: every signal here means "somebody's hand was on this", and a false
 * positive costs nothing — the holes flip simply behaves the way it did yesterday. A false NEGATIVE
 * is the bug, so the removal it cannot see is the one case this knowingly misses, and it is the
 * reason the real answer is recorded going forward instead of computed.
 */
export function inferHandStyledForMigration(profile) {
  for (const e of (profile && profile.elements) || []) {
    if (e.type === 'arctext' || e.type === 'weight') {
      if (e.fillUserSet === true || e.fillLocked === true) return true;
      for (const o of Array.isArray(e.outlines) ? e.outlines : []) {
        if (o && (o.userSet === true || o.added === true || o.locked === true || o.enabled === false)) return true;
      }
    } else if (e.type === 'logo') {
      if (Array.isArray(e.logoBandsOff) && e.logoBandsOff.length) return true;
      if (Array.isArray(e.logoBandUserSet) && e.logoBandUserSet.length) return true;
      if (Array.isArray(e.logoToneSwaps) && e.logoToneSwaps.length) return true;
      if (e.logoAddOutline && (!Array.isArray(e.logoAddOutline) || e.logoAddOutline.length)) return true;
      if (normalizeHex(e.logoCenterColor) && e.logoCenterAuto !== true) return true;
    }
  }
  return false;
}

export function applyAutoStyleToElement(profile, el) {
  const as = profile && profile.autoStyle;
  if (!el || (el.type !== 'arctext' && el.type !== 'weight')) return false;
  // No autoStyle (plain-palette logo) → still correct the piece's BORN-WITH ring against the real
  // palette, because createElement has no palette to consult and always seeds one.
  if (!as || !Array.isArray(as.outlines)) {
    /* ⛔ THIS IS THE PATH THE BAUERS BUG ACTUALLY TOOK (Ryan 2026-08-07), and it is worth stating
       plainly because two fixes missed it by looking in the obvious place. Their mark returns a
       LOW-confidence structure ("outer-not-exposed(0.17) · colour-borne detail"), so
       `applyLogoStructure` bails before building `autoStyle` — and every plate-aware rule we have
       lives on the HIGH-confidence side. The elements `smartStart` then builds keep
       `createElement`'s hardcoded `[{colorRef:'secondary'}]`, their secondary is the brand's #010101,
       and nothing between there and the canvas ever asks whether it reads on the plate.
         The old test below only rescued the case where secondary was ABSENT. Absent and INVISIBLE
       need the same answer, and the second one is the common one: a plain-palette logo is exactly
       the kind whose dominant tone is often a near-black.
         `defaultOutlinesFor` already resolves this correctly against both the plate and the fill —
       it is what the product-switch rebuild calls, which is why walking to another product and back
       "fixed" it and made the two paths disagree. Calling it here is what makes the FIRST render
       agree with every render after it.
       ⚠ ONLY THE UNTOUCHED DEFAULT IS RE-DERIVED. An outline the buyer added or recoloured carries
       `added`/`colorRef` of its own and is left exactly alone — we are correcting a seed nobody
       chose, never a decision somebody made. */
    const bornDefault = Array.isArray(el.outlines) && el.outlines.length
      && el.outlines.every((o) => o && o.colorRef === 'secondary' && o.added !== true);
    /* ⛔ A MARK WITH NO KEYLINE GIVES THE TYPE NO KEYLINE (Ryan 2026-08-17, Lawson State on Live):
     * *"the number defaults to blue with a thick white obnoxious outline when the logo has none.
     * Should be a straight blue number."* The third face of the mirror rule the other two shipped
     * in `applyVanishingLogoDefaults` today — the type wears what the mark wears, because the mark
     * has already answered this question on this plate.
     *   ⚠ IT HAS TO BE HERE, and finding that cost a cycle. Gating the three `defaultOutlinesFor`
     * seeders missed it completely: on a FIRST upload `smartStart` rebuilds the elements through
     * `createElement` afterwards, whose born ring is a hardcoded `secondary @2.5`, and this branch
     * is the only thing downstream that owns that ring. The tell was the stored value — a LITERAL
     * `#FFFFFF`, which no `defaultOutlinesFor` path can emit (it returns refs), so the ring was the
     * born one recoloured, not a seeded one.
     *   ⚠ And the same `bornDefault` test still bounds it: a ring the buyer added or recoloured is
     * a decision and is left alone. Legibility is unaffected — a fill that would vanish is still
     * caught by `applyVanishingLogoDefaults` and the contrast-ring rescues. */
    const ls = (profile || {}).logoStructure;
    /* ⚠ …UNLESS the mark wears a DE-FACTO keyline the band detector declined (Ryan 2026-08-23,
       ECC Kats): `ls.wrapTone` — computed from the touch table at structure time — names the
       colour that wraps the mark's elements while the band list is empty. The born ring then
       seeds in that colour, so an outlined badge's text matches it out of the box instead of
       going bare. A mark with neither bands nor a wrap tone keeps the Lawson-State rule. */
    if (bornDefault && !(ls && Array.isArray(ls.bands) && ls.bands.length)) {
      if (ls && ls.wrapTone) {
        el.outlines = [{ colorRef: ls.wrapTone, thickness: outlineThicknessFor(profile) }];
        return true;
      }
      /* ⚠ …AND THE NO-KEYLINE RULE YIELDS TO LEGIBILITY. "The mark has no keyline" answers a
         STYLE question; it cannot answer "can this fill be seen on this plate?", and until now it
         returned bare before the roles → bench → house-neutral ladder below ever ran.

         ⛔ THE COMMENT ABOVE CLAIMED `applyVanishingLogoDefaults` COVERS THIS. It does not.
         That rescue triggers on `colorsConflict`, which is ΔE-only, so there is a whole band that
         clears it and is still unreadable: the DB monogram's dark green `#164734` sits at 1.85:1
         on the black plate and conflicts with nothing (Ryan, 2026-08-12: *"why on earth is it
         adding this as a green on green outline?"*). WD 325 made that fix unreachable five days
         after it shipped, and the effect compounds — stripping rings leaves fewer style donors,
         so MORE elements fall into this branch.

         ✅ `readsOn`'s 2.0 bar is what separates the two reports rather than trading one for the
         other: Lawson State's navy `#244196` reads on black at 2.12:1 and still goes bare, which
         is exactly what WD 325 was asked for; the dark green at 1.85 and a near-black on
         near-black at 1.07 fall through and get their ring back. Same predicate as
         `state.js`'s own legibility test. */
      const plateHex0 = normalizeHex((profile || {}).bodyColor);
      const fillHex0 = normalizeHex(resolvePaletteRef(el.fillRef || 'primary', (profile || {}).palette || {}));
      if (!plateHex0 || !fillHex0
          || (!colorsConflict(plateHex0, fillHex0) && readsOn(plateHex0, fillHex0))) {
        el.outlines = []; return true;
      }
      /* the fill vanishes into the plate — fall through to the ladder below */
    }
    if (bornDefault) {
      const pal = (profile || {}).palette || {};
      /* ⛔ AND THE THICKNESS IS DERIVED HERE TOO (Ryan 2026-08-09): *"1 black comp plate… the text
         outline defaults to being too thick. If I then change the plate color it reverts to its
         proper thickness, and if I go back to black it stays thin."*
           A plate colour cannot change a line WEIGHT, and nothing here was trying to — the two
         numbers simply came from two places. `createElement` is a pure factory with no profile to
         ask, so it hardcodes `DEFAULT_OUTLINE_THICKNESS`; every later derivation calls
         `outlineThicknessFor`, which scales that constant by the ARTWORK's own linework weight
         (`logoStrokeFrac`). The Angry Panda measures 0.0136 against a 0.026 reference, so the seed
         shipped 2.5 and the first plate dye re-derived 1.5 — a change with no cause the buyer could
         see, and unreachable without dyeing the plate.
           This branch is already the place that corrects a born-with ring nobody chose (see the
         Bauers note above); the COLOUR was being re-derived here and the thickness was being carried
         through. Now both are.
           ⚠ ONLY THE FACTORY NUMBER IS REPLACED. A thickness the buyer moved is a decision, and it
         arrives here unchanged — `bornDefault` cannot tell an edited width from a seeded one because
         it only inspects `colorRef`, so the equality test is what keeps this a seed correction.
           ⚠ ONE-SHOT: the branch rewrites `colorRef` to a literal hex, so `bornDefault` is false on
         every stamp after this one and nothing can re-derive a width twice. */
      const born = el.outlines[0] && el.outlines[0].thickness;
      const thickness = (!born || born === DEFAULT_OUTLINE_THICKNESS)
        ? outlineThicknessFor(profile)
        : born;
      const plate = normalizeHex(profile.bodyColor);
      const fill = normalizeHex(resolvePaletteRef(el.fillRef || 'primary', pal));
      const clearsPlate = (hex) => !!hex && (!plate || !colorsConflict(plate, hex));
      const clearsFill = (hex) => !!hex && (!fill || !colorsConflict(fill, hex));

      /* ⚠ THE BENCH IS SEARCHED TOO, AND THAT IS WHAT FINALLY ANSWERS RYAN'S ACTUAL ASK. His words
         were *"it left their 3rd colour of #818285 on the sidelines"* — and it literally is on a
         bench: `applyPalette` seats a logo's third tone via `addSwatch(..., { ownTone: true })`
         rather than as a palette ROLE, because only primary and secondary become roles. Every
         plate-aware chooser we had — `defaultOutlinesFor` included — reads roles only, so their grey
         was invisible to the code in the exact sense he meant it.
         ⚠ SWATCHES ARE THE BUYER'S OWN COLOURS, not house neutrals (those live elsewhere on purpose),
         so picking from here keeps the result on-brand — the same reasoning `applyVanishingLogoDefaults`
         uses when it prefers a colour already in the design over a house white. */
      // ⚠ RANKED FOR THE RING ROLE, not by population — `ringPreferenceOrder`, the same call
      // `assignOutlineColors` makes, so the seed and every later re-derive answer this identically.
      const bench = ringPreferenceOrder(profile.swatches, profile.bodyColor);
      const roleHexes = ['secondary', 'tertiary', 'primary'].map((r) => normalizeHex(pal[r]));

      /* ⛔ ORDER MATTERS, AND IT IS NOT `defaultOutlinesFor`'s ORDER. That function prefers a role
         that clears the PLATE even when it equals the fill, on the stated grounds that a ring
         matching the letter merely reads as a bolder letter while a ring matching the plate is gone.
         True, and it still ranks blue-on-blue above a grey that is genuinely distinct — which is
         what this path produced before the bench was searched, and it is not what Ryan asked for.
         So: anything that clears BOTH wins first, roles before bench so a real role still leads.
         ⚠ …AND NEAR-NEUTRALS BEHIND EVERY REAL BRAND COLOUR (Ryan 2026-08-10) — the same
         `demoteNearNeutrals` call `assignOutlineColors` makes, applied to the SAME ladder, because
         the seed and every later re-derive answering this differently is the failure this branch was
         built to end.
         ⛔ …AND THE HOUSE NEUTRALS ARE THE THIRD RUNG OF THAT SAME LADDER (Ryan 2026-08-12, the DB
         monogram). This searched roles + bench and then went STRAIGHT to the bolder-letter
         compromise, so on a mark with one usable tone it had exactly one candidate and took it:
         *"text color as green (good), text outline as green too (bad) — why on earth is it adding
         this as a green on green outline?"*
           The ring that mark wants is WHITE, and it had one until the buyer ticked "let weight show
         through" — the white counters ARE the mark's second tone, so cutting them re-extracts a
         one-tone palette (measured: `secondary` #FFFFFF → null, `logoToneCount` 2 → 1) and the only
         role left is the fill itself. Nothing was broken by the flip; the ladder simply had nowhere
         else to go.
           `assignOutlineColors` never had this hole — its own ladder ends `usableAccents(...)`, the
         documented order everywhere in the colour model (roles → the buyer's bench → house neutrals
         LAST). It just never runs here: the whole re-cascade at the build beat is gated behind
         `!isNearBlackHex(bodyColor)`, and this plate is #0B0C0F. So the seed is on its own on a
         black plate, which is the commonest plate there is.
           ⚠ STILL LAST, AND STILL BEHIND BOTH PREDICATES. A neutral only wins when no colour the
         buyer actually owns clears the plate AND the fill, and it must clear both itself — so every
         design with a real second tone is untouched, and `usableAccents` additionally withholds a
         neutral that merges with a colour already in the design. Same call, same argument shape as
         `assignOutlineColors` line ~1787, so the two cannot drift.
         ⚠ The bolder-letter compromise below is UNCHANGED and still reachable — it is now what
         happens when not even a neutral clears the plate, which is the case its comment describes. */
      const best = demoteNearNeutrals([...roleHexes, ...bench]).find((h) => clearsPlate(h) && clearsFill(h))
        || usableAccents([...roleHexes, ...bench].filter(Boolean)).find((h) => clearsPlate(h) && clearsFill(h))
        || roleHexes.find(clearsPlate)   // …then the bolder-letter compromise
        || null;                         // …and finally nothing, which beats an invisible ring

      el.outlines = best ? [{ colorRef: best, thickness }] : [];
      return true;
    }
    return false;
  }
  el.fillRef = as.fillRef || 'primary';
  /* ⚠ THE STAMP IS FILTERED TOO, NOT JUST THE SEED (Ryan 2026-08-07, the second half of the Bauers
     report: *"why didn't it appear grey from the onset?"*).
     Filtering only where `autoStyle` is BUILT left three ways for an invisible ring to reach an
     element anyway — a design saved before that fix (autoStyle round-trips through every snapshot,
     so it survives resume, undo and redo), a design whose plate was dyed after the seed, and any
     future writer of `autoStyle`. Every one of them lands here, which is why the rule belongs here
     as well: this is the single door every stamp goes through.
     ⛔ AND IT FILTERS `as.outlines` WITHOUT REWRITING IT. The template keeps all its slots; only the
     ELEMENT gets the reduced stack. That is what makes this non-cumulative and reversible — dye the
     plate white and the next stamp hands the near-black back, because it was never deleted. Writing
     the filtered list into `autoStyle` would erode it one plate change at a time, which is exactly
     the bug the plate-dye peel taught us (see `seedOutlinesFrom`). */
  const plate = normalizeHex(profile && profile.bodyColor);
  /* ⚠ A slot that ECHOES one of the mark's own bands is NEVER withheld here (Ryan 2026-08-18,
     E Logo's white ring on the white plate: the mark keeps its band there, and the ring between
     the fill and the outer ring is what makes the gap read). Its per-plate fate belongs to the
     band mirror in `assignOutlineColors` — kept, repainted with the band, or dropped with its
     peel — the same answer the raster gives the band itself. The filter still withholds a
     merging NON-band slot (the Bauers protection, unchanged). */
  const lsAll = profile && profile.logoStructure;
  const lsBands = (lsAll && lsAll.confidence === 'high'
    && Array.isArray(lsAll.bands)) ? lsAll.bands.map((h) => normalizeHex(h)).filter(Boolean) : [];
  // The WRAP tone's ring is a band echo in everything but detection (Ryan 2026-08-23, the Kats'
  // gray plate: the wrap ring merged with the plate and this filter dropped it, leaving the 10's
  // text bare while the other three configs wore their rings). Kept, so the ladder RECOLOURS it
  // per plate — on the gray plate that lands the dark, on the dark plate the white.
  const wrapHex = lsAll ? normalizeHex(lsAll.wrapTone) : null;
  const visible = (o) => {
    if (!plate) return true;
    const hex = normalizeHex(o && o.colorRef && o.colorRef.startsWith('#') ? o.colorRef : (profile.palette || {})[o.colorRef]);
    if (!hex) return true;
    if (lsBands.some((b) => b === hex || colorsConflict(b, hex))) return true; // band echo → the mirror decides
    if (wrapHex && (hex === wrapHex || colorsConflict(wrapHex, hex))) return true; // wrap echo → the ladder recolours
    return !colorsConflict(plate, hex);
  };
  // `autoIdx` records WHICH autoStyle slot each default came from (Ryan 2026-07-24). Without it, a
  // removed default is unidentifiable and "+ Add" can only append by position — delete the INNER of
  // two and Add hands back the OUTER's colour, duplicating it and losing the inner one for good. With
  // it, Add can restore the lowest MISSING slot at its correct depth. Survives recolours, because
  // identity is the slot, not the colour.
  /* `autoIdx` is assigned from the ORIGINAL index before filtering, so a dropped ring does not
     renumber the survivors — "+ Add" restores a removed default to its own depth by that number, and
     shifting them would hand a slot back the wrong colour. */
  el.outlines = as.outlines
    .map((o, i) => ({ colorRef: o.colorRef, thickness: o.thickness, autoIdx: i }))
    .filter(visible);
  return true;
}

/** Stamp the auto-style onto EVERY text/weight element (a new logo restyles the
 *  whole design). Pure. */
export function applyAutoStyleToElements(profile) {
  let touched = false;
  for (const e of (profile && profile.elements) || []) {
    if (applyAutoStyleToElement(profile, e)) touched = true;
  }
  return touched;
}

// ── serialization (quote / autosave payload) ─────────────────────────────────

function serializeLogo(logo) {
  const l = logo || createDefaultLogo();
  return {
    dataUrl: l.dataUrl ?? null,
    fileName: l.fileName ?? null,
    mimeType: l.mimeType ?? null,
    width: l.width ?? null,
    height: l.height ?? null,
    aspectRatio: l.aspectRatio ?? null,
    // WHERE THE ARTWORK ISN'T, measured at upload (logo-structure.findClearSpot). Two numbers, and
    // they have to survive the wire for the same reason `aspectRatio` does: the render path cannot
    // look at pixels, so a resumed design with no clear spot would silently put Full Face's weight
    // number back in a fixed place.
    // ⚠ `ver` RIDES ALONG, and it is carried as a plain number rather than checked against
    // `CLEAR_SPOT_VERSION`: this module stays free of a logo-structure import (it is on the
    // first-paint path), and the check belongs to `backfillClearSpot`, which is the one place that
    // can act on the answer. Dropping it here would make every resume look like a stale
    // measurement and re-scan the artwork on each one.
    clearSpot: l.clearSpot && Number.isFinite(l.clearSpot.u) && Number.isFinite(l.clearSpot.v)
      ? {
        u: Math.max(-1, Math.min(1, l.clearSpot.u)),
        v: Math.max(-1, Math.min(1, l.clearSpot.v)),
        ...(Number.isFinite(l.clearSpot.ver) ? { ver: l.clearSpot.ver | 0 } : {}),
      }
      : null,
    isVector: !!l.isVector,
    quality: l.quality ?? null,
    transparentBg: l.transparentBg !== false,
    // Whitelist — anything not named here is dropped on save, which is exactly how the key context
    // went missing across a resume in the first place.
    keyColor: Array.isArray(l.keyColor) ? l.keyColor.slice(0, 3).map((n) => n | 0) : null,
    keyWhite: Number.isFinite(l.keyWhite) ? (l.keyWhite | 0) : null,
    keyField: Array.isArray(l.keyField) ? l.keyField.slice(0, 3).map((n) => n | 0) : null,
    keyGlobal: typeof l.keyGlobal === 'boolean' ? l.keyGlobal : null,
    keyPockets: l.keyPockets && typeof l.keyPockets === 'object' ? {
      share: Number(l.keyPockets.share) || 0,
      pocketHex: normalizeHex(l.keyPockets.pocketHex) || '#FFFFFF',
      ringHex: normalizeHex(l.keyPockets.ringHex) || null,
    } : null,
    keyGlobalUserSet: l.keyGlobalUserSet === true,
    tapFixed: l.tapFixed === true,
  };
}

function serializeOutline(o) {
  const out = { colorRef: o.colorRef || 'secondary', thickness: Number(o.thickness) || 0 };
  // A disabled outline is kept in the stack (grayed in the UI, easy to re-enable) but not
  // rendered — the on/off toggle grays out instead of deleting (Ryan 2026-07-21).
  if (o.enabled === false) out.enabled = false;
  // A user-ADDED outline (vs auto-seeded from the logo) — its − removes it instead of
  // graying it (Ryan 2026-07-22).
  if (o.added) out.added = true;
  // LOCKED (Ryan 2026-07-23) — peel immunity for the CURRENT plate: the plate-dye peel leaves this
  // ring alone until the buyer picks a new plate colour (re-cascade) or Resets. Scoped to one plate
  // BY DESIGN, and set on auto-derived rings too (syncFillContrastOutline locks the contrast ring it
  // adds, since peeling it would undo the very reason it exists).
  if (o.locked === true) out.locked = true;
  // USER-SET (Ryan 2026-07-24) — the buyer chose this ring's colour themselves. Distinct from `locked`,
  // which they were conflated with: `locked` must RESET on a plate change (a new plate needs a fresh
  // conflict verdict) while this must SURVIVE one (their colour shouldn't revert). Sharing one flag
  // meant an auto contrast ring added for a yellow plate stayed peel-immune on a green plate — green
  // outline on a green plate.
  if (o.userSet === true) out.userSet = true;
  // Which autoStyle slot this default came from, so "+ Add" can restore a REMOVED default to its own
  // depth with its own colour (Ryan 2026-07-24). MUST persist: an outline removed before a reload
  // would otherwise be unrestorable. Absent = a user-added outline (or an old save).
  if (Number.isInteger(o.autoIdx)) out.autoIdx = o.autoIdx;
  return out;
}

/** Whitelisted serialization of one element (defends against expando leakage). */
export function serializeElement(elm) {
  const e = elm || {};
  const out = { id: String(e.id || ''), type: e.type, slot: e.slot };
  // Orientation flip (Ryan 2026-07-13) — carried on any element type so a
  // reload / quote handoff keeps an upside-down text/weight/logo.
  if (e.upsideDown === true) out.upsideDown = true;
  // "Big" logo flag (Ryan 2026-07-13, #7/#8) — a logo sized to fill the whole
  // paintable ring (top→bottom + ¼" bleed) on one side, clipped to ring B. Only
  // the paintable ring receives ink (hub + rings A/C unchanged); the weight stays
  // in its normal slot. Carried on the element so a reload / quote keeps it.
  if (e.big === true) out.big = true;
  // Big-logo face mode (dumbbell layout family) — 'ring' (default, clipped to the
  // paintable ring like a bumper) or 'disc' (fills the WHOLE face, unclipped —
  // only correct on a product with no physical hub covering the center).
  if (e.big === true && e.bigFace === 'disc') out.bigFace = 'disc';
  // "Big BOX" (2026-07-30) — a LARGER ROOM, not a different placement path. `big` above swaps the
  // renderer onto `bigLogoImageBox`, which is ring-B geometry; this one leaves the mark on the
  // ordinary point path — sized by the dial, clipped to nothing, honestly centred — and only asks
  // the zone for a bigger box. It is what makes the cap's "Big Badge" a large centred logo rather
  // than a badge clipped to an annulus this face does not have. Carried so a reload keeps it.
  if (e.bigBox === true) out.bigBox = true;
  // Manual position angle (§2.4) — set only when the buyer disengaged auto-balance
  // for this piece via the Advanced position stepper.
  if (Number.isFinite(e.angleDeg)) out.angleDeg = e.angleDeg;
  // ⚠ SIZE IS WRITTEN BEFORE THE TYPE BRANCHES (Ryan 2026-07-28: "the logo size also reverts on
  // resume"). It used to sit below them, after the `type === 'logo'` branch had already `return`ed —
  // so of the four element types, the one whose size the buyer adjusts most was the only one whose
  // size was never persisted.
  //   Losing `sizeScale` alone would have been recoverable; losing `sizeUserSet` with it is what made
  // it visible. `syncLogoSize` re-seeds the 75% default for any logo that is not flagged hand-sized,
  // so a resumed design did not merely forget the number — it actively re-seeded over it on the next
  // panel build. Everything else about the mark came back correctly, which is why it read as the
  // size specifically "reverting" rather than as a save failing.
  out.sizeScale = Number.isFinite(e.sizeScale) ? e.sizeScale : 1;
  // The buyer sized this piece BY HAND (Ryan 2026-07-25). Serialized because the default is seeded
  // from the engine's edge on every panel build — without this flag a resume would re-seed over a
  // size they chose deliberately. Twin of the text side's `textUserSet` / `fillUserSet`.
  if (e.sizeUserSet === true) out.sizeUserSet = true;
  // Badge-logo nudge (Ryan 2026-07-29) — a fraction of the plate radius, +u right / +v DOWN (the
  // viewBox convention `bigLogoImageBox` returns a box in, so the sign never has to be flipped at
  // paint time). Only meaningful on a big logo, so only written on one.
  //   ⚠ THIS WHITELIST IS WHY THE LINE EXISTS. `sizeScale` sat below the type branches and was
  // therefore never persisted for the one element type whose size gets adjusted most (the note
  // above). A new field with no line here is not "unsaved" in a way anyone would notice at the
  // time — it comes back as a design that quietly forgot where the buyer put their logo, on
  // resume, on a share link and on the quote PDF.
  /* ⛔ `e.big === true` WAS THE GATE AND IT NO LONGER CAN BE (Ryan 2026-08-09). The weight NUMBER
     now nudges too, on the same layouts, and a weight element is never `big` — so the old test
     silently dropped every number offset on save, which is precisely the failure this block's own
     warning describes. The value is clamped to 0 when absent, so writing it for any element that
     carries one costs nothing and cannot invent one. */
  {
    const nu = clampBigNudge(e.nudgeU, nudgeMaxFor(e), nudgeStepFor(e));
    const nv = clampBigNudge(e.nudgeV, nudgeMaxFor(e), nudgeStepFor(e));
    if (nu !== 0) out.nudgeU = nu;
    if (nv !== 0) out.nudgeV = nv;
  }
  if (e.type === 'logo') {
    out.logo = serializeLogo(e.logo);
    // Manually disabled logo outline bands (indices, outer→in). Empty/absent = none.
    if (Array.isArray(e.logoBandsOff) && e.logoBandsOff.length) {
      out.logoBandsOff = [...new Set(e.logoBandsOff.filter((i) => Number.isInteger(i) && i >= 0 && i < 4))].sort((a, b) => a - b);
    }
    // Added outer rings (C — up to TWO, inner→outer): [{ color, thickness (frac) }]. A legacy
    // single-object save is normalized to an array.
    const aoRaw = Array.isArray(e.logoAddOutline) ? e.logoAddOutline : (e.logoAddOutline ? [e.logoAddOutline] : []);
    const aoOut = aoRaw
      .filter((a) => a && normalizeHex(a.color) && a.thickness > 0)
      .slice(0, 2)
      // `auto` marks the ring syncLogoContrastOutline created on its own (a flatten that would
      // vanish on the dyed plate) as opposed to one the buyer added with "+" (Ryan 2026-07-24).
      // The plate-colour re-cascade re-derives auto rings and KEEPS manual ones. Absent = manual,
      // so an old save's rings are treated as the buyer's — the safe default.
      .map((a) => ({ color: normalizeHex(a.color), thickness: Math.min(0.2, Math.max(0.01, Number(a.thickness))), ...(a.auto === true ? { auto: true } : {}) }));
    if (aoOut.length) out.logoAddOutline = aoOut;
    // Flattened logo-center color (recolors the whole interior to one tone). Absent = original.
    if (normalizeHex(e.logoCenterColor)) out.logoCenterColor = normalizeHex(e.logoCenterColor);
    // `logoCenterAuto` — the flatten above was DERIVED by applyVanishingLogoDefaults (the logo would
    // have vanished into the plate), not picked by the buyer. Same convention as an outline ring's
    // `auto` just above: the plate re-cascade drops derived flattens and keeps chosen ones. Absent =
    // the buyer's, so an old save is treated as an explicit choice — the safe default, and it only
    // costs one extra plate change to re-derive. Serialized because without it a resumed design would
    // carry a derived white forever and grow a contrast ring on a plate it already reads against.
    if (normalizeHex(e.logoCenterColor) && e.logoCenterAuto === true) out.logoCenterAuto = true;
    // Per-band outline color overrides (index outer→in → hex). Absent = detected colors.
    if (e.logoBandColors && typeof e.logoBandColors === 'object') {
      const bc = {};
      for (const k of Object.keys(e.logoBandColors)) {
        const idx = k | 0, hx = normalizeHex(e.logoBandColors[k]);
        if (idx >= 0 && idx < 4 && hx) bc[idx] = hx;
      }
      if (Object.keys(bc).length) out.logoBandColors = bc;
    }
    // Which bands the buyer recoloured THEMSELVES (Ryan 2026-07-25). Serialized because the logo-fill
    // cascade skips these, so without it a resumed design would have its hand-picked band colours
    // overwritten the next time a logo colour was chosen. Twin of the text side's outline `userSet`.
    if (Array.isArray(e.logoBandUserSet) && e.logoBandUserSet.length) {
      const us = [...new Set(e.logoBandUserSet.map((i) => i | 0).filter((i) => i >= 0 && i < 4))].sort((a, b) => a - b);
      if (us.length) out.logoBandUserSet = us;
    }
    // TONE SWAPS (Ryan 2026-07-26) — the buyer edited one of the colours the extractor read off the
    // mark, on the Add Logo tab, and meant the MARK to change. Each entry maps a colour of the
    // ORIGINAL artwork to what it should paint as now, so the list stays canonical however many
    // times a tone is re-edited (see `recolorEverywhere`, which rewrites an entry's `to` rather than
    // stacking a second hop onto it).
    //   Serialized because this is the buyer's artwork, not a derived view: unlike the
    // contextual-contrast remap it shares a code path with, it must survive a resume unchanged.
    if (Array.isArray(e.logoToneSwaps) && e.logoToneSwaps.length) {
      const sw = e.logoToneSwaps
        .map((s) => ({ from: normalizeHex(s && s.from), to: normalizeHex(s && s.to) }))
        .filter((s) => s.from && s.to && s.from !== s.to)
        .slice(0, 8); // a logo with more distinct tones than this isn't being hand-recoloured
      if (sw.length) out.logoToneSwaps = sw;
    }
    return out;
  }
  out.fillRef = e.fillRef || 'primary';
  // Fill LOCKED (Ryan 2026-07-23) — swap immunity for the CURRENT plate: the plate-dye view keeps this
  // fill (green stays green on a green plate) until a new plate pick re-cascades, or Reset.
  if (e.fillLocked === true) out.fillLocked = true;
  // Fill USER-SET (Ryan 2026-07-24) — the buyer picked this fill colour. See serializeOutline's
  // userSet note: survives a re-cascade, whereas fillLocked resets with the plate.
  if (e.fillUserSet === true) out.fillUserSet = true;
  out.outlines = Array.isArray(e.outlines) ? e.outlines.slice(0, 4).map(serializeOutline) : [];
  /* WD 486: which of the app's own outline rings the buyer deleted or re-sized, by `autoIdx` - so a plate pick (and a
     reload) does not bring a deleted ring back or snap a dialled thickness back. See `noteOutlineEdit` (ui/state.js). */
  if (e.outlineEdits && typeof e.outlineEdits === 'object') {
    const m = {};
    for (const [k, v] of Object.entries(e.outlineEdits)) {
      if (!/^[0-3]$/.test(k) || !v) continue;
      if (v.removed === true) m[k] = { removed: true };
      else if (Number.isFinite(v.thickness)) m[k] = { thickness: Math.max(0, Math.min(20, Number(v.thickness))) };
    }
    if (Object.keys(m).length) out.outlineEdits = m;
  }
  if (e.ringsOwn === true) out.ringsOwn = true; // WD 488: the buyer took the text rings over (see effectiveTextStyle)
  if (e.type === 'weight') {
    out.weightFormat = WEIGHT_FORMATS.includes(e.weightFormat) ? e.weightFormat : 'stacked-dash';
  } else if (e.type === 'arctext') {
    // TEXT_STORE_MAX, not the field cap — a cross-product hop JOINS the design's lines into one
    // string, so the app itself writes text longer than anything a buyer can type. Cutting to the
    // field cap here amputated their words mid-word on every autosave.
    out.text = (e.text || '').slice(0, TEXT_STORE_MAX);
    out.letterSpacing = Number.isFinite(e.letterSpacing) ? e.letterSpacing : 3;
    // The buyer typed THIS line themselves on Customize Design → Text, overriding whatever
    // deriveArcText split out of their org name. Persisted so a later edit to the name (or a
    // resume) can't quietly re-derive over the top of it. Twin of `fillUserSet` above.
    if (e.textUserSet === true) out.textUserSet = true;
  }
  return out;
}

/**
 * The logo-structure record, for the wire. `applyLogoStructure` can write TWO shapes and this has
 * to carry BOTH of them (Ryan 2026-07-28).
 *
 * ⚠ THIS IS WHERE THE BRONCOS BADGE KEPT COMING BACK. The gate used to be
 * `confidence === 'high' && Array.isArray(bands)` — everything else serialized as `null` — which was
 * exactly right while the record only ever described outline BANDS: a mark with no detectable bands
 * had nothing worth a line in localStorage.
 *   `colorBorneDetail` broke that equivalence and broke it in the worst possible direction. It is the
 * one field that lives on the LOW-confidence record BY CONSTRUCTION: a concentric badge has no
 * border-and-fill shape to find, so it is precisely the artwork that returns low confidence AND is
 * ruined by a flatten. Serializing only the high-confidence shape therefore destroyed the flag for
 * every logo it was invented to protect — and `serializeProfile` is not some export corner: `record()`
 * runs it on every commit and `applySnapshot` reads it back on undo/redo, on the autosave resume, and
 * on a share link. So the four suppressions worked on a freshly-uploaded badge and were gone one
 * reload later, which is why the Denver badge kept flattening to a navy disc on an orange plate after
 * being verified as fixed.
 *   The high shape lost it too — `colorBorneDetail` simply was not in the whitelist — so a badge that
 * DOES have detectable bands was equally unprotected after a reload.
 *
 * Rule going forward: the wire shape is derived from what `applyLogoStructure` writes, not from what
 * one consumer happens to read. A field that only exists on the low path still has to survive.
 */
/**
 * One census list for the wire (WD 506): an array of small plain rows - `{hex, share}`,
 * `{hex, touches: [hex]}`, `{share, hex, dom}`, `{hex, survival, share}` - copied field by field so
 * nothing heavy (a raster, a typed array, a function) can ride along, and capped so a pathological
 * mark cannot grow the envelope. Absent or empty reads back as null, which is what an un-measured
 * mark already carries.
 */
function serializeLogoCensus(list) {
  if (!Array.isArray(list) || !list.length) return null;
  const plain = (v, depth) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') return v.slice(0, 64);
    if (typeof v === 'boolean' || v === null) return v;
    if (depth >= 3 || !v || typeof v !== 'object') return undefined;
    if (Array.isArray(v)) return v.slice(0, 32).map((x) => plain(x, depth + 1)).filter((x) => x !== undefined);
    if (ArrayBuffer.isView(v)) return undefined;
    const o = {};
    for (const k of Object.keys(v)) { const x = plain(v[k], depth + 1); if (x !== undefined) o[k] = x; }
    return o;
  };
  const out = list.slice(0, 32).map((row) => plain(row, 0)).filter((row) => row && typeof row === 'object' && !Array.isArray(row));
  return out.length ? out : null;
}

function serializeLogoStructure(ls) {
  if (!ls) return null;
  const colorBorneDetail = ls.colorBorneDetail === true;
  if (ls.confidence === 'high' && Array.isArray(ls.bands)) {
    return {
      confidence: 'high',
      bands: ls.bands.map(normalizeHex).filter(Boolean).slice(0, 4),
      // Serialized alongside `bands` (Ryan 2026-07-25) — without it a RESUMED design loses which
      // bands abut the fill, and the next single-tone logo recolour would swallow a keyline that
      // the same action correctly spared before the reload.
      bandTouchesFill: Array.isArray(ls.bandTouchesFill) ? ls.bandTouchesFill.slice(0, 4).map(Boolean) : [],
      /* Same argument, one field over (Ryan 2026-08-15): which bands the peel can actually reach is
         measured ONCE, from the full-size raster at upload, and a resumed session has no raster to
         re-measure from — `applyLogoStructure` does not run on a reload. Dropping it would bring the
         dead chip back on exactly the designs that have been saved and reopened, which is the harder
         half of the bug to ever see again.
         ⚠ Absent (a save from before this) reads as "peelable", i.e. the old behaviour. */
      bandPeelable: Array.isArray(ls.bandPeelable) ? ls.bandPeelable.slice(0, 4).map(Boolean) : undefined,
      wrapTone: normalizeHex(ls.wrapTone) || undefined,
      fillHex: normalizeHex(ls.fillHex) || null,
      fillUniform: !!ls.fillUniform,
      colorBorneDetail,
    };
  }
  // The low shape carries ONE fact, and only when it is the fact worth carrying. Identical to what
  // `applyLogoStructure` builds on its low path, so a resumed design is indistinguishable from a
  // freshly-uploaded one to every consumer.
  return colorBorneDetail
    ? { confidence: 'low', bands: [], bandTouchesFill: [], fillHex: null, fillUniform: false, colorBorneDetail: true }
    : null;
}

/**
 * `savedArcText` for the wire, in whichever of its two shapes it arrived in.
 *
 * The live shape is `{ [family]: { [slot]: text } }` (2026-07-30); a save written before that is
 * flat, `{ [slot]: text }`. Both are emitted faithfully — see the note at the call site for why this
 * does not migrate. Keys are not whitelisted against a slot list on purpose: a new product's zone
 * may name a slot this file has never heard of (the dumbbell's `center` is the next one), and a
 * whitelist would quietly drop its wording the way the four-slot loop just dropped everything.
 */
const SAVED_ARC_MAX_KEYS = 8; // families, or slots on a legacy flat save — a bound, not a schema
function serializeSavedArcText(saved) {
  if (!saved || typeof saved !== 'object') return null;
  const clean = (v) => (typeof v === 'string' && v.trim() ? v.slice(0, TEXT_STORE_MAX) : null);
  const out = {};
  for (const key of Object.keys(saved).slice(0, SAVED_ARC_MAX_KEYS)) {
    const v = saved[key];
    const flat = clean(v);
    if (flat) { out[key] = flat; continue; } // legacy flat entry — carried through as flat
    if (!v || typeof v !== 'object') continue;
    const inner = {};
    for (const slot of Object.keys(v).slice(0, SAVED_ARC_MAX_KEYS)) {
      const t = clean(v[slot]);
      if (t) inner[slot] = t;
    }
    if (Object.keys(inner).length) out[key] = inner;
  }
  return out;
}

/**
 * `savedSizes` for the wire — `{ [family]: { 'type:slot': sizeScale } }`.
 *
 * ⚠ IT HAS TO BE PERSISTED, and that is the whole point of it (Ryan 2026-07-30: *"the point of this
 * tool is for people to design something they love then buy it — if they can't save what they love,
 * why would they be motivated to buy it?"*). The ACTIVE product's sizes ride on `elements` and were
 * always saved; every OTHER family's lived only in memory, so a design specced across bumpers,
 * dumbbells and barbells — which is the normal shape of a quote — came back from a share link with
 * everything but the last-viewed product reset to defaults.
 *
 * Slot keys are not whitelisted, for the reason `serializeSavedArcText` gives right above: a new
 * product's zone can name a slot this file has never heard of, and a whitelist would silently drop
 * it. Values are clamped to the dial's own range so a hand-edited payload can't paint off-plate.
 */
const SAVED_SIZES_MAX_KEYS = 8;   // families — a bound, not a schema
const SAVED_SIZES_MAX_SLOTS = 12; // pieces within one family
function serializeSavedSizes(saved) {
  if (!saved || typeof saved !== 'object') return null;
  const out = {};
  for (const family of Object.keys(saved).slice(0, SAVED_SIZES_MAX_KEYS)) {
    const v = saved[family];
    if (!v || typeof v !== 'object') continue;
    const inner = {};
    for (const key of Object.keys(v).slice(0, SAVED_SIZES_MAX_SLOTS)) {
      const n = Number(v[key]);
      if (Number.isFinite(n) && n > 0) inner[key] = Math.min(2, Math.max(0.1, n));
    }
    if (Object.keys(inner).length) out[family] = inner;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * `savedInk` for the wire — `{ [contextKey]: { text: {...}, logo: {...} } }`.
 *
 * ⚠ IT HAS TO BE PERSISTED, for the reason `serializeSavedSizes` gives above and then some: the ACTIVE
 * plate colour's ink rides on `elements` and was always saved, so a design whose whole point is a
 * yellow plate with black ink and a black plate with white ink would come back from a share link with
 * one of the two silently reverted — and unlike a size, wrong ink is invisible rather than merely
 * wrong: the mark is still there, in a colour that cannot be seen.
 *
 * ⛔ AND THE FIELD IN `serializeProfile` IS HALF OF THIS. `savedArcText` was re-keyed while its
 * serializer still read the old shape and every save wrote `{}` for a day — it worked all session and
 * vanished on the next resume. `savedTextStyle` was never added at all and is session-only to this day.
 * A store this file does not emit does not exist.
 *
 * ONE RECORD PER ROLE, not per piece — see `captureInk` for why per-piece keys actively broke.
 * Colour refs are kept as refs so a later palette-role edit still moves the remembered ink.
 */
const SAVED_INK_MAX_KEYS = 10;  // plate colours remembered — a bound, not a schema
const SAVED_INK_MAX_RINGS = 4;  // the outline cap the rest of the app enforces
const SAVED_INK_MAX_BANDS = 12;
/** A colour ref is a palette ROLE name or a literal hex — anything else is dropped. */
function cleanRef(v) {
  if (v === 'primary' || v === 'secondary' || v === 'tertiary') return v;
  return normalizeHex(v) || null;
}
/** De-duped, in-range integer indices — the `logoBandUserSet` convention. */
function inkIdxList(v, max) {
  return Array.isArray(v)
    ? [...new Set(v.map((n) => Number(n)).filter((n) => Number.isInteger(n) && n >= 0 && n < max))]
    : [];
}
function serializeSavedInk(saved) {
  if (!saved || typeof saved !== 'object') return null;
  const out = {};
  for (const ctx of Object.keys(saved).slice(0, SAVED_INK_MAX_KEYS)) {
    const v = saved[ctx];
    if (!v || typeof v !== 'object') continue;
    const rec = {};
    const t = v.text;
    if (t && typeof t === 'object') {
      const tr = {};
      const fill = cleanRef(t.fill);
      if (fill) tr.fill = fill;
      if (t.fillUserSet === true) tr.fillUserSet = true;
      if (Array.isArray(t.outlines)) {
        // ⚠ EMITTED EVEN WHEN EMPTY. The LENGTH is the ring cap on restore, so "the buyer made this
        // solid" is a real remembered state and `[]` has to survive the wire. Dropping it as
        // "nothing to save" is how a stripped outline comes back on the next resume.
        tr.outlines = t.outlines.slice(0, SAVED_INK_MAX_RINGS).map(cleanRef);
      }
      // Ring WIDTHS, so a ring `restoreInk` re-creates comes back at its own thickness rather than
      // the design default. Additive: an envelope written before this simply has no `outlinesW`.
      if (Array.isArray(t.outlinesW)) {
        tr.outlinesW = t.outlinesW.slice(0, SAVED_INK_MAX_RINGS)
          .map((n) => (Number.isFinite(n) && n > 0 ? Math.min(12, n) : null));
      }
      // Ring SLOT identity (autoIdx), so a restored ring still knows which autoStyle slot it
      // echoes — the band mirror and `preferredFor` read it. Additive, same as `outlinesW`.
      if (Array.isArray(t.outlinesAuto)) {
        tr.outlinesAuto = t.outlinesAuto.slice(0, SAVED_INK_MAX_RINGS)
          .map((n) => (Number.isInteger(n) && n >= 0 && n < SAVED_INK_MAX_RINGS ? n : null));
      }
      const us = inkIdxList(t.outlinesUserSet, SAVED_INK_MAX_RINGS);
      if (us.length) tr.outlinesUserSet = us;
      // WD 495: the per-plate HAND state (added rings, a taken-over stack, edits to the app's rings). Additive.
      const ad = inkIdxList(t.outlinesAdded, SAVED_INK_MAX_RINGS);
      if (ad.length) tr.outlinesAdded = ad;
      if (t.own === true) tr.own = true;
      if (t.edits && typeof t.edits === 'object') {
        const m = {};
        for (const [k, v] of Object.entries(t.edits).slice(0, SAVED_INK_MAX_RINGS)) {
          if (!/^\d$/.test(k) || !v || typeof v !== 'object') continue;
          if (v.removed === true) m[k] = { removed: true };
          else if (Number.isFinite(v.thickness) && v.thickness > 0) m[k] = { thickness: Math.min(12, v.thickness) };
        }
        if (Object.keys(m).length) tr.edits = m;
      }
      if (Object.keys(tr).length) rec.text = tr;
    }
    const l = v.logo;
    if (l && typeof l === 'object') {
      // `center: null` is "the original artwork" — a choice, kept explicitly.
      const lr = { center: normalizeHex(l.center) || null };
      if (l.bands && typeof l.bands === 'object') {
        const bands = {};
        for (const i of Object.keys(l.bands).slice(0, SAVED_INK_MAX_BANDS)) {
          const hx = normalizeHex(l.bands[i]);
          if (hx && Number.isFinite(Number(i))) bands[Number(i) | 0] = hx;
        }
        if (Object.keys(bands).length) lr.bands = bands;
      }
      const bus = inkIdxList(l.bandsUserSet, SAVED_INK_MAX_BANDS);
      if (bus.length) lr.bandsUserSet = bus;
      if (Array.isArray(l.adds)) {
        const adds = l.adds.slice(0, OUTLINES_MAX).map((c) => normalizeHex(c)).filter(Boolean);
        if (adds.length) lr.adds = adds;
        // WD 495: each added ring's width, so a plate's own ring comes back at its own thickness. Additive.
        if (adds.length && Array.isArray(l.addsW)) {
          lr.addsW = l.addsW.slice(0, adds.length).map((n) => (Number.isFinite(n) && n > 0 ? Math.min(1, n) : null));
        }
      }
      rec.logo = lr;
    }
    if (Object.keys(rec).length) out[ctx] = rec;
  }
  return Object.keys(out).length ? out : null;
}

/** Whitelisted serialization view for quote/localStorage handoff. */
export function serializeProfile(profile) {
  const p = profile || createDefaultProfile();
  const pal = p.palette || {};
  return {
    palette: {
      primary: normalizeHex(pal.primary) || '#1E3A8A',
      // NULLABLE (Ryan 2026-07-25). A one-tone logo genuinely has no second colour, and coercing the
      // app default back in here would resurrect it on the next resume — text that opened solid would
      // come back outlined in a colour the mark never had. `resolvePaletteRef` already handles null.
      secondary: normalizeHex(pal.secondary) || null,
      tertiary: normalizeHex(pal.tertiary) || null,
    },
    // The saved bench (deduped, capped) — persists so a resume keeps your color library.
    swatches: Array.isArray(p.swatches)
      ? [...new Set(p.swatches.map(normalizeHex).filter(Boolean))].slice(0, SWATCH_CAP)
      : [],
    bodyColor: normalizeHex(p.bodyColor) || DEFAULT_BODY_COLOR,
    // What the analysis found in the artwork — the outline bands (outer→in) for the peel, and the
    // colour-borne verdict that governs whether the mark may be flattened at all. Persists so a
    // resume, an undo and a share link all describe the same logo. See serializeLogoStructure.
    logoStructure: serializeLogoStructure(p.logoStructure),
    // Rides along with it, and for the same reason: it governs a colour DECISION, so losing it on a
    // reload would silently change the design (see serializeLogoStructure's note).
    logoToneCount: Number.isFinite(p.logoToneCount) ? p.logoToneCount : 0,
    // Same wire, same reason — it governs a thickness DECISION, so a reload must not silently
    // re-weight the design.
    logoStrokeFrac: Number.isFinite(p.logoStrokeFrac) ? p.logoStrokeFrac : 0,
    // Same wire, same reason — the keyed-out school colour; losing it on reload would demote the
    // field swatch to "buyer-added" (✕ returns, chips drop it, the plate mix forgets it).
    keyedFieldHex: normalizeHex(p.keyedFieldHex) || null,
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 506, Ryan 2026-11-04) - THE FIVE CENSUS FIELDS SURVIVE THE
       WIRE. `applyLogoStructure` seeds them at upload and every recolour / ring rule reads them off
       the profile (`markRecolourAllowed`, `keylineOnlyTones`, `ghostedPieceSwaps`, `syncLogoContrastRing`),
       but this whitelist never carried them - so a share link, a Resume or a My Designs open restored
       a profile with all five null, and the rules' "no evidence takes the DEFAULT" refused the swaps
       the live session had made. Oak Ridge WEIGHT-1030-A: perfect in the designer, half-yellow logo on
       the yellow plate and a ringed navy logo on the navy plate at its own share link. A few hundred
       bytes per design; see `seedLogoCensus` in ui/state.js and `ensureLogoCensus` in ui/logo-step.js
       for records written before this. */
    logoTraceTones: serializeLogoCensus(p.logoTraceTones),
    logoSoloTones: serializeLogoCensus(p.logoSoloTones),
    logoInkPieces: serializeLogoCensus(p.logoInkPieces),
    logoToneNeighbours: serializeLogoCensus(p.logoToneNeighbours),
    logoToneSubstance: serializeLogoCensus(p.logoToneSubstance),
    // The auto-style template (fill + default outline stack derived from the logo). MUST
    // persist: it's re-stamped onto new pieces (applyLayoutPreset) and is what "Reset to
    // original" rebuilds the text outlines from — and every snapshot round-trips through
    // applySnapshot (undo/redo AND resume), which would otherwise null it (Ryan 2026-07-22).
    autoStyle: p.autoStyle && Array.isArray(p.autoStyle.outlines)
      ? {
          fillRef: p.autoStyle.fillRef || 'primary',
          outlines: p.autoStyle.outlines.slice(0, 4).map((o) => ({ colorRef: o.colorRef || 'secondary', thickness: Number(o.thickness) || 0 })),
        }
      : null,
    // The hand-styled LATCH. Persisted for the same reason `autoStyle` is: every snapshot round-trips
    // through `applySnapshot` (undo/redo AND resume), so leaving it off the wire would re-arm the
    // holes flip's restyle over outlines the buyer had already chosen — the exact case it exists to
    // refuse — the first time they reloaded the tab.
    styleUserEdited: p.styleUserEdited === true,
    // The logo-ring suppression latch, on the wire for exactly the reason above: every snapshot
    // round-trips through `applySnapshot` (undo/redo AND resume), so leaving it off would put the
    // deleted ring back the first time the buyer reloaded the tab or hit undo — which is the bug
    // it exists to fix, wearing a different hat.
    logoRingSuppressed: p.logoRingSuppressed === true,
    // Post-upload color baseline for "Reset to original" (small; persists across a resume).
    styleBaseline: p.styleBaseline && p.styleBaseline.palette
      ? {
          palette: {
            primary: normalizeHex(p.styleBaseline.palette.primary) || null,
            secondary: normalizeHex(p.styleBaseline.palette.secondary) || null,
            tertiary: normalizeHex(p.styleBaseline.palette.tertiary) || null,
          },
          swatches: Array.isArray(p.styleBaseline.swatches) ? p.styleBaseline.swatches.map(normalizeHex).filter(Boolean).slice(0, SWATCH_CAP) : [],
        }
      : null,
    font: FONTS[p.font] ? p.font : DEFAULT_FONT_ID,
    allCaps: p.allCaps !== false,
    layoutPreset: p.layoutPreset || DEFAULT_LAYOUT_PRESET,
    elements: Array.isArray(p.elements) ? p.elements.map(serializeElement) : [],
    // Persist the remembered logo so a design saved / quoted while on a text-only
    // layout still knows its logo (and restores it on a later logo layout).
    savedLogo: p.savedLogo && p.savedLogo.dataUrl ? serializeLogo(p.savedLogo) : null,
    /* WHERE THE BUYER'S ORIGINAL FILE LIVES — a signed `/files/…` PATH, never the bytes
       (2026-08-27). `ui/artwork-hold.js` keeps the real file in memory and is deliberately cleared
       the moment a DIFFERENT design is opened, so a rep reopening a saved design has an enhanced
       logo and no original, and the Vendor Design Sheet had nothing to link to — the workflow it
       exists for. A path is a few dozen bytes and survives the wire, so the POINTER can ride the
       design while the rule that the BYTES are never persisted for a browsing session stands
       untouched. Written in rep mode at upload; see `ui/artwork-upload.js`.
         ⚠ ON THE PROFILE, NOT ON THE LOGO OBJECT, and that is the whole reason it is here. The
       logo ELEMENT is torn down and rebuilt by every layout change and by the reveal choreography
       that runs immediately after the upload this is written during — a pin on the element would
       be dropped by the next relayout, silently, and the sheet would go back to "Not on file" for
       reasons nobody could see. The design owns its artwork; a slot on the plate does not.
         ⛔ OUR OWN SIGNED PATH ONLY. This field rides the envelope a SHARE LINK carries, so an
       absolute URL accepted here would put a link to somewhere we did not put a file onto a
       document we hand a supplier. Same guard, same reason, as the worker's `safeRel`. */
    originalArtwork: p.originalArtwork
      && typeof p.originalArtwork.url === 'string'
      && p.originalArtwork.url.startsWith('/files/')
      ? {
        url: p.originalArtwork.url,
        ...(p.originalArtwork.fileName ? { fileName: String(p.originalArtwork.fileName).slice(0, 120) } : {}),
      }
      : null,
    // Its paint, persisted alongside — a resume must bring back the recoloured mark, not the upload.
    savedLogoToneSwaps: Array.isArray(p.savedLogoToneSwaps) && p.savedLogoToneSwaps.length
      ? p.savedLogoToneSwaps
        .map((sw) => ({ from: normalizeHex(sw && sw.from), to: normalizeHex(sw && sw.to) }))
        .filter((sw) => sw.from && sw.to && sw.from !== sw.to)
        .slice(0, 8)
      : null,
    // Persist typed arch wording so a design saved while on a text-less layout still knows it.
    //
    // ⛔ THIS DROPPED THE WHOLE MEMORY FOR A DAY. It read `p.savedArcText[slot]` over the four slot
    // keys — the FLAT shape — but 2026-07-30 re-keyed the store by FAMILY (`{ [family]: { [slot]:
    // text } }`) so the bumper's `top` and the grip's `top` would stop overwriting each other. A
    // nested store has no string at `.top`, so every save wrote `{}` and the per-product wording
    // survived only in memory: it worked all session and was gone on the next resume.
    //
    // Shape-preserving rather than shape-asserting, deliberately. A nested value is serialized as
    // nested; a bare string at the top level is a pre-2026-07-30 flat save and is carried through
    // AS FLAT, so `layouts.normaliseSavedArcText` still migrates it on the read side exactly as it
    // does today. That keeps the migration rule in one place instead of copying it here, where a
    // second copy could disagree with the first.
    savedArcText: serializeSavedArcText(p.savedArcText),
    // Per-family hand-set sizes — see serializeSavedSizes for why this must survive the wire.
    savedSizes: serializeSavedSizes(p.savedSizes),
    // Per-plate-colour ink — see serializeSavedInk for why this must survive the wire.
    savedInk: serializeSavedInk(p.savedInk),
    /* ⚠ ON THE WIRE, and this one is not merely a convenience like its neighbours. The live
       `bodyColor` field carries ONE configuration's colour — the one on the stage — so a resumed or
       shared two-colour design without this comes back with both sets the same colour, silently,
       and the buyer's white/green split is gone with no error anywhere to say so. */
    savedBodyColor: serializeSavedBodyColor(p.savedBodyColor),
    bodyColorUserSet: serializeUserSetMap(p.bodyColorUserSet),
    /* ⚠ ON THE WIRE, or a resumed design forgets every product's position but the one it is showing
       — the live element carries that one, and nothing else. Same argument as `savedSizes` and
       `savedInk` above; the whitelist note on the element serializer names the cost exactly. */
    savedWeightNudge: serializeSavedWeightNudge(p.savedWeightNudge),
    // Persisted so a resumed design re-fills the Add Logo tab's Organization/Mascot
    // fields (Ryan 2026-07-18). Capped to the input maxlength (NAME, not per-arc).
    orgName: String(p.orgName || '').slice(0, NAME_MAX_LENGTH),
    mascot: String(p.mascot || '').slice(0, NAME_MAX_LENGTH),
    weightValue: Number.isFinite(p.weightValue) ? p.weightValue : 45,
    unit: p.unit === 'kg' ? 'kg' : 'lb',
    createdAt: p.createdAt ?? null,
  };
}
